"use client";

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, HTMLAttributes, KeyboardEvent, RefObject } from "react";
import { createPortal } from "react-dom";
import { AutoResizeTextarea } from "./AutoResizeTextarea";
import PromptImageReferenceOverlay from "./PromptImageReferenceOverlay";
import PromptImageMentionMenu from "./PromptImageMentionMenu";
import EcomOnboardingGlowLights from "./EcomOnboardingGlowLights";
import { ReversePromptAiIcon } from "./ReversePromptAiIcon";
import { CropIcon, EcomButton, EcomButton as Button, EcomIconAction, EcomModal, EcomTutorialPopover, EcomTutorialVideoModal } from "../../ui";
import {
  ImageUploadAddTrigger,
  ImageUploadEmptyState,
  ImageUploadField,
  type ImageUploadFieldItem,
} from "../ui/image-upload-field";
import { ReferenceImageUploadDialog } from "../ui/reference-image-upload-dialog";
import {
  ReferenceImageSlotGrid,
  type ReferenceImageSlotGridSlot,
} from "../ui/reference-image-slot-grid";
import { EcomSkeleton as Skeleton } from "../../ui";
import { ArrowUpRight, ChevronRight, CircleHelp, ImagePlus, Loader2, Trash2, X } from "lucide-react";
import { useEcomFeedback } from "../../ui";
import {
  ecomImageApi,
  type CozeTemplateSetDetail,
  type CozeTemplateReversePromptResult,
  type CozeTemplateSetSlotDTO,
  type CozeTemplateSetSummary,
  type CozeTemplateUserModuleDTO,
  type CozeTemplateUserSlotDTO,
  type ComponentConfig,
  type MyCostsResult,
} from "../../lib/ecom-image-api";
import {
  calculateCozeReverseEstimate,
  getCozeReversePublicErrorText,
  normalizeCozeReverseUnitCost,
  resolvePaidCozeReverseBehavior,
  type CozeReverseEstimate,
} from "../../lib/ecom-coze-reverse-billing";
import {
  clearCozeReversePromptPendingAction,
  clearCozeReversePromptHistory,
  loadCozeReversePromptPendingActions,
  loadCozeReversePromptHistory,
  saveCozeReversePromptPendingAction,
  saveCozeReversePromptHistory,
  type CozeReversePromptPendingAction,
} from "../../lib/ecom-coze-reverse-prompt-history";
import { createClientUUID } from "../../lib/client-uuid";
import {
  claimReversePromptOperation,
  isReversePromptOperationCurrent,
  updateReversePromptOperation,
  type ReversePromptOperationClaim,
} from "../../lib/ecom-reverse-prompt-lifecycle";
import { normalizeReversePromptToPlainText } from "../../lib/ecom-reverse-prompt-plain-text";
import { buildDetailReplicaUploadURLCandidates } from "../../lib/ecom-detail-replica-upload-url";
import {
  insertPromptImageReferenceAtCaret,
  rewritePromptImageReferencesForMove,
  rewritePromptImageReferencesForRemoval,
  rewriteReversePromptImageReferences,
} from "../../lib/ecom-prompt-image-reference";
import { IMAGE_ACCEPT, MAX_REVERSE_PROMPT_BYTES, getImageFilesFromDataTransfer, getImageFilesFromFileList, isImageFile } from "../../lib/file-utils";
import {
  COZE_TEMPLATE_APPLY_TEMPLATE_BUTTON_LABEL,
  COZE_TEMPLATE_DEFAULT_PRODUCT_TYPES,
  COZE_TEMPLATE_HISTORY_UPLOAD_SOURCE_KEY,
  COZE_TEMPLATE_REFERENCE_DRAFT_ID,
  COZE_TEMPLATE_REFERENCE_IMAGE_LIMIT,
  COZE_TEMPLATE_FIXED_REFERENCE_SLOT_COUNT,
  COZE_TEMPLATE_UNDEFINED_DEFINITION,
  COZE_TEMPLATE_UPLOAD_REFERENCE_LABEL,
  applyCozeTemplateReverseDraftResults,
  canCreateCozeTemplateUserReference,
  completeCozeTemplateModuleSlots,
  filterCozeTemplateSetSummaries,
  getCozeTemplateProductTypeOptions,
  getCozeTemplateProductImageDefinitionOptions,
  getCozeTemplateReferenceUploadSlotKey,
  getCozeTemplateReversePromptBatchCounts,
  getCozeTemplateApplicableReferenceEntries,
  getCozeTemplateDetailPreviewSlots,
  getCozeTemplateSequentialSlotName,
  getCozeTemplatePromptKey,
  getCozeTemplateReferencePickerSlots,
  getCozeTemplateSetValidationMessage,
  getCozeTemplateSummaryPreviewImages,
  buildCozeTemplateReversePromptSlots,
  getCozeTemplateSetGeneratableSlots,
  getCozeTemplateSlotUploadedImages,
  getCozeTemplateSlotCardClassName,
  getCozeTemplateSlotImage,
  getCozeTemplateSlotPrompt,
  invalidateCozeTemplateSlotPrompt,
  isDefinitiveCozeReversePromptPostError,
  mergeCozeTemplateReferenceSlots,
  normalizeCozeTemplateSetValue,
  normalizeCozeTemplateSequentialSlotNames,
  normalizeCozeTemplateSequentialSlotValue,
  prepareCozeTemplateReferencePickerContext,
  planCozeTemplatePreviewFileDrop,
  projectCozeTemplateUserModuleSlots,
  projectCozeTemplateReferencePickerDisplaySlots,
  projectCozeTemplateWorkspaceSlots,
  reconcileCozeTemplateReversePromptDrafts,
  reorderCozeTemplateReferenceSlotImages,
  resolveCozeTemplateActiveSlot,
  resolveCozeTemplateLibraryView,
  runCozeTemplateReversePromptAction,
  runCozeTemplateReverseRequestOnce,
  selectSingleCozeTemplateSlot,
  shouldShowCozeTemplateOnboarding,
  sortCozeTemplateSlots,
  shouldProjectCozeTemplateUserModules,
  validateCozeTemplateSetValue,
  type CozeTemplatePromptVariant,
  type CozeTemplateReferencePickerSlot,
  type CozeTemplateReversePromptDraftItem,
  type CozeTemplateSetSlot,
  type CozeTemplateSetValue,
  type CozeTemplateUploadedImage,
} from "../../lib/ecom-coze-template-set";
import { mapSettledWithConcurrency } from "../../lib/ecom-upload-batch";
import { cn } from "../../lib/utils";
import type { DetailReplicaTutorialConfig } from "../../lib/ecom-detail-replica-tutorial";
import { getHelpTipHoverPopoverPosition, type HelpTipPopoverPosition } from "../../lib/ecom-help-tip-popover-position";
import ImageCropDialog from "./ImageCropDialog";
import { TemplateImage, TemplateImageSourceProvider } from "./SafeImage";

type SourceImage = {
  id: string;
  url: string;
  preview_url?: string;
  source_param_key?: string;
  source_label?: string;
};

type CozeProductUploadFieldItem = ImageUploadFieldItem & {
  image: CozeTemplateUploadedImage;
};

interface CozeTemplateSetFieldProps {
  comp: ComponentConfig;
  workflowKey: string;
  value: CozeTemplateSetValue | null;
  sourceImages: SourceImage[];
  onChange: (value: CozeTemplateSetValue | null) => void;
  myCosts?: MyCostsResult | null;
  notifyUploading?: (delta: number) => void;
  onPromptGeneratingChange?: (componentKey: string, generating: boolean) => void;
  onHoverPreviewChange?: (preview: { url: string; alt?: string } | null) => void;
  onOnboardingChange?: (visible: boolean) => void;
  leftNavCollapsed?: boolean;
  reversePromptHistoryScope?: string;
  mainReplicaTutorialConfig?: DetailReplicaTutorialConfig | null;
  imageReferenceBindingEnabled?: boolean;
}

type PromptStatus = "idle" | "generating" | "success" | "failed";
type TemplatePreviewSlot = CozeTemplateSetSlot & { template_set_id: number };
type ReferencePickerDraftImage = {
  imageUrl: string;
  previewUrl: string;
};
type ReferencePickerSessionSlot = CozeTemplateReferencePickerSlot & {
  is_reference_picker_session_slot?: boolean;
};
type ReverseModalStage = "closed" | "preview" | "generating" | "result";
const PROMPT_VARIANTS: Array<{ value: CozeTemplatePromptVariant; label: string }> = [
  { value: "standard", label: "标准版" },
  { value: "remove_text", label: "去文字版" },
];

const LOCAL_PRODUCT_IMAGE_SOURCE_KEY = "__coze_template_product_upload";
const COZE_PROMPT_MIN_HEIGHT = 210;
const COZE_PROMPT_MAX_HEIGHT = 450;
const COZE_REVERSE_PROGRESS_MAX = 94;
const COZE_REVERSE_PENDING_ACTION_MAX_AGE = 10 * 60 * 1000;
const COZE_IMAGE_REFERENCE_PROMPT_PLACEHOLDER = `输入提示词，点击上方图片插入@图片引用\n例：让@图片1中的人物@图片2中的衣服`;

function MainReplicaTutorialButton({ config }: { config: DetailReplicaTutorialConfig }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPinned, setPreviewPinned] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [position, setPosition] = useState<HelpTipPopoverPosition | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const detailOpenRef = useRef(detailOpen);
  detailOpenRef.current = detailOpen;

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);
  const updatePosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;
    setPosition(getHelpTipHoverPopoverPosition({
      anchor: button.getBoundingClientRect(),
      viewport: { width: window.innerWidth, height: window.innerHeight },
    }));
  }, []);
  const openTutorialPreview = useCallback((pin = false) => {
    clearCloseTimer();
    updatePosition();
    setPreviewOpen(true);
    setPreviewPinned((current) => current || pin);
    requestAnimationFrame(updatePosition);
  }, [clearCloseTimer, updatePosition]);
  const closeTutorialPreview = useCallback(() => {
    clearCloseTimer();
    setPreviewOpen(false);
    setPreviewPinned(false);
  }, [clearCloseTimer]);
  const schedulePreviewClose = useCallback(() => {
    if (previewPinned) return;
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setPreviewOpen(false), 140);
  }, [clearCloseTimer, previewPinned]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);
  useEffect(() => {
    if (!previewOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || previewRef.current?.contains(target)) return;
      closeTutorialPreview();
    };
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      const shouldRestoreFocus = previewRef.current?.contains(document.activeElement);
      closeTutorialPreview();
      if (shouldRestoreFocus) buttonRef.current?.focus();
    };
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [closeTutorialPreview, previewOpen, updatePosition]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        data-main-replica-tutorial-button
        aria-haspopup="dialog"
        aria-expanded={previewOpen}
        aria-controls="main-replica-tutorial-preview"
        onMouseEnter={() => openTutorialPreview(false)}
        onMouseLeave={schedulePreviewClose}
        onFocus={() => openTutorialPreview(false)}
        onClick={() => {
          if (previewOpen && previewPinned) closeTutorialPreview();
          else openTutorialPreview(true);
        }}
        onBlur={(event) => {
          const nextTarget = event.relatedTarget as Node | null;
          if (!nextTarget || !previewRef.current?.contains(nextTarget)) schedulePreviewClose();
        }}
        className="absolute right-14 top-14 z-20 mt-5 inline-flex h-9 items-center gap-1.5 rounded-[8px] border border-[#272727] bg-[#272727] px-4 text-[13px] text-[#8C8C8C] transition-colors hover:border-[#272727] hover:bg-[#272727] hover:text-[#F6F6F6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current max-md:right-4 max-md:top-16"
      >
        <CircleHelp aria-hidden="true" className="size-3.5" />
        {config.button_label}
      </button>
      {position && (
        <EcomTutorialPopover
          open={previewOpen}
          id="main-replica-tutorial-preview"
          ariaLabel={config.title || config.button_label}
          position={position}
          popoverRef={previewRef}
          onMouseEnter={clearCloseTimer}
          onMouseLeave={schedulePreviewClose}
          onBlur={(event) => {
            const nextTarget = event.relatedTarget as Node | null;
            if (!nextTarget || (!previewRef.current?.contains(nextTarget) && !buttonRef.current?.contains(nextTarget))) {
              schedulePreviewClose();
            }
          }}
        >
          <div data-main-replica-tutorial-preview className="max-h-[min(70vh,560px)] overflow-y-auto p-4 text-left">
            {(config.image_url || config.media_url) && (
              <div className="mb-4 overflow-hidden rounded-[12px] bg-black/35">
                {config.image_url || config.media_type === "image" ? (
                  <img src={config.image_url || config.media_url} alt={config.title} className="block h-[174px] w-full object-cover" />
                ) : (
                  <video src={config.media_url} muted playsInline autoPlay loop className="block h-[174px] w-full object-cover" />
                )}
              </div>
            )}
            {config.title && <div className="mb-2 text-[18px] font-bold leading-6 text-white">{config.title}</div>}
            {config.content && <div className="whitespace-pre-wrap text-[14px] leading-6 text-white/80">{config.content}</div>}
            {config.detail_url && (
              <div className="mt-4 flex justify-end">
                <EcomButton
                  type="button"
                  variant="default"
                  size="medium"
                  data-main-replica-tutorial-detail
                  onClick={() => {
                    clearCloseTimer();
                    setPreviewOpen(false);
                    setPreviewPinned(false);
                    setDetailOpen(true);
                  }}
                >
                  查看详情
                </EcomButton>
              </div>
            )}
          </div>
        </EcomTutorialPopover>
      )}
      {config.detail_media_type === "video" && config.detail_url ? (
        <EcomTutorialVideoModal
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          title={config.title}
          videoUrl={config.detail_url}
          returnFocusRef={buttonRef}
        />
      ) : (
        <EcomModal
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          title={config.title}
          layer="tutorial"
          closeLabel="关闭教程详情图片"
          width="min(1000px, calc(100vw - 40px))"
          afterClose={() => {
            if (!detailOpenRef.current) buttonRef.current?.focus();
          }}
          classNames={{
            mask: "bg-black/20 backdrop-blur-[7.5px]",
            panel: "max-h-[calc(100vh-64px)] overflow-hidden rounded-[16px] bg-black shadow-[0_24px_80px_rgba(0,0,0,0.7)]",
            header: "border-b border-white/10 bg-black px-[15px]",
            body: "p-0",
          }}
        >
          <div data-main-replica-tutorial-modal data-main-replica-tutorial-panel className="flex aspect-video max-h-[calc(100vh-150px)] w-full min-h-0 items-center justify-center bg-black">
            {config.detail_url || config.image_url ? (
              <img
                data-main-replica-tutorial-image
                src={config.detail_url || config.image_url}
                alt={config.title}
                className="h-full w-full object-contain"
                decoding="async"
              />
            ) : (
              <p className="py-8 text-center text-sm text-white/45">暂无教程媒体</p>
            )}
          </div>
        </EcomModal>
      )}
    </>
  );
}

function CozeTemplateSlotPlaceholder() {
  return (
    <svg viewBox="0 0 40 40" aria-hidden="true" className="block size-6 text-white/22" fill="none">
      <rect x="4" y="5" width="32" height="30" rx="4" stroke="currentColor" strokeWidth="2" />
      <path d="m9 29 7-7 5 5 4-4 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="15" cy="14" r="2.5" fill="currentColor" />
    </svg>
  );
}

function createReverseProgress(index: number): number {
  return Math.min(COZE_REVERSE_PROGRESS_MAX, 8 + index * 5 + Math.floor(Math.random() * 5));
}

function advanceReverseProgress(progress: number, forceAdvance = false): number {
  if (progress >= COZE_REVERSE_PROGRESS_MAX) return progress;
  const pauseChance = progress < 45 ? 0.18 : progress < 75 ? 0.42 : 0.68;
  if (!forceAdvance && Math.random() < pauseChance) return progress;
  const step = progress < 45
    ? 1 + Math.floor(Math.random() * 4)
    : progress < 75
      ? 1 + Math.floor(Math.random() * 3)
      : 1;
  return Math.min(COZE_REVERSE_PROGRESS_MAX, progress + step);
}

function isSameReversePromptDraftInput(
  current: CozeTemplateReversePromptDraftItem,
  pending: CozeTemplateReversePromptDraftItem,
): boolean {
  return current.template_set_id === pending.template_set_id &&
    current.slot_key === pending.slot_key &&
    current.slot_name === pending.slot_name &&
    current.template_image === pending.template_image &&
    current.variant === pending.variant &&
    current.upload_images.length === pending.upload_images.length &&
    current.upload_images.every((image, index) => (
      image.url === pending.upload_images[index]?.url &&
      image.definition === pending.upload_images[index]?.definition
    ));
}

function restorePendingReversePromptDrafts(
  value: CozeTemplateSetValue,
  pending: CozeReversePromptPendingAction,
): CozeTemplateReversePromptDraftItem[] | null {
  if (
    value.template_set_id !== pending.template_set_id ||
    value.variant !== pending.variant ||
    Date.now() - pending.started_at > COZE_REVERSE_PENDING_ACTION_MAX_AGE
  ) {
    return null;
  }
  const reconciled = reconcileCozeTemplateReversePromptDrafts(value, pending.drafts);
  const pendingByKey = new Map(pending.drafts.map((draft) => [draft.slot_key, draft]));
  const requestedKeys = new Set(pending.requested_slot_keys);
  if (pending.requested_slot_keys.some((slotKey) => {
    const current = reconciled.find((draft) => draft.slot_key === slotKey);
    const stored = pendingByKey.get(slotKey);
    return !current || !stored || !isSameReversePromptDraftInput(current, stored);
  })) {
    return null;
  }
  return reconciled.map((draft) => requestedKeys.has(draft.slot_key)
    ? { ...draft, prompt: "", status: "generating", errorText: undefined }
    : draft);
}

function mergeReversePromptDraftResults(
  drafts: CozeTemplateReversePromptDraftItem[],
  requestedSlotKeys: readonly string[],
  results: CozeTemplateReversePromptResult[],
  editedSlotKeys: ReadonlySet<string>,
): CozeTemplateReversePromptDraftItem[] {
  const requestedKeys = new Set(requestedSlotKeys);
  const promptResults = new Map(results.map((item) => [item.slot_key, item]));
  return drafts.map((item) => {
    if (!requestedKeys.has(item.slot_key)) return item;
    if (editedSlotKeys.has(item.slot_key)) return item;
    const result = promptResults.get(item.slot_key);
    if (!result || result.status === "generating") return item;
    const prompt = result.status === "success"
      ? rewriteReversePromptImageReferences(
          // Coze 本地编号把模板图固定为图片1，上传素材从图片2开始。
          normalizeReversePromptToPlainText(cleanText(result.prompt)),
          [{ url: item.template_image }, ...item.upload_images],
          ["", ...item.upload_images.map((image) => image.url)],
          1,
        )
      : "";
    return prompt
      ? { ...item, prompt, status: "success", errorText: undefined }
      : {
          ...item,
          prompt: "",
          status: "failed",
          errorText: getCozeReversePublicErrorText(result.error_code),
        };
  });
}

function CozePromptTextarea({
  value,
  onChange,
  disabled,
  textareaRef: forwardedTextareaRef,
  imageUrls,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  textareaRef?: RefObject<HTMLTextAreaElement | null>;
  imageUrls: string[];
}) {
  const fallbackTextareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = forwardedTextareaRef || fallbackTextareaRef;
  const [showBottomFade, setShowBottomFade] = useState(false);

  const syncBottomFade = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const hasOverflow = textarea.scrollHeight > textarea.clientHeight + 1;
    const isAtBottom = textarea.scrollTop + textarea.clientHeight >= textarea.scrollHeight - 2;
    setShowBottomFade(hasOverflow && !isAtBottom);
  }, [textareaRef]);

  useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(syncBottomFade);
    return () => window.cancelAnimationFrame(frame);
  }, [syncBottomFade, value]);

  return (
    <div className="relative">
      <AutoResizeTextarea
        ref={textareaRef}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onScroll={syncBottomFade}
        placeholder={COZE_IMAGE_REFERENCE_PROMPT_PLACEHOLDER}
        minHeight={COZE_PROMPT_MIN_HEIGHT}
        maxHeight={COZE_PROMPT_MAX_HEIGHT}
        disabled={disabled}
        aria-busy={disabled}
        data-ecom-prompt-input
        data-coze-prompt-textarea="true"
        className="block w-full bg-transparent px-3 py-2.5 text-[12px] leading-5 text-white outline-none placeholder:text-white/32 disabled:cursor-not-allowed disabled:text-white/25 disabled:placeholder:text-white/18 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-track]:bg-transparent"
      />
      {imageUrls.length > 0 && (
        <PromptImageReferenceOverlay
          value={value}
          imageUrls={imageUrls}
          textareaRef={textareaRef}
          onChange={onChange}
          mirrorClassName="px-3 py-2.5 text-[12px] leading-5"
          disabled={disabled}
        />
      )}
      <PromptImageMentionMenu
        value={value}
        imageUrls={imageUrls}
        textareaRef={textareaRef}
        mirrorClassName="px-3 py-2.5 text-[12px] leading-5"
        onChange={onChange}
        disabled={disabled}
      />
      <div
        aria-hidden="true"
        data-coze-prompt-bottom-fade="true"
        className={cn(
          "pointer-events-none absolute right-1 bottom-0 left-0 h-12 bg-[linear-gradient(180deg,rgba(25,25,25,0)_0%,rgba(25,25,25,.72)_58%,#191919_100%)] transition-opacity duration-200",
          showBottomFade ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  );
}

function CozeReverseCostLine({
  estimate,
  label = "预估消耗",
  className = "",
}: {
  estimate: CozeReverseEstimate;
  label?: string;
  className?: string;
}) {
  return (
    <div
      className={`flex items-center justify-between text-xs text-[#A0A0A0] ${className}`}
      data-coze-reverse-cost-line
    >
      <span>{label}</span>
      <span className="flex items-baseline">
        {estimate.hasDiscount && (
          <span className="text-xs text-[#A0A0A0] line-through" data-coze-reverse-original-cost>
            {estimate.originalCost}
          </span>
        )}
        <span
          className={`${estimate.hasDiscount ? "ml-2" : ""} text-lg font-medium text-[#FCB453]`}
          data-coze-reverse-current-cost
        >
          {estimate.cost}
        </span>
        <span className="ml-1 text-xs text-[#A0A0A0]">豌豆</span>
      </span>
    </div>
  );
}

function getCozeTemplateReverseRequests(
  currentValue: CozeTemplateSetValue,
  targetSlots = currentValue.slots,
  force = false,
) {
  const variant = currentValue.variant;
  const slotsToReverse = (currentValue.template_set_id === COZE_TEMPLATE_REFERENCE_DRAFT_ID
    ? targetSlots.slice(0, COZE_TEMPLATE_REFERENCE_IMAGE_LIMIT)
    : targetSlots)
    .filter((slot) => {
      const imageUrl = getCozeTemplateSlotImage(slot);
      return Boolean(imageUrl) && (force || !getCozeTemplateSlotPrompt(slot, variant));
    });

  return buildCozeTemplateReversePromptSlots({
    slots: slotsToReverse,
    uploadedImages: currentValue.uploaded_images,
    variant,
  });
}

function cleanText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function getUploadedUrl(data: unknown): string {
  if (!data || typeof data !== "object") return "";
  const record = data as Record<string, unknown>;
  const directUrl = (
    cleanText(record.file_url) ||
    cleanText(record.public_url) ||
    cleanText(record.url) ||
    cleanText(record.image_url) ||
    cleanText(record.thumbnail_url)
  );
  const candidates = buildDetailReplicaUploadURLCandidates(data);
  if (cleanText(record.s3_key)) {
    return candidates.primary || directUrl || candidates.fallback;
  }
  return directUrl || candidates.primary;
}

function getUploadedPreviewUrl(data: unknown): string {
  if (!data || typeof data !== "object") return "";
  const record = data as Record<string, unknown>;
  return (
    cleanText(record.thumbnail_url) ||
    cleanText(record.public_url) ||
    cleanText(record.file_url) ||
    cleanText(record.url) ||
    cleanText(record.image_url)
  );
}

function isBlobUrl(value?: string): boolean {
  return typeof value === "string" && value.startsWith("blob:");
}

function revokeBlobUrl(value?: string) {
  if (isBlobUrl(value)) {
    URL.revokeObjectURL(value as string);
  }
}

function revokeReferencePickerDraftImage(draft?: ReferencePickerDraftImage) {
  if (!draft) return;
  revokeBlobUrl(draft.imageUrl);
  if (draft.previewUrl !== draft.imageUrl) {
    revokeBlobUrl(draft.previewUrl);
  }
}

function getTemplateImageFromDTO(slot: CozeTemplateSetSlotDTO | CozeTemplateUserSlotDTO | CozeTemplateSetSlot): string {
  return cleanText(slot.template_image) || cleanText(slot.image_url);
}

function resolveTemplateCoverImage(slots: Array<CozeTemplateSetSlotDTO | CozeTemplateSetSlot>): string {
  return slots.map(getTemplateImageFromDTO).find(Boolean) || "";
}

function toSystemSlot(slot: CozeTemplateSetSlotDTO): CozeTemplateSetSlot {
  return {
    id: slot.id,
    slot_id: slot.id,
    slot_key: slot.slot_key,
    slot_name: slot.slot_name,
    param_key: slot.param_key,
    template_image: getTemplateImageFromDTO(slot),
    image_url: getTemplateImageFromDTO(slot),
    is_user_slot: false,
    selected: slot.is_default_selected !== false,
    prompt_by_variant: {},
    prompt_status_by_variant: {},
    sort_order: slot.sort_order,
  };
}

function toUserSlot(slot: CozeTemplateUserSlotDTO): CozeTemplateSetSlot {
  return {
    id: slot.user_module_id || slot.id,
    user_slot_id: slot.template_user_slot_id || undefined,
    user_module_id: slot.user_module_id || slot.id,
    template_user_slot_id: slot.template_user_slot_id,
    slot_key: cleanText(slot.slot_key) || `user_slot_${slot.user_module_id || slot.id}`,
    slot_name: cleanText(slot.slot_name) || "个人模块",
    param_key: slot.param_key,
    template_image: getTemplateImageFromDTO(slot),
    image_url: getTemplateImageFromDTO(slot),
    is_user_slot: true,
    selected: slot.is_default_selected !== false,
    prompt_by_variant: {},
    prompt_status_by_variant: {},
    sort_order: slot.sort_order,
  };
}

function resolveUserModuleSlots(
  slots: CozeTemplateSetSlot[],
  userModules: CozeTemplateUserModuleDTO[],
  userModulesLoaded: boolean,
  includeUserModules = true,
  includeTemplateUserSlots = true,
): CozeTemplateSetSlot[] {
  const projectedSlots = projectCozeTemplateUserModuleSlots(
    slots,
    userModules,
    includeUserModules,
    includeTemplateUserSlots,
  );
  if (userModulesLoaded || !includeUserModules) {
    return normalizeCozeTemplateSequentialSlotNames(projectedSlots);
  }

  const preservedUserSlots = slots.filter((slot) => slot.is_user_slot && !userModules.some((userModule) => (
    slot.user_module_id === userModule.id || slot.slot_key === userModule.slot_key
  )));
  return normalizeCozeTemplateSequentialSlotNames(
    sortCozeTemplateSlots([...projectedSlots, ...preservedUserSlots]),
  );
}

function mergeTemplateDetailToValue(
  detail: CozeTemplateSetDetail,
  existing?: CozeTemplateSetValue | null,
  userModules: CozeTemplateUserModuleDTO[] = [],
  userModulesLoaded = true,
  includeUserModules = true,
): CozeTemplateSetValue {
  const canReuseSlotState = existing?.template_set_id === detail.id;
  const oldSlots = new Map((canReuseSlotState ? existing?.slots || [] : []).map((slot) => [slot.slot_key, slot]));
  const slots = completeCozeTemplateModuleSlots(getTemplatePreviewSlots(
    detail,
    userModules,
    userModulesLoaded,
    includeUserModules,
  ).map((slot) => {
    const old = oldSlots.get(slot.slot_key);
    return old ? {
      ...slot,
      selected: old.selected ?? slot.selected,
      prompt_by_variant: old.prompt_by_variant || {},
      prompt_status_by_variant: old.prompt_status_by_variant || {},
    } : slot;
  }));

  return {
    template_set_id: detail.id,
    template_set_name: detail.name,
    template_set_cover_url: cleanText(detail.cover_image_url),
    category: cleanText(detail.product_type) || cleanText(detail.category),
    size_label: cleanText(detail.size_label),
    variant: existing?.variant || "standard",
    slots: selectSingleCozeTemplateSlot(sortCozeTemplateSlots(slots)),
    uploaded_images: existing?.uploaded_images || [],
  };
}

function syncUploadedImagesWithSources(
  current: CozeTemplateUploadedImage[],
  sources: SourceImage[],
  validDefinitions: string[],
): CozeTemplateUploadedImage[] {
  const sourceByUrl = new Map(sources.map((source) => [source.url, source]));
  const validSet = new Set(validDefinitions);
  const resolveDefinition = (existing?: CozeTemplateUploadedImage) => (
    existing?.definition && validSet.has(existing.definition)
      ? existing.definition
      : COZE_TEMPLATE_UNDEFINED_DEFINITION
  );
  const synced: CozeTemplateUploadedImage[] = [];

  current.forEach((image) => {
    const source = sourceByUrl.get(image.url);
    if (source) {
      synced.push({
        ...image,
        id: image.id || source.id,
        url: source.url,
        preview_url: source.preview_url,
        source_param_key: source.source_param_key,
        source_label: source.source_label,
        definition: resolveDefinition(image),
      });
      return;
    }
    if (
      (image.source_param_key === LOCAL_PRODUCT_IMAGE_SOURCE_KEY ||
        image.source_param_key === COZE_TEMPLATE_HISTORY_UPLOAD_SOURCE_KEY) &&
      cleanText(image.url)
    ) {
      synced.push({
        ...image,
        definition: resolveDefinition(image),
      });
    }
  });

  return synced;
}

function getPromptStatus(slot: CozeTemplateSetSlot, variant: CozeTemplatePromptVariant): PromptStatus {
  return slot.prompt_status_by_variant?.[variant] || "idle";
}

function slotWithPrompt(
  slot: CozeTemplateSetSlot,
  variant: CozeTemplatePromptVariant,
  prompt: string,
  status: PromptStatus,
): CozeTemplateSetSlot {
  return {
    ...slot,
    prompt_by_variant: {
      ...(slot.prompt_by_variant || {}),
      [variant]: prompt,
    },
    prompt_status_by_variant: {
      ...(slot.prompt_status_by_variant || {}),
      [variant]: status,
    },
  };
}

function rewriteCozeSlotPromptImageReferences(
  slot: CozeTemplateSetSlot,
  rewrite: (prompt: string) => string,
): CozeTemplateSetSlot {
  return (["standard", "remove_text"] as const).reduce((currentSlot, variant) => {
    const currentPrompt = getCozeTemplateSlotPrompt(currentSlot, variant);
    if (!currentPrompt) return currentSlot;
    const nextPrompt = rewrite(currentPrompt);
    if (nextPrompt === currentPrompt) return currentSlot;
    return slotWithPrompt(
      currentSlot,
      variant,
      nextPrompt,
      nextPrompt.trim() ? getPromptStatus(currentSlot, variant) : "idle",
    );
  }, slot);
}

function normalizeSingleSelectedSlot(
  value: CozeTemplateSetValue,
  preferredSlotKey?: string,
): CozeTemplateSetValue {
  const normalizedValue = normalizeCozeTemplateSequentialSlotValue(value);
  const selectableSlotKeys = new Set(normalizedValue.slots
    .filter((slot) => getCozeTemplateSlotImage(slot))
    .map((slot) => slot.slot_key));
  const enabledPreferredSlotKey = preferredSlotKey && normalizedValue.slots.some((slot) => (
    slot.slot_key === preferredSlotKey &&
    selectableSlotKeys.has(slot.slot_key)
  ))
    ? preferredSlotKey
    : "";
  const enabledSelectedSlotKey = normalizedValue.slots.find((slot) => (
    slot.selected &&
    selectableSlotKeys.has(slot.slot_key)
  ))?.slot_key;
  const firstEnabledSlotKey = normalizedValue.slots.find((slot) => selectableSlotKeys.has(slot.slot_key))?.slot_key;
  return {
    ...normalizedValue,
    slots: selectSingleCozeTemplateSlot(
      normalizedValue.slots,
      enabledPreferredSlotKey || enabledSelectedSlotKey || firstEnabledSlotKey,
    ),
  };
}

function selectReferenceDraftSlot(
  value: CozeTemplateSetValue,
  preferredSlotKey?: string,
): CozeTemplateSetValue {
  const slots = sortCozeTemplateSlots(value.slots);
  const selectableSlotKeys = new Set(slots
    .filter((slot) => getCozeTemplateSlotImage(slot))
    .map((slot) => slot.slot_key));
  const selectedSlotKey = preferredSlotKey && selectableSlotKeys.has(preferredSlotKey)
    ? preferredSlotKey
    : slots.find((slot) => slot.selected && selectableSlotKeys.has(slot.slot_key))?.slot_key ||
      slots.find((slot) => selectableSlotKeys.has(slot.slot_key))?.slot_key ||
      "";

  return {
    ...value,
    slots: selectSingleCozeTemplateSlot(slots, selectedSlotKey),
  };
}

function getTemplatePreviewSlots(
  detail: CozeTemplateSetDetail | null,
  userModules: CozeTemplateUserModuleDTO[] = [],
  userModulesLoaded = true,
  includeUserModules = true,
  includeTemplateUserSlots = includeUserModules,
): TemplatePreviewSlot[] {
  if (!detail) return [];
  const slots = [
    ...(detail.slots || []).map(toSystemSlot),
    ...(detail.user_slots || []).map(toUserSlot),
  ];
  return resolveUserModuleSlots(
    slots,
    userModules,
    userModulesLoaded,
    includeUserModules,
    includeTemplateUserSlots,
  ).map((slot) => ({
    ...slot,
    template_set_id: detail.id,
  }));
}

function getFirstPreviewSlotKey(
  detail: CozeTemplateSetDetail,
  userModules: CozeTemplateUserModuleDTO[] = [],
  userModulesLoaded = true,
  includeUserModules = true,
  preferredSlotKey = "",
): string {
  const slots = getTemplatePreviewSlots(
    detail,
    userModules,
    userModulesLoaded,
    includeUserModules,
    includeUserModules,
  );
  const preferredSlot = slots.find((slot) => (
    slot.slot_key === preferredSlotKey && Boolean(getTemplateImageFromDTO(slot))
  ));
  if (preferredSlot) return preferredSlot.slot_key;
  const firstSlotWithImage = slots.find((slot) => getTemplateImageFromDTO(slot));
  return firstSlotWithImage?.slot_key || slots[0]?.slot_key || "";
}

function createPreviewTemplateDraft(detail: CozeTemplateSetDetail): TemplatePreviewSlot[] {
  const detailSlots = getTemplatePreviewSlots(detail, [], true, false, true);
  return normalizeCozeTemplateSequentialSlotNames(
    getCozeTemplateDetailPreviewSlots(detailSlots, {
      ownedTemplate: true,
      includeUploadSlot: false,
    }),
  ).map((slot, index) => ({
    ...slot,
    template_set_id: detail.id,
    slot_name: getCozeTemplateSequentialSlotName(index),
    sort_order: index + 1,
    is_upload_placeholder: false,
  }));
}

function getPreviewTemplateDraftSignature(slots: readonly TemplatePreviewSlot[]): string {
  return JSON.stringify(slots.map((slot, index) => ({
    slot_key: cleanText(slot.slot_key),
    slot_name: getCozeTemplateSequentialSlotName(index),
    image_url: getTemplateImageFromDTO(slot),
    sort_order: index + 1,
    is_user_slot: Boolean(slot.is_user_slot),
    user_module_id: slot.user_module_id || 0,
  })));
}

function upsertUserSlotInTemplateDetail(
  detail: CozeTemplateSetDetail | null,
  slot: CozeTemplateUserSlotDTO,
  templateSetId: number,
): CozeTemplateSetDetail | null {
  if (!detail || detail.id !== templateSetId) return detail;
  const existingUserSlots = detail.user_slots || [];
  const userModuleId = slot.user_module_id;
  // 去重按 user_module_id 与 slot_key 双键：后端复用/改名模块时，返回的绑定可能与
  // detail.user_slots 里既有项的 slot_key 不一致（模块被改名），但 user_module_id
  // 仍指向同一模块；反之亦然。任一键命中都视作同一槽位的更新，移除所有命中项后再
  // 追加，避免出现「点一次新增多出两个槽位」的重复。
  const matchesExisting = (item: CozeTemplateUserSlotDTO) => (
    (Boolean(userModuleId) && item.user_module_id === userModuleId) ||
    item.slot_key === slot.slot_key
  );
  const filtered = existingUserSlots.filter((item) => !matchesExisting(item));
  const nextSlot = { ...slot, template_set_id: templateSetId };

  return {
    ...detail,
    user_slots: [...filtered, nextSlot],
  };
}

function upsertUserSlotInTemplateSummary(
  summary: CozeTemplateSetSummary,
  slot: CozeTemplateUserSlotDTO,
  templateSetId: number,
): CozeTemplateSetSummary {
  if (summary.id !== templateSetId) return summary;
  const existingUserSlots = summary.user_slots || [];
  const userModuleId = slot.user_module_id;
  // 与 upsertUserSlotInTemplateDetail 一致：user_module_id 或 slot_key 任一命中即视为
  // 同一槽位更新，移除所有命中项后追加，避免重复槽位。
  const matchesExisting = (item: CozeTemplateUserSlotDTO) => (
    (Boolean(userModuleId) && item.user_module_id === userModuleId) ||
    item.slot_key === slot.slot_key
  );
  const filtered = existingUserSlots.filter((item) => !matchesExisting(item));
  const nextSlot = { ...slot, template_set_id: templateSetId };

  return {
    ...summary,
    user_slots: [...filtered, nextSlot],
  };
}

function buildReferenceDraftTemplateValue(
  entries: Array<{ slot: CozeTemplateReferencePickerSlot; imageUrl: string }>,
  referenceSlots: CozeTemplateReferencePickerSlot[],
  productType: string,
  existing: CozeTemplateSetValue,
): CozeTemplateSetValue {
  const cleanProductType = cleanText(productType);
  const referenceSlotsByKey = new Map(referenceSlots.map((slot) => [slot.slot_key, slot]));
  const entryByKey = new Map(entries.map((entry) => [entry.slot.slot_key, entry]));
  const existingSlotKeyByName = new Map(existing.slots.map((slot) => [cleanText(slot.slot_name), slot.slot_key]));
  const slots: CozeTemplateSetSlot[] = referenceSlots
    .filter((slot) => !slot.is_upload_placeholder)
    .slice(0, COZE_TEMPLATE_REFERENCE_IMAGE_LIMIT)
    .map((referenceSlot, index) => {
      const entry = entryByKey.get(referenceSlot.slot_key);
      const imageUrl = entry?.imageUrl || "";
      return {
        ...(entry?.slot || {}),
        ...referenceSlot,
        id: referenceSlot.id ?? COZE_TEMPLATE_REFERENCE_DRAFT_ID - index - 1,
        // Local picker/user-module keys may be arbitrary. The reference draft
        // contract uses canonical positional keys so sparse slot identity
        // remains stable across generate, reverse, history restore and reopen.
        slot_key: `reference_${index + 1}`,
        slot_name: getCozeTemplateSequentialSlotName(index),
        sort_order: index + 1,
        param_key: cleanText(referenceSlot.param_key) || `reference_${index + 1}`,
        template_image: imageUrl,
        image_url: imageUrl,
        // The draft's first five positions are the fixed skeleton even when
        // a dynamic image was dragged into one of those empty slots. Keep
        // user-module identity only for dynamic positions 5–9 so reopening
        // the picker preserves the visual position instead of reclassifying
        // the image as a later personal module.
        is_user_slot: index >= COZE_TEMPLATE_FIXED_REFERENCE_SLOT_COUNT,
        is_reference_picker_session_slot: true,
        selected: Boolean(entry),
        prompt_by_variant: {},
        prompt_status_by_variant: {},
      };
    });

  const mergedSlots = slots;
  const remappedUploadedImages = existing.uploaded_images.map((image) => {
    const existingSlotKey = existingSlotKeyByName.get(cleanText(image.definition));
    const nextSlotName = existingSlotKey ? referenceSlotsByKey.get(existingSlotKey)?.slot_name : undefined;
    if (!existingSlotKey) return [image];
    return nextSlotName ? [{ ...image, definition: nextSlotName }] : [];
  }).flat();

  return selectReferenceDraftSlot({
    template_set_id: COZE_TEMPLATE_REFERENCE_DRAFT_ID,
    template_set_name: cleanProductType ? `${cleanProductType}参考图模版` : "参考图模版",
    template_set_cover_url: entries[0]?.imageUrl || "",
    category: cleanProductType,
    size_label: "",
    variant: existing.variant,
    slots: mergedSlots,
    uploaded_images: remappedUploadedImages,
  });
}

function DeleteCustomTemplateConfirmModal({
  template,
  deleting,
  onClose,
  onConfirm,
}: {
  template: CozeTemplateSetSummary | null;
  deleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    if (!template) return;
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape" && !deleting) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [deleting, onClose, template]);

  if (!template || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[370] flex items-center justify-center bg-black/20 px-4 py-6 backdrop-blur-[7.5px]"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget && !deleting) {
          onClose();
        }
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label="删除自定义模版"
        className="w-[min(420px,calc(100vw-32px))] overflow-hidden rounded-[15px] border border-[#353537] bg-[#181818] shadow-2xl"
      >
        <div className="flex h-[70px] items-center justify-between border-b border-[#353537] px-8">
          <h3 className="text-[14px] font-medium text-[#F6F6F6]">删除自定义模版</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] text-[#8C8C8C] transition-colors hover:bg-white/8 hover:text-[#F6F6F6] disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="关闭删除确认"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-8 pb-7 pt-6">
          <p className="text-[14px] leading-6 text-[#F6F6F6]">
            确认删除「<span className="font-medium text-white">{template.name}</span>」？
          </p>
          <p className="mt-2 text-[12px] leading-5 text-[#8C8C8C]">删除后无法恢复，该模版将从我的模版中移除。</p>
          <div className="mt-7 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={deleting}
              className="h-10 w-[74px] rounded-[12px] border border-[#3F3F3F] text-sm font-normal text-[#9F9F9F] transition-colors hover:border-[#555] hover:text-[#F6F6F6] disabled:cursor-not-allowed disabled:opacity-60"
            >
              取消
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={deleting}
              className="inline-flex h-10 w-[112px] items-center justify-center rounded-[8px] bg-[#FAFBFF] text-sm font-medium text-[#222222] transition-colors hover:bg-[#F1F3FA] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {deleting && <Loader2 size={14} className="mr-2 animate-spin" />}
              确认删除
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function TemplateSecondaryPreviewStrip({
  images,
  templateName,
  onPreviewClick,
}: {
  images: string[];
  templateName: string;
  onPreviewClick?: () => void;
}) {
  const stripRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [atEnd, setAtEnd] = useState(false);

  const updateOverflow = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const overflow = strip.scrollWidth > strip.clientWidth + 1;
    const end = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 1;
    setHasOverflow(overflow);
    setAtEnd(end);
  }, []);

  useEffect(() => {
    updateOverflow();
    const strip = stripRef.current;
    if (!strip) return undefined;
    strip.addEventListener("scroll", updateOverflow, { passive: true });
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateOverflow) : null;
    observer?.observe(strip);
    return () => {
      strip.removeEventListener("scroll", updateOverflow);
      observer?.disconnect();
    };
  }, [images.length, updateOverflow]);

  if (images.length === 0) {
    return <div className="h-12" aria-hidden="true" />;
  }

  const movePreview = () => {
    const strip = stripRef.current;
    if (!strip) return;
    if (atEnd) {
      strip.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }
    strip.scrollBy({ left: 56, behavior: "smooth" });
  };

  return (
    <div
      className="relative min-w-0"
      data-coze-template-preview-strip
      data-coze-template-preview-overflow={hasOverflow ? "true" : "false"}
    >
      <div
        ref={stripRef}
        className="flex min-w-0 gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        aria-label={`${templateName}副图预览`}
      >
        {images.map((image, index) => (
          <button
            type="button"
            key={`${image}-${index}`}
            onClick={(event) => {
              event.stopPropagation();
              onPreviewClick?.();
            }}
            className="size-12 shrink-0 cursor-pointer overflow-hidden rounded-[4px] bg-[#272727] outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-white/70"
            data-coze-template-secondary-preview
            aria-label={`${templateName}副图${index + 1}预览`}
          >
            <TemplateImage src={image} alt={`${templateName}副图${index + 1}`} className="size-full object-cover" draggable={false} />
          </button>
        ))}
      </div>
      {hasOverflow && (
        <>
          <div
            className={cn(
              "pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-r from-transparent to-[#181818]",
              atEnd && "from-[#181818]/20 to-[#181818]",
            )}
            data-coze-template-secondary-fade
            aria-hidden="true"
          />
          <button
            type="button"
            onClick={movePreview}
            className="absolute right-0 top-1/2 z-10 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-[#222]/90 text-white/80 shadow-[0_2px_8px_rgba(0,0,0,.28)] transition-colors hover:bg-[#333] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            aria-label={atEnd ? "回到副图预览开头" : "查看更多副图预览"}
            data-coze-template-secondary-next
          >
            <ChevronRight className={cn("size-4", atEnd && "rotate-180")} aria-hidden="true" />
          </button>
        </>
      )}
    </div>
  );
}

function TemplateLibraryCategoryFilter({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string;
  onChange: (value: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: true, end: true });

  useEffect(() => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    const updateEdges = () => setEdges({
      start: scroll.scrollLeft <= 1,
      end: scroll.scrollLeft + scroll.clientWidth >= scroll.scrollWidth - 1,
    });
    const handleWheel = (event: WheelEvent) => {
      if (event.ctrlKey || scroll.scrollWidth <= scroll.clientWidth + 1) return;
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (!delta) return;
      event.preventDefault();
      const unit = event.deltaMode === 1 ? 32 : event.deltaMode === 2 ? scroll.clientWidth : 1;
      scroll.scrollLeft += delta * unit;
    };
    updateEdges();
    scroll.addEventListener("scroll", updateEdges, { passive: true });
    // A non-passive listener lets the wheel scroll categories without moving the template list.
    scroll.addEventListener("wheel", handleWheel, { passive: false });
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateEdges) : null;
    observer?.observe(scroll);
    return () => {
      scroll.removeEventListener("scroll", updateEdges);
      scroll.removeEventListener("wheel", handleWheel);
      observer?.disconnect();
    };
  }, [options]);

  useEffect(() => {
    scrollRef.current?.querySelector('[aria-pressed="true"]')?.scrollIntoView({
      block: "nearest",
      inline: "nearest",
    });
  }, [options, value]);

  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <button
        type="button"
        aria-label="向左滚动产品分类"
        disabled={edges.start}
        onClick={() => scrollRef.current?.scrollBy({ left: -240, behavior: "smooth" })}
        className="flex size-7 shrink-0 items-center justify-center rounded-full text-white/70 hover:bg-white/10 disabled:cursor-default disabled:opacity-25"
      >
        <ChevronRight size={16} className="rotate-180" aria-hidden="true" />
      </button>
      <div
        ref={scrollRef}
        role="group"
        aria-label="产品类型分类，可使用鼠标滚轮横向浏览"
        data-coze-template-category-scroll
        className="flex min-w-0 flex-1 flex-nowrap items-center gap-2 overflow-x-auto overscroll-x-contain py-1 whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {["全部", ...options].map((item) => {
          const category = item === "全部" ? "" : item;
          const active = value === category;
          return (
            <button
              key={item}
              type="button"
              onClick={() => onChange(category)}
              className={cn(
                "inline-flex h-8 shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-[8px] px-3 text-center text-white/72 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/45",
                active && "bg-white/12 text-white",
              )}
              aria-pressed={active}
            >
              {item}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        aria-label="向右滚动产品分类"
        disabled={edges.end}
        onClick={() => scrollRef.current?.scrollBy({ left: 240, behavior: "smooth" })}
        className="flex size-7 shrink-0 items-center justify-center rounded-full text-white/70 hover:bg-white/10 disabled:cursor-default disabled:opacity-25"
      >
        <ChevronRight size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

function TemplateLibrarySkeletonCard() {
  return (
    <div
      className="flex aspect-[275/399] h-auto w-full min-w-0 flex-col overflow-hidden rounded-[12px] bg-[#181818]"
      data-coze-template-skeleton-card
      aria-hidden="true"
    >
      <Skeleton className="aspect-square h-auto w-full rounded-none bg-[#2A2A2A]" />
      <div className="flex h-[clamp(56px,5vw,64px)] shrink-0 items-center gap-2 px-[14px]">
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} className="size-12 shrink-0 rounded-[4px] bg-[#292929]" />
        ))}
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center px-[14px]">
        <Skeleton className="h-9 w-[143px] rounded-[8px] bg-[#292929]" />
      </div>
    </div>
  );
}

export default function CozeTemplateSetField({
  comp,
  workflowKey,
  value,
  sourceImages,
  onChange,
  myCosts,
  notifyUploading,
  onPromptGeneratingChange,
  onHoverPreviewChange,
  onOnboardingChange,
  leftNavCollapsed = false,
  reversePromptHistoryScope,
  mainReplicaTutorialConfig,
  imageReferenceBindingEnabled = false,
}: CozeTemplateSetFieldProps) {
  const feedback = useEcomFeedback();
  const normalizedValue = useMemo(() => normalizeCozeTemplateSetValue(value), [value]);
  const draftValue = useMemo<CozeTemplateSetValue>(() => (
    normalizedValue
      ? normalizedValue.template_set_id === COZE_TEMPLATE_REFERENCE_DRAFT_ID
        ? selectReferenceDraftSlot(normalizedValue)
        : normalizeSingleSelectedSlot(normalizedValue)
      : {
          variant: "standard",
          slots: [],
          uploaded_images: [],
        }
  ), [normalizedValue]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [templateSets, setTemplateSets] = useState<CozeTemplateSetSummary[]>([]);
  const [mineTemplateSets, setMineTemplateSets] = useState<CozeTemplateSetSummary[]>([]);
  const [userModules, setUserModules] = useState<CozeTemplateUserModuleDTO[]>([]);
  const userModulesRef = useRef<CozeTemplateUserModuleDTO[]>([]);
  const syncUserModules = useCallback((next: CozeTemplateUserModuleDTO[] | ((prev: CozeTemplateUserModuleDTO[]) => CozeTemplateUserModuleDTO[])) => {
    setUserModules((prev) => {
      const resolved = typeof next === "function" ? (next as (p: CozeTemplateUserModuleDTO[]) => CozeTemplateUserModuleDTO[])(prev) : next;
      userModulesRef.current = resolved;
      return resolved;
    });
  }, []);
  const [loadingUserModules, setLoadingUserModules] = useState(false);
  const [userModulesLoaded, setUserModulesLoaded] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [mineTemplatesLoaded, setMineTemplatesLoaded] = useState(false);
  const [loadingMineTemplates, setLoadingMineTemplates] = useState(false);
  const [mineTemplateError, setMineTemplateError] = useState("");
  const [templateError, setTemplateError] = useState("");
  const [applyingId, setApplyingId] = useState<number | null>(null);
  // Keep the apply interaction independent from the card hover state. The
  // library card remains a simple pointer target; only the apply button
  // should activate the image-area mask.
  const [applyHoverTemplateId, setApplyHoverTemplateId] = useState<number | null>(null);
  const [previewDetail, setPreviewDetail] = useState<CozeTemplateSetDetail | null>(null);
  const [previewTemplateDraftId, setPreviewTemplateDraftId] = useState<number | null>(null);
  const [previewTemplateSnapshot, setPreviewTemplateSnapshot] = useState<TemplatePreviewSlot[]>([]);
  const [previewTemplateDraft, setPreviewTemplateDraft] = useState<TemplatePreviewSlot[]>([]);
  const previewTemplateDraftRef = useRef<TemplatePreviewSlot[]>([]);
  const previewTemplateEditSessionRef = useRef(0);
  const [previewTemplateUploadingSlotKeys, setPreviewTemplateUploadingSlotKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [savingPreviewTemplate, setSavingPreviewTemplate] = useState(false);
  const [previewSlotKey, setPreviewSlotKey] = useState("");
  const [loadingDetailId, setLoadingDetailId] = useState<number | null>(null);
  const [libraryCategory, setLibraryCategory] = useState("");
  const [libraryNotice, setLibraryNotice] = useState("");
  const [referencePickerOpen, setReferencePickerOpen] = useState(false);
  const [referencePickerContext, setReferencePickerContext] = useState<CozeTemplateSetValue | null>(null);
  // User slots created from the picker tail belong to this picker session. Keep
  // them separate from the account-level module list so an async refresh of
  // historical modules cannot remove the newly uploaded image from the UI.
  const [referencePickerSessionSlots, setReferencePickerSessionSlots] = useState<CozeTemplateReferencePickerSlot[]>([]);
  const [referencePickerSessionTemplateId, setReferencePickerSessionTemplateId] = useState<number | null>(null);
  const [selectedReferenceSlotKey, setSelectedReferenceSlotKey] = useState("");
  const [croppingReferenceSlot, setCroppingReferenceSlot] = useState<{
    slotKey: string;
    slotName: string;
    imageUrl: string;
  } | null>(null);
  const [croppingPreviewTemplateSlot, setCroppingPreviewTemplateSlot] = useState<{
    slotKey: string;
    imageUrl: string;
  } | null>(null);
  const [referencePickerSaveTemplate, setReferencePickerSaveTemplate] = useState(false);
  const [referencePickerProductType, setReferencePickerProductType] = useState(COZE_TEMPLATE_DEFAULT_PRODUCT_TYPES[0] || "");
  const [pendingDeleteTemplate, setPendingDeleteTemplate] = useState<CozeTemplateSetSummary | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState(false);
  const [referencePickerDraftImages, setReferencePickerDraftImages] = useState<Record<string, ReferencePickerDraftImage>>({});
  const [referencePickerRemovedSlotKeys, setReferencePickerRemovedSlotKeys] = useState<Set<string>>(() => new Set());
  const [referencePickerSlotOrder, setReferencePickerSlotOrder] = useState<string[]>([]);
  const [dragOverReferenceSlotKey, setDragOverReferenceSlotKey] = useState<string | null>(null);
  const [dragOverPreviewSlotKey, setDragOverPreviewSlotKey] = useState<string | null>(null);
  const [referencePickerUploadingSlotKeys, setReferencePickerUploadingSlotKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const uploadingCustomReference = referencePickerUploadingSlotKeys.size > 0;
  const [addingCustom, setAddingCustom] = useState(false);
  const [uploadingSlotKey, setUploadingSlotKey] = useState<string | null>(null);
  const [reverseModalStage, setReverseModalStage] = useState<ReverseModalStage>("closed");
  const [reverseModalOpen, setReverseModalOpen] = useState(false);
  const [reversePromptDrafts, setReversePromptDrafts] = useState<CozeTemplateReversePromptDraftItem[]>([]);
  const [reverseProgressBySlot, setReverseProgressBySlot] = useState<Record<string, number>>({});
  const [pendingReverseActions, setPendingReverseActions] = useState<Record<string, CozeReversePromptPendingAction>>({});
  const activeReverseActionsRef = useRef(new Map<string, CozeReversePromptPendingAction>());
  const reversePromptDraftsRef = useRef<CozeTemplateReversePromptDraftItem[]>([]);
  const editedReversePromptSlotKeysRef = useRef(new Set<string>());
  const pendingReverseSlotKeys = useMemo(() => new Set(
    Object.values(pendingReverseActions).flatMap((action) => action.requested_slot_keys),
  ), [pendingReverseActions]);
  const reverseGeneratingSlotKey = useMemo(
    () => reversePromptDrafts
      .filter((item) => item.status === "generating")
      .map((item) => item.slot_key)
      .join("|"),
    [reversePromptDrafts],
  );
  const promptGenerating = useMemo(
    () => reverseModalStage === "generating" || normalizedValue?.slots.some((slot) => (
      slot.prompt_status_by_variant?.[normalizedValue.variant] === "generating"
    )) || false,
    [normalizedValue, reverseModalStage],
  );

  useEffect(() => {
    onPromptGeneratingChange?.(comp.coze_param_key, promptGenerating);
  }, [comp.coze_param_key, onPromptGeneratingChange, promptGenerating]);

  useEffect(() => {
    if (reverseModalStage !== "generating" || !reverseGeneratingSlotKey) {
      setReverseProgressBySlot({});
      return undefined;
    }

    const slotKeys = reverseGeneratingSlotKey.split("|");
    setReverseProgressBySlot((current) => Object.fromEntries(
      slotKeys.map((slotKey, index) => [
        slotKey,
        current[slotKey] ?? createReverseProgress(index),
      ]),
    ));
    let tick = 0;
    const timer = window.setInterval(() => {
      setReverseProgressBySlot((current) => Object.fromEntries(
        slotKeys.map((slotKey, index) => [
          slotKey,
          advanceReverseProgress(
            current[slotKey] ?? createReverseProgress(index),
            index === tick % slotKeys.length,
          ),
        ]),
      ));
      tick += 1;
    }, 480);

    return () => window.clearInterval(timer);
  }, [reverseGeneratingSlotKey, reverseModalStage]);

  useEffect(() => () => {
    activeReverseActionsRef.current.clear();
    onPromptGeneratingChange?.(comp.coze_param_key, false);
  }, [comp.coze_param_key, onPromptGeneratingChange]);
  useEffect(() => {
    reversePromptDraftsRef.current = reversePromptDrafts;
  }, [reversePromptDrafts]);
  const [uploadingProductImages, setUploadingProductImages] = useState(false);
  const [reorderingPreviewTemplate, setReorderingPreviewTemplate] = useState(false);
  const [croppingProductImage, setCroppingProductImage] = useState<CozeTemplateUploadedImage | null>(null);
  const [productDragOver, setProductDragOver] = useState(false);
  const [productReorderDragIndex, setProductReorderDragIndex] = useState<number | null>(null);
  const [productReorderOverIndex, setProductReorderOverIndex] = useState<number | null>(null);
  const productReorderDragIndexRef = useRef<number | null>(null);
  const [customReferenceDragOver, setCustomReferenceDragOver] = useState(false);
  const [activeSlotKey, setActiveSlotKey] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const productUploadInputRef = useRef<HTMLInputElement>(null);
  const promptTextareaRef = useRef<HTMLTextAreaElement>(null);
  const customReferenceInputRef = useRef<HTMLInputElement>(null);
  const libraryScrollRef = useRef<HTMLDivElement>(null);
  const templateDetailRef = useRef<HTMLDivElement>(null);
  const templateDetailRequestRef = useRef(0);
  const templateNavigationPendingRef = useRef(false);
  const reverseModalScrollRef = useRef<HTMLDivElement>(null);
  const pendingUploadSlotKeyRef = useRef<string | null>(null);
  const pendingUploadTemplateSetIdRef = useRef<number | null>(null);
  const pendingUploadUserModuleIdRef = useRef<number | null>(null);
  const pendingReferencePickerSlotKeyRef = useRef<string | null>(null);
  const referencePickerOpenRef = useRef(false);
  const referencePickerUploadSeqRef = useRef(0);
  const referencePickerUploadTokenBySlotRef = useRef(new Map<string, number>());
  const referencePickerKeepSessionSlotsRef = useRef(false);
  const pendingReferencePickerFilesRef = useRef<File[] | null>(null);
  const userModulesRequestedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const latestValueRef = useRef<CozeTemplateSetValue | null>(draftValue);
  const applyingTemplateIdRef = useRef<number | null>(null);
  const deletingTemplateRef = useRef(false);
  const [libraryHasBottomOverflow, setLibraryHasBottomOverflow] = useState(false);
  const [reverseModalHasBottomOverflow, setReverseModalHasBottomOverflow] = useState(false);

  const syncPreviewTemplateDraft = useCallback((
    next: TemplatePreviewSlot[] | ((current: TemplatePreviewSlot[]) => TemplatePreviewSlot[]),
  ) => {
    setPreviewTemplateDraft((current) => {
      const resolved = typeof next === "function"
        ? (next as (value: TemplatePreviewSlot[]) => TemplatePreviewSlot[])(current)
        : next;
      previewTemplateDraftRef.current = resolved;
      return resolved;
    });
  }, []);

  const resetPreviewTemplateDraft = useCallback(() => {
    previewTemplateEditSessionRef.current += 1;
    const blobUrls = new Set(previewTemplateDraftRef.current.flatMap((slot) => [
      cleanText(slot.image_url),
      cleanText(slot.template_image),
    ]).filter(isBlobUrl));
    blobUrls.forEach(revokeBlobUrl);
    previewTemplateDraftRef.current = [];
    setPreviewTemplateDraft([]);
    setPreviewTemplateSnapshot([]);
    setPreviewTemplateDraftId(null);
    setPreviewTemplateUploadingSlotKeys(new Set());
    setSavingPreviewTemplate(false);
  }, []);

  const initializePreviewTemplateDraft = useCallback((detail: CozeTemplateSetDetail) => {
    resetPreviewTemplateDraft();
    const draft = createPreviewTemplateDraft(detail);
    const snapshot = draft.map((slot) => ({ ...slot }));
    previewTemplateDraftRef.current = draft;
    setPreviewTemplateDraft(draft);
    setPreviewTemplateSnapshot(snapshot);
    setPreviewTemplateDraftId(detail.id);
  }, [resetPreviewTemplateDraft]);

  useEffect(() => {
    latestValueRef.current = draftValue;
  }, [draftValue]);

  useEffect(() => {
    if (
      reversePromptDrafts.length === 0 ||
      reversePromptDrafts.some((item) => item.status === "generating")
    ) {
      return;
    }
    saveCozeReversePromptHistory(
      reversePromptDrafts,
      reversePromptHistoryScope,
      workflowKey,
      comp.coze_param_key,
    );
  }, [
    comp.coze_param_key,
    reversePromptDrafts,
    reversePromptHistoryScope,
    workflowKey,
  ]);

  useEffect(() => {
    referencePickerOpenRef.current = referencePickerOpen;
  }, [referencePickerOpen]);

  const allowUserSlots = comp.config?.allow_user_slots !== false;
  const maxUserSlots = typeof comp.config?.max_user_slots === "number" && comp.config.max_user_slots > 0
    ? comp.config.max_user_slots
    : 12;
  const referencePickerUserSlotLimit = Math.min(
    maxUserSlots,
    COZE_TEMPLATE_REFERENCE_IMAGE_LIMIT - 5,
  );
  const maxProductImages = typeof comp.config?.max_images === "number" && comp.config.max_images > 0
    ? comp.config.max_images
    : 14;
  const mineTemplateIds = useMemo(
    () => new Set(mineTemplateSets.map((template) => template.id)),
    [mineTemplateSets],
  );
  const includeUserModulesForCurrent = useMemo(
    () => shouldProjectCozeTemplateUserModules(
      normalizedValue,
      normalizedValue?.template_set_id !== undefined && mineTemplateIds.has(normalizedValue.template_set_id),
    ),
    [mineTemplateIds, normalizedValue],
  );
  const isReferenceDraft = normalizedValue?.template_set_id === COZE_TEMPLATE_REFERENCE_DRAFT_ID;

  const slots = useMemo(
    () => isReferenceDraft
      ? [...(draftValue.slots || [])].sort((left, right) => (
          (left.sort_order ?? Number.MAX_SAFE_INTEGER) -
          (right.sort_order ?? Number.MAX_SAFE_INTEGER)
        ))
      : resolveUserModuleSlots(
        draftValue.slots || [],
        userModules,
        includeUserModulesForCurrent ? userModulesLoaded : true,
        includeUserModulesForCurrent,
      ),
    [draftValue, includeUserModulesForCurrent, isReferenceDraft, userModules, userModulesLoaded],
  );
  const visibleSlots = useMemo(
    () => projectCozeTemplateWorkspaceSlots(slots),
    [slots],
  );
  const visibleSlotDisplayNames = useMemo(
    () => new Map(
      (isReferenceDraft ? visibleSlots : normalizeCozeTemplateSequentialSlotNames(visibleSlots))
        .map((slot) => [slot.slot_key, slot.slot_name]),
    ),
    [isReferenceDraft, visibleSlots],
  );
  const productDefinitionOptions = useMemo(
    () => getCozeTemplateProductImageDefinitionOptions(
      slots,
      includeUserModulesForCurrent ? userModules : [],
    ),
    [includeUserModulesForCurrent, slots, userModules],
  );
  const hasAppliedTemplate = Boolean(normalizedValue?.template_set_id);
  const appliedTemplateId = normalizedValue?.template_set_id;
  const isReferenceDraftApplied = appliedTemplateId === COZE_TEMPLATE_REFERENCE_DRAFT_ID;
  const showOnboarding = shouldShowCozeTemplateOnboarding(normalizedValue);
  useLayoutEffect(() => {
    onOnboardingChange?.(showOnboarding);
    return () => onOnboardingChange?.(false);
  }, [onOnboardingChange, showOnboarding]);
  const validation = useMemo(() => validateCozeTemplateSetValue(normalizedValue), [normalizedValue]);
  const generatableSlotCount = useMemo(
    () => getCozeTemplateSetGeneratableSlots(normalizedValue).length,
    [normalizedValue],
  );
  const statusMessage = useMemo(() => {
    if (hasAppliedTemplate && normalizedValue) {
      return validation.ok && generatableSlotCount > 0
        ? ""
        : getCozeTemplateSetValidationMessage(validation);
    }
    return "请先从模板库选择模版";
  }, [
    generatableSlotCount,
    hasAppliedTemplate,
    normalizedValue,
    validation,
  ]);
  const referencePickerContextValue = referencePickerContext;
  const referencePickerSlots = useMemo(() => {
    const baseSlots = getCozeTemplateReferencePickerSlots(referencePickerContextValue?.slots || [], {
      includeUploadSlot: false,
      includeUserSlots: referencePickerContextValue?.template_set_id === COZE_TEMPLATE_REFERENCE_DRAFT_ID,
      maxUserSlots: referencePickerUserSlotLimit,
    });
    const baseSlotKeys = new Set(baseSlots.map((slot) => slot.slot_key));
    const sessionSlotsByKey = new Map<string, ReferencePickerSessionSlot>();
    (referencePickerContextValue?.slots || []).forEach((slot) => {
      const sessionSlot = slot as ReferencePickerSessionSlot;
      if (sessionSlot.is_reference_picker_session_slot) sessionSlotsByKey.set(slot.slot_key, sessionSlot);
    });
    referencePickerSessionSlots.forEach((slot) => sessionSlotsByKey.set(slot.slot_key, slot));
    const sessionSlots = Array.from(sessionSlotsByKey.values()).filter((slot) => !baseSlotKeys.has(slot.slot_key));
    const slotsWithSession = [...baseSlots, ...sessionSlots].slice(0, COZE_TEMPLATE_REFERENCE_IMAGE_LIMIT);
    const slotsWithVirtualTail = [...slotsWithSession];
    if (allowUserSlots && slotsWithSession.length < COZE_TEMPLATE_REFERENCE_IMAGE_LIMIT) {
      const nextIndex = slotsWithSession.length;
      slotsWithVirtualTail.push({
        slot_key: getCozeTemplateReferenceUploadSlotKey(slotsWithSession),
        slot_name: getCozeTemplateSequentialSlotName(nextIndex),
        sort_order: nextIndex + 1,
        is_upload_placeholder: true,
      });
    }
    if (slotsWithVirtualTail.length === 0 || referencePickerSlotOrder.length === 0) return slotsWithVirtualTail;
    const slotsByKey = new Map(slotsWithVirtualTail.map((slot) => [slot.slot_key, slot]));
    const ordered = referencePickerSlotOrder.flatMap((slotKey) => {
      const slot = slotsByKey.get(slotKey);
      if (!slot) return [];
      slotsByKey.delete(slotKey);
      return [slot];
    });
    return [...ordered, ...Array.from(slotsByKey.values())];
  }, [allowUserSlots, referencePickerContextValue, referencePickerSessionSlots, referencePickerSlotOrder, referencePickerUserSlotLimit]);
  const referencePickerSlotImages = useMemo(() => {
    const images = new Map<string, string>();
    for (const slot of referencePickerSlots) {
      const persistedImage = cleanText(slot.template_image) || cleanText(slot.image_url);
      const draft = referencePickerDraftImages[slot.slot_key];
      const draftImage = draft ? cleanText(draft.previewUrl) || cleanText(draft.imageUrl) : "";
      images.set(
        slot.slot_key,
        draftImage || (referencePickerRemovedSlotKeys.has(slot.slot_key) ? "" : persistedImage),
      );
    }
    return images;
  }, [referencePickerDraftImages, referencePickerRemovedSlotKeys, referencePickerSlots]);
  const referencePickerApplicableEntries = useMemo(() => (
    getCozeTemplateApplicableReferenceEntries(
      referencePickerSlots,
      referencePickerDraftImages,
      referencePickerRemovedSlotKeys,
    )
  ), [referencePickerDraftImages, referencePickerRemovedSlotKeys, referencePickerSlots]);
  const referencePickerHasApplicableImage = referencePickerApplicableEntries.length > 0;
  const selectedReferencePickerSlot = useMemo(() => (
    referencePickerSlots.find((slot) => (
      slot.slot_key === selectedReferenceSlotKey &&
      Boolean(referencePickerSlotImages.get(slot.slot_key))
    )) ||
    referencePickerSlots.find((slot) => Boolean(referencePickerSlotImages.get(slot.slot_key))) ||
    null
  ), [referencePickerSlotImages, referencePickerSlots, selectedReferenceSlotKey]);
  const referencePickerPreviewImage = selectedReferencePickerSlot
    ? referencePickerSlotImages.get(selectedReferencePickerSlot.slot_key) || ""
    : "";
  const referencePickerFilledSlots = useMemo(
    () => referencePickerSlots.filter((slot) => Boolean(referencePickerSlotImages.get(slot.slot_key))),
    [referencePickerSlotImages, referencePickerSlots],
  );
  const referencePickerDisplaySlots = useMemo(
    () => projectCozeTemplateReferencePickerDisplaySlots(
      referencePickerSlots,
      new Set(referencePickerFilledSlots.map((slot) => slot.slot_key)),
      referencePickerRemovedSlotKeys,
    ),
    [referencePickerFilledSlots, referencePickerRemovedSlotKeys, referencePickerSlots],
  );
  const referenceUploadDialogSlots = useMemo(
    () => referencePickerDisplaySlots.map((slot, slotIndex) => {
      const imageUrl = referencePickerSlotImages.get(slot.slot_key) || "";
      return {
        slotKey: slot.slot_key,
        slotName: slot.slot_name,
        displayName: getCozeTemplateSequentialSlotName(slotIndex),
        imageUrl,
        active: Boolean(imageUrl) && selectedReferencePickerSlot?.slot_key === slot.slot_key,
        isUploadPlaceholder: Boolean(slot.is_upload_placeholder),
        draggable: Boolean(imageUrl) && !uploadingCustomReference,
        dragTarget: dragOverReferenceSlotKey === slot.slot_key,
      };
    }),
    [
      dragOverReferenceSlotKey,
      referencePickerDisplaySlots,
      referencePickerSlotImages,
      selectedReferencePickerSlot?.slot_key,
      uploadingCustomReference,
    ],
  );
  const selectableSlotNames = useMemo(
    () => new Set(slots.filter((slot) => getCozeTemplateSlotImage(slot)).map((slot) => cleanText(slot.slot_name))),
    [slots],
  );
  const activeSlot = useMemo(
    () => resolveCozeTemplateActiveSlot(slots, activeSlotKey, selectableSlotNames),
    [activeSlotKey, selectableSlotNames, slots],
  );
  const activeSlotDisplayName = activeSlot
    ? visibleSlotDisplayNames.get(activeSlot.slot_key) || activeSlot.slot_name
    : "";
  const activeUploadedImages = useMemo(
    () => getCozeTemplateSlotUploadedImages(draftValue, activeSlot?.slot_name || ""),
    [activeSlot?.slot_name, draftValue],
  );
  const canReversePrompts = useMemo(
    () => Boolean(normalizedValue?.template_set_id) &&
      getCozeTemplateReverseRequests(draftValue, draftValue.slots, true).length > 0,
    [draftValue, normalizedValue?.template_set_id],
  );
  const libraryDataLoading = loadingTemplates || loadingMineTemplates || !templatesLoaded || !mineTemplatesLoaded;
  const allLibraryTemplateSets = useMemo<CozeTemplateSetSummary[]>(() => {
    if (libraryDataLoading) return [];
    return templateSets;
  }, [libraryDataLoading, templateSets]);
  const mergedLibraryTemplateSets = useMemo(() => {
    const summariesByID = new Map<number, CozeTemplateSetSummary>();
    allLibraryTemplateSets.forEach((template) => summariesByID.set(template.id, template));
    // A custom template occurs in both endpoints. Prefer its `mine=true`
    // summary, which carries the owner's complete slot image list; the general
    // list can be a lightweight projection and omit secondary thumbnails.
    mineTemplateSets.forEach((template) => summariesByID.set(template.id, template));
    return Array.from(summariesByID.values());
  }, [allLibraryTemplateSets, mineTemplateSets]);
  const templateDisplaySources = useMemo(() => Object.assign(
    {},
    ...mergedLibraryTemplateSets.map((template) => template.image_sources || {}),
    previewDetail?.image_sources || {},
  ), [mergedLibraryTemplateSets, previewDetail?.image_sources]);
  const libraryProductTypeOptions = useMemo(
    () => getCozeTemplateProductTypeOptions(mergedLibraryTemplateSets),
    [mergedLibraryTemplateSets],
  );
  const filteredTemplateSets = useMemo(() => (
    filterCozeTemplateSetSummaries(mergedLibraryTemplateSets, {
      keyword: "",
      category: libraryCategory,
    })
  ), [libraryCategory, mergedLibraryTemplateSets]);
  const updateLibraryOverflow = useCallback(() => {
    const scroll = libraryScrollRef.current;
    if (!scroll) return;
    setLibraryHasBottomOverflow(scroll.scrollHeight > scroll.clientHeight + 1 &&
      scroll.scrollTop + scroll.clientHeight < scroll.scrollHeight - 1);
  }, []);
  useEffect(() => {
    if (!libraryOpen) {
      setLibraryHasBottomOverflow(false);
      return undefined;
    }
    updateLibraryOverflow();
    const scroll = libraryScrollRef.current;
    if (!scroll) return undefined;
    scroll.addEventListener("scroll", updateLibraryOverflow, { passive: true });
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateLibraryOverflow) : null;
    observer?.observe(scroll);
    return () => {
      scroll.removeEventListener("scroll", updateLibraryOverflow);
      observer?.disconnect();
    };
  }, [filteredTemplateSets.length, libraryDataLoading, libraryOpen, updateLibraryOverflow]);
  const updateReverseModalOverflow = useCallback(() => {
    const scroll = reverseModalScrollRef.current;
    if (!scroll) return;
    setReverseModalHasBottomOverflow(scroll.scrollHeight > scroll.clientHeight + 1 &&
      scroll.scrollTop + scroll.clientHeight < scroll.scrollHeight - 1);
  }, []);
  useEffect(() => {
    if (!reverseModalOpen || reverseModalStage === "closed") {
      setReverseModalHasBottomOverflow(false);
      return undefined;
    }
    updateReverseModalOverflow();
    const scroll = reverseModalScrollRef.current;
    if (!scroll) return undefined;
    scroll.addEventListener("scroll", updateReverseModalOverflow, { passive: true });
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateReverseModalOverflow) : null;
    observer?.observe(scroll);
    return () => {
      scroll.removeEventListener("scroll", updateReverseModalOverflow);
      observer?.disconnect();
    };
  }, [reverseModalOpen, reverseModalStage, reversePromptDrafts, updateReverseModalOverflow]);
  const ownedPreviewDetail = Boolean(previewDetail && mineTemplateIds.has(previewDetail.id));
  const previewTemplateDraftActive = Boolean(
    previewDetail && previewTemplateDraftId === previewDetail.id,
  );
  const previewTemplateDirty = useMemo(() => (
    ownedPreviewDetail && previewTemplateDraftActive &&
    getPreviewTemplateDraftSignature(previewTemplateDraft) !==
      getPreviewTemplateDraftSignature(previewTemplateSnapshot)
  ), [
    ownedPreviewDetail,
    previewTemplateDraft,
    previewTemplateDraftActive,
    previewTemplateSnapshot,
  ]);
  const uploadingPreviewTemplate = previewTemplateUploadingSlotKeys.size > 0;
  const previewSlots = useMemo(() => {
    const previewTemplateSlots = previewTemplateDraftActive
      ? previewTemplateDraft
      : getTemplatePreviewSlots(
        previewDetail,
        userModules,
        userModulesLoaded,
        false,
        ownedPreviewDetail,
      );
    // 投影会裁掉尾部空系统槽并保留账号上传槽，连续展示名必须跟随投影后的
    // 顺序重新编号：模板只建到副图6 时，尾部上传追加的账号槽显示为副图7，
    // 而不是原始全量序列里的副图19。slot_key 保持稳定，仅展示名按顺序重排。
    return normalizeCozeTemplateSequentialSlotNames(
      getCozeTemplateDetailPreviewSlots(previewTemplateSlots, {
        ownedTemplate: ownedPreviewDetail,
        // 平台预置模板（非 owned）不追加尾部上传框，主站不允许对其做任何修改；
        // 仅自建模板（owned）在 allowUserSlots 开启时才显示尾部上传槽。
        includeUploadSlot: allowUserSlots && ownedPreviewDetail,
      }),
    ).map((slot) => ({
      ...slot,
      template_set_id: previewDetail?.id || 0,
    }));
  }, [
    allowUserSlots,
    ownedPreviewDetail,
    previewDetail,
    previewTemplateDraft,
    previewTemplateDraftActive,
    userModules,
    userModulesLoaded,
  ]);
  const previewImageSlots = useMemo(
    () => previewSlots.filter((slot) => !slot.is_upload_placeholder),
    [previewSlots],
  );
  const previewFilledSlotCount = useMemo(
    () => previewImageSlots.filter((slot) => Boolean(getTemplateImageFromDTO(slot))).length,
    [previewImageSlots],
  );
  const previewSlot = useMemo(() => (
    previewImageSlots.find((slot) => (
      slot.slot_key === previewSlotKey && Boolean(getTemplateImageFromDTO(slot))
    )) ||
    previewImageSlots.find((slot) => Boolean(getTemplateImageFromDTO(slot))) ||
    previewImageSlots[0]
  ), [previewImageSlots, previewSlotKey]);
  const previewImageUrl = previewSlot ? getTemplateImageFromDTO(previewSlot) : cleanText(previewDetail?.cover_image_url);
  const previewSlotGridSlots = useMemo<ReferenceImageSlotGridSlot[]>(() => (
    previewSlots.map((slot) => {
      const imageUrl = getTemplateImageFromDTO(slot);
      const isUploadPlaceholder = Boolean(slot.is_upload_placeholder);
      const canManageSlot = ownedPreviewDetail;
      return {
        slotKey: slot.slot_key,
        slotName: slot.slot_name,
        imageUrl,
        active: Boolean(imageUrl) && previewSlot?.slot_key === slot.slot_key,
        isUploadPlaceholder,
        draggable: Boolean(imageUrl) && canManageSlot && !reorderingPreviewTemplate,
        droppable: !isUploadPlaceholder && canManageSlot,
        dragTarget: dragOverPreviewSlotKey === slot.slot_key,
        uploading: previewTemplateUploadingSlotKeys.has(slot.slot_key),
        canRemove: canManageSlot,
        canEdit: canManageSlot,
        canUpload: isUploadPlaceholder ? allowUserSlots && ownedPreviewDetail : canManageSlot,
        editLabel: "裁剪",
        resourceName: "模板图",
        itemData: {
          "data-coze-template-slot-key": slot.slot_key,
          "data-coze-template-user-module": slot.is_user_slot ? slot.slot_name : undefined,
          "data-coze-template-upload-placeholder": isUploadPlaceholder ? "true" : undefined,
        },
        removeActionData: { "data-coze-template-slot-delete": "true" },
        editActionData: { "data-coze-template-slot-crop": "true" },
      };
    })
  ), [
    allowUserSlots,
    dragOverPreviewSlotKey,
    ownedPreviewDetail,
    previewSlot?.slot_key,
    previewSlots,
    previewTemplateUploadingSlotKeys,
    reorderingPreviewTemplate,
  ]);
  const libraryView = resolveCozeTemplateLibraryView({
    hasPreviewDetail: Boolean(previewDetail),
    loadingTemplates,
    templateError,
  });
  const reverseCost = normalizeCozeReverseUnitCost(comp.config?.reverse_cost);
  const isPaidReverse = resolvePaidCozeReverseBehavior(reverseCost) === "explicit";
  const discountRate = myCosts?.tier?.discount_rate;
  const reverseBatchCounts = useMemo(
    () => getCozeTemplateReversePromptBatchCounts(reversePromptDrafts),
    [reversePromptDrafts],
  );
  const reverseEstimate = useMemo(() => calculateCozeReverseEstimate({
    standardUnitCost: reverseCost,
    eligibleCount: reverseBatchCounts.rerunnableCount,
    discountRate,
  }), [discountRate, reverseBatchCounts.rerunnableCount, reverseCost]);
  const singleReverseEstimate = useMemo(() => calculateCozeReverseEstimate({
    standardUnitCost: reverseCost,
    eligibleCount: 1,
    discountRate,
  }), [discountRate, reverseCost]);
  useEffect(() => {
    if (slots.length === 0) {
      if (activeSlotKey) setActiveSlotKey("");
      return;
    }
    const activeSlotStillEnabled = slots.some((slot) => (
      slot.slot_key === activeSlotKey &&
      selectableSlotNames.has(cleanText(slot.slot_name))
    ));
    if (!activeSlotStillEnabled) {
      setActiveSlotKey(
        slots.find((slot) => slot.selected && selectableSlotNames.has(cleanText(slot.slot_name)))?.slot_key ||
        slots.find((slot) => selectableSlotNames.has(cleanText(slot.slot_name)))?.slot_key ||
        slots[0].slot_key,
      );
    }
  }, [activeSlotKey, selectableSlotNames, slots]);

  const commitValue = useCallback((nextValue: CozeTemplateSetValue | null) => {
    const sortedValue = nextValue
      ? nextValue.template_set_id === COZE_TEMPLATE_REFERENCE_DRAFT_ID
        ? selectReferenceDraftSlot(nextValue)
        : normalizeSingleSelectedSlot(nextValue)
      : nextValue;
    latestValueRef.current = sortedValue;
    onChangeRef.current(sortedValue);
  }, []);

  useEffect(() => {
    const current = latestValueRef.current;
    if (!userModulesLoaded || !current?.template_set_id) return;
    if (current.template_set_id === COZE_TEMPLATE_REFERENCE_DRAFT_ID) return;
    const shouldProjectUserModules = shouldProjectCozeTemplateUserModules(
      current,
      mineTemplateIds.has(current.template_set_id),
    );
    if (!shouldProjectUserModules) return;
    // Stored template values use sequential display names (主图、副图1...).
    // Compare the account-module projection in that same normalized form;
    // comparing against the raw module names makes every parent render look
    // like a change and can recursively emit onChange.
    const mergedSlots = resolveUserModuleSlots(current.slots, userModules, true, true);
    const sameProjection = mergedSlots.length === current.slots.length && mergedSlots.every((slot, index) => {
      const previous = current.slots[index];
      return previous?.slot_key === slot.slot_key &&
        previous.slot_name === slot.slot_name &&
        previous.user_module_id === slot.user_module_id &&
        previous.template_user_slot_id === slot.template_user_slot_id &&
        getCozeTemplateSlotImage(previous) === getCozeTemplateSlotImage(slot);
    });
    if (!sameProjection) {
      commitValue({ ...current, slots: mergedSlots });
    }
  }, [commitValue, draftValue, mineTemplateIds, userModules, userModulesLoaded]);

  const emitValue = useCallback((updater: (current: CozeTemplateSetValue) => CozeTemplateSetValue) => {
    const current = latestValueRef.current;
    if (!current) return;
    const nextValue = updater(current);
    if (nextValue === current) return;
    commitValue(nextValue);
  }, [commitValue]);

  const getCurrentTemplateValue = useCallback((templateSetId?: number): CozeTemplateSetValue | null => {
    const current = latestValueRef.current;
    if (!current) return null;
    if (templateSetId && current.template_set_id !== templateSetId) return null;
    return current;
  }, []);

  const setReferencePickerSlotDraft = useCallback((slotKey: string, draft: ReferencePickerDraftImage | null) => {
    setReferencePickerDraftImages((current) => {
      const previous = current[slotKey];
      if (previous) revokeReferencePickerDraftImage(previous);

      if (!draft) {
        const rest = { ...current };
        delete rest[slotKey];
        return rest;
      }

      return {
        ...current,
        [slotKey]: draft,
      };
    });
  }, []);

  const clearReferencePickerDrafts = useCallback(() => {
    setReferencePickerDraftImages((current) => {
      Object.values(current).forEach(revokeReferencePickerDraftImage);
      return {};
    });
  }, []);

  const restoreReferencePickerSlot = useCallback((slotKey: string) => {
    setReferencePickerRemovedSlotKeys((current) => {
      if (!current.has(slotKey)) return current;
      const next = new Set(current);
      next.delete(slotKey);
      return next;
    });
  }, []);

  const removeReferencePickerSlot = useCallback((slotKey: string) => {
    if (uploadingCustomReference) return;
    const removedSlot = referencePickerSlots.find((slot) => slot.slot_key === slotKey);
    if (!removedSlot) return;
    pendingReferencePickerSlotKeyRef.current = null;
    setReferencePickerDraftImages((current) => {
      const removedDraft = current[slotKey];
      if (removedDraft) revokeReferencePickerDraftImage(removedDraft);
      const next = { ...current };
      delete next[slotKey];
      return next;
    });
    setReferencePickerRemovedSlotKeys((current) => {
      const next = new Set(current);
      next.add(slotKey);
      return next;
    });
    if (removedSlot.is_default_reference_slot) return;
    // Keep every slot that was rendered by the picker addressable after a
    // delete. Clearing the image is enough; removing trailing dynamic slots
    // here loses their physical positions when the draft is applied.
    setReferencePickerSessionSlots((current) => current.map((slot) => slot.slot_key === slotKey
      ? { ...slot, template_image: "", image_url: "" }
      : slot));
    setReferencePickerContext((current) => current ? {
      ...current,
      slots: current.slots.map((slot) => slot.slot_key === slotKey
        ? { ...slot, template_image: "", image_url: "" }
        : slot),
    } : current);
    setSelectedReferenceSlotKey((current) => current === slotKey
      ? referencePickerSlots.find((slot) => slot.slot_key !== slotKey && referencePickerSlotImages.get(slot.slot_key))?.slot_key || ""
      : current);
  }, [referencePickerSlotImages, referencePickerSlots, uploadingCustomReference]);

  const loadUserModules = useCallback(async () => {
    if (!allowUserSlots || loadingUserModules) return;
    setLoadingUserModules(true);
    await ecomImageApi.getCozeTemplateUserModules()
      .then((res) => {
        const items = res.data?.items || [];
        syncUserModules(items.filter((module) => module.status !== "deleted" && module.status !== "inactive"));
        setUserModulesLoaded(true);
      })
      .catch(() => {
        syncUserModules([]);
        setUserModulesLoaded(false);
      })
      .finally(() => {
        setLoadingUserModules(false);
      });
  }, [allowUserSlots, loadingUserModules, syncUserModules]);

  useEffect(() => {
    if (!allowUserSlots || userModulesRequestedRef.current) return;
    userModulesRequestedRef.current = true;
    void loadUserModules();
  }, [allowUserSlots, loadUserModules]);

  useEffect(() => {
    const current = latestValueRef.current;
    if (!current) return;
    const nextUploaded = syncUploadedImagesWithSources(current.uploaded_images, sourceImages, productDefinitionOptions);
    const same =
      nextUploaded.length === current.uploaded_images.length &&
      nextUploaded.every((image, index) => {
        const prev = current.uploaded_images[index];
        return prev?.url === image.url && prev.definition === image.definition && prev.preview_url === image.preview_url;
      });
    if (!same) {
      commitValue({ ...current, uploaded_images: nextUploaded });
    }
  }, [commitValue, draftValue, productDefinitionOptions, sourceImages]);

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    setTemplatesLoaded(false);
    setTemplateError("");
    setLibraryNotice("");
    try {
      const res = await ecomImageApi.getCozeTemplateSets({ page_size: 100 });
      const items = res.data?.items || [];
      setTemplateSets(items);
    } catch {
      setTemplateSets([]);
      setLibraryNotice("模板库暂不可用，请稍后重试");
    } finally {
      setTemplatesLoaded(true);
      setLoadingTemplates(false);
    }
  }, []);

  const loadMineTemplates = useCallback(async () => {
    setLoadingMineTemplates(true);
    setMineTemplateError("");
    try {
      const res = await ecomImageApi.getCozeTemplateSets({ mine: true, page_size: 100 });
      const items = res.data?.items || [];
      setMineTemplateSets(items);
      return items;
    } catch {
      setMineTemplateError("我的模版加载失败，请稍后重试");
      return [];
    } finally {
      setMineTemplatesLoaded(true);
      setLoadingMineTemplates(false);
    }
  }, []);

  useEffect(() => {
    if (!libraryOpen || templatesLoaded || loadingTemplates) return;
    loadTemplates();
  }, [libraryOpen, loadTemplates, loadingTemplates, templatesLoaded]);

  useEffect(() => {
    if (!libraryOpen || mineTemplatesLoaded || loadingMineTemplates) return;
    loadMineTemplates();
  }, [libraryOpen, loadMineTemplates, loadingMineTemplates, mineTemplatesLoaded]);

  const openTemplateLibrary = useCallback(() => {
    resetPreviewTemplateDraft();
    setPreviewDetail(null);
    setTemplateError("");
    setLibraryCategory("");
    if (!userModulesLoaded && !loadingUserModules) {
      void loadUserModules();
    }
    setLibraryOpen(true);
  }, [loadUserModules, loadingUserModules, resetPreviewTemplateDraft, userModulesLoaded]);

  const resumePendingReversePromptAction = useCallback(async (
    pending: CozeReversePromptPendingAction,
    initialDrafts: CozeTemplateReversePromptDraftItem[],
    postRequest?: Promise<{ data?: { prompts?: CozeTemplateReversePromptResult[] } }>,
    operationClaim?: ReversePromptOperationClaim,
  ) => {
    const operation = operationClaim || claimReversePromptOperation(
      reversePromptHistoryScope,
      `coze:${workflowKey}:${comp.coze_param_key}`,
      pending.action_id,
      { pending },
    );
    if (!operation) return;
    activeReverseActionsRef.current.set(pending.action_id, pending);
    setPendingReverseActions((current) => ({ ...current, [pending.action_id]: pending }));
    let latestDrafts = initialDrafts;
    const taskIDsBySlot = { ...(pending.task_ids_by_slot || {}) };
    const outcome = await runCozeTemplateReversePromptAction({
      requestedSlotKeys: pending.requested_slot_keys,
      startedAt: pending.started_at,
      postRequest,
      getProgress: () => ecomImageApi.getCozeTemplateReversePromptProgressForSet(
        pending.template_set_id,
        pending.action_id,
      ),
      onResults: (results) => {
        if (!isReversePromptOperationCurrent(operation)) return;
        const currentValue = latestValueRef.current;
        const currentDrafts = currentValue
          ? reconcileCozeTemplateReversePromptDrafts(currentValue, reversePromptDraftsRef.current)
          : reversePromptDraftsRef.current;
        const currentDraftsBySlot = new Map(currentDrafts.map((draft) => [draft.slot_key, draft]));
        const requestedDraftsBySlot = new Map(pending.drafts.map((draft) => [draft.slot_key, draft]));
        const ownedResults = results.filter((result) => {
          const currentDraft = currentDraftsBySlot.get(result.slot_key);
          const requestedDraft = requestedDraftsBySlot.get(result.slot_key);
          if (!currentDraft || !requestedDraft || !isSameReversePromptDraftInput(currentDraft, requestedDraft)) {
            return false;
          }
          const taskID = cleanText(result.task_id);
          const expectedTaskID = taskIDsBySlot[result.slot_key];
          if (taskID && expectedTaskID && taskID !== expectedTaskID) return false;
          if (taskID && !expectedTaskID) taskIDsBySlot[result.slot_key] = taskID;
          return true;
        });
        latestDrafts = currentDrafts;
        latestDrafts = mergeReversePromptDraftResults(
          latestDrafts,
          pending.requested_slot_keys,
          ownedResults,
          editedReversePromptSlotKeysRef.current,
        );
        reversePromptDraftsRef.current = latestDrafts;
        setReversePromptDrafts(latestDrafts);
        saveCozeReversePromptPendingAction(
          { ...pending, task_ids_by_slot: taskIDsBySlot, drafts: latestDrafts },
          reversePromptHistoryScope,
          workflowKey,
          comp.coze_param_key,
        );
      },
      shouldContinue: () => (
        isReversePromptOperationCurrent(operation) &&
        activeReverseActionsRef.current.has(pending.action_id)
      ),
      maxAgeMs: COZE_REVERSE_PENDING_ACTION_MAX_AGE,
      isDefinitivePostError: isDefinitiveCozeReversePromptPostError,
    });

    if (
      outcome.status === "cancelled" ||
      !activeReverseActionsRef.current.has(pending.action_id) ||
      !isReversePromptOperationCurrent(operation)
    ) return;
    activeReverseActionsRef.current.delete(pending.action_id);
    setPendingReverseActions((current) => {
      const next = { ...current };
      delete next[pending.action_id];
      return next;
    });
    if (outcome.status === "complete") {
      clearCozeReversePromptPendingAction(
        reversePromptHistoryScope,
        workflowKey,
        comp.coze_param_key,
        pending.action_id,
      );
      saveCozeReversePromptHistory(
        latestDrafts,
        reversePromptHistoryScope,
        workflowKey,
        comp.coze_param_key,
      );
      updateReversePromptOperation(operation, "completed", { drafts: latestDrafts });
      setReverseModalStage(activeReverseActionsRef.current.size > 0
        ? "generating"
        : latestDrafts.some((item) => item.status === "idle") ? "preview" : "result");
      return;
    }

    const requestedKeys = new Set(pending.requested_slot_keys);
    latestDrafts = latestDrafts.map((item) => requestedKeys.has(item.slot_key) && item.status === "generating"
      ? {
          ...item,
          status: "failed",
          errorText: getCozeReversePublicErrorText(outcome.status === "failed" ? outcome.error : "E5302"),
        }
      : item);
    clearCozeReversePromptPendingAction(
      reversePromptHistoryScope,
      workflowKey,
      comp.coze_param_key,
      pending.action_id,
    );
    saveCozeReversePromptHistory(
      latestDrafts,
      reversePromptHistoryScope,
      workflowKey,
      comp.coze_param_key,
    );
    updateReversePromptOperation(operation, "failed", { drafts: latestDrafts, errorCode: "E5302" });
    setReversePromptDrafts(latestDrafts);
    reversePromptDraftsRef.current = latestDrafts;
    setReverseModalStage(activeReverseActionsRef.current.size > 0
      ? "generating"
      : latestDrafts.some((item) => item.status === "idle") ? "preview" : "result");
  }, [comp.coze_param_key, reversePromptHistoryScope, workflowKey]);

  useEffect(() => {
    if (!reversePromptHistoryScope) return;
    const currentValue = latestValueRef.current;
    if (!currentValue?.template_set_id) return;
    const pendingActions = loadCozeReversePromptPendingActions(
      reversePromptHistoryScope,
      workflowKey,
      comp.coze_param_key,
    );
    const resumable = pendingActions.flatMap((pending) => {
      if (activeReverseActionsRef.current.has(pending.action_id)) return [];
      const restored = restorePendingReversePromptDrafts(currentValue, pending);
      if (!restored) {
        clearCozeReversePromptPendingAction(reversePromptHistoryScope, workflowKey, comp.coze_param_key, pending.action_id);
        return [];
      }
      return [{ pending, restored }];
    });
    if (resumable.length === 0) return;
    const pendingSlotKeys = new Set(resumable.flatMap((item) => item.pending.requested_slot_keys));
    const restoredDrafts = reconcileCozeTemplateReversePromptDrafts(
      currentValue,
      reversePromptDraftsRef.current.length > 0
        ? reversePromptDraftsRef.current
        : loadCozeReversePromptHistory(reversePromptHistoryScope, workflowKey, comp.coze_param_key),
    ).map((draft) => pendingSlotKeys.has(draft.slot_key)
      ? { ...draft, prompt: "", status: "generating" as const, errorText: undefined }
      : draft);
    reversePromptDraftsRef.current = restoredDrafts;
    setReversePromptDrafts(restoredDrafts);
    setReverseModalStage("generating");
    resumable.forEach(({ pending }) => void resumePendingReversePromptAction(pending, restoredDrafts));
  }, [
    comp.coze_param_key,
    draftValue,
    resumePendingReversePromptAction,
    reversePromptHistoryScope,
    workflowKey,
  ]);

  const openReversePromptModal = useCallback(() => {
    if (reverseModalStage === "generating") {
      const currentValue = latestValueRef.current;
      if (currentValue) {
        const nextDrafts = reconcileCozeTemplateReversePromptDrafts(currentValue, reversePromptDrafts);
        setReversePromptDrafts(nextDrafts);
      }
      setReverseModalOpen(true);
      return;
    }
    const currentValue = latestValueRef.current;
    if (!currentValue?.template_set_id) return;
    const hasUploadingImage = currentValue.uploaded_images.some((image) => isBlobUrl(image.url));
    if (hasUploadingImage) {
      feedback.warning("素材图片上传中，请稍后再反推");
      return;
    }
    const historyDrafts = reversePromptDrafts.length > 0
      ? reversePromptDrafts
      : loadCozeReversePromptHistory(
          reversePromptHistoryScope,
          workflowKey,
          comp.coze_param_key,
        );
    const nextDrafts = reconcileCozeTemplateReversePromptDrafts(currentValue, historyDrafts);
    if (nextDrafts.length === 0) {
      feedback.warning("当前模版暂无可反推图片");
      return;
    }
    setReversePromptDrafts(nextDrafts);
    setReverseModalStage(nextDrafts.some((item) => item.status !== "success") ? "preview" : "result");
    setReverseModalOpen(true);
  }, [
    comp.coze_param_key,
    reverseModalStage,
    reversePromptDrafts,
    reversePromptHistoryScope,
    workflowKey,
  ]);

  const closeReversePromptModal = useCallback(() => {
    setReverseModalOpen(false);
  }, []);

  const handleReversePromptVariantChange = useCallback((variant: CozeTemplatePromptVariant) => {
    if (reverseModalStage === "generating") return;
    const currentValue = latestValueRef.current;
    if (!currentValue?.template_set_id || currentValue.variant === variant) return;

    const nextValue = { ...currentValue, variant };
    commitValue(nextValue);
    saveCozeReversePromptHistory(
      reversePromptDrafts,
      reversePromptHistoryScope,
      workflowKey,
      comp.coze_param_key,
    );
    const historyDrafts = loadCozeReversePromptHistory(
      reversePromptHistoryScope,
      workflowKey,
      comp.coze_param_key,
    );
    const nextDrafts = reconcileCozeTemplateReversePromptDrafts(nextValue, historyDrafts);
    setReversePromptDrafts(nextDrafts);
    setReverseModalStage(nextDrafts.some((item) => item.status !== "success") ? "preview" : "result");
    reverseModalScrollRef.current?.scrollTo({ top: 0 });
  }, [
    commitValue,
    comp.coze_param_key,
    reverseModalStage,
    reversePromptDrafts,
    reversePromptHistoryScope,
    workflowKey,
  ]);

  const runReversePromptDraft = useCallback(async (targetSlotKeys?: string[]) => {
    const currentValue = latestValueRef.current;
    if (!currentValue?.template_set_id) return;
    const variant = currentValue.variant;
    const templateSetId = currentValue.template_set_id;
    const requestedSlotKeys = (targetSlotKeys || reversePromptDraftsRef.current
      .filter((item) => item.status !== "success")
      .map((item) => item.slot_key))
      .filter((slotKey) => {
        const draft = reversePromptDraftsRef.current.find((item) => item.slot_key === slotKey);
        return draft?.status !== "generating" && !pendingReverseSlotKeys.has(slotKey);
      });
    const targetKeySet = new Set(requestedSlotKeys);
    const targetSlots = currentValue.slots.filter((slot) => targetKeySet.has(slot.slot_key));
    const slotRequests = getCozeTemplateReverseRequests(currentValue, targetSlots, true);
    if (slotRequests.length === 0) return;

    const generatingKeys = new Set(slotRequests.map((slot) => slot.slot_key));
    generatingKeys.forEach((slotKey) => editedReversePromptSlotKeysRef.current.delete(slotKey));
    const pendingDrafts = reversePromptDraftsRef.current.map((item) => generatingKeys.has(item.slot_key)
      ? { ...item, prompt: "", status: "generating" as const, errorText: undefined }
      : item);
    const actionID = createClientUUID();
    const pendingAction: CozeReversePromptPendingAction = {
      action_id: actionID,
      template_set_id: templateSetId,
      variant,
      requested_slot_keys: Array.from(generatingKeys),
      drafts: pendingDrafts,
      started_at: Date.now(),
    };
    const operation = claimReversePromptOperation(
      reversePromptHistoryScope,
      `coze:${workflowKey}:${comp.coze_param_key}`,
      pendingAction.action_id,
      { pending: pendingAction },
    );
    if (!operation) return;
    activeReverseActionsRef.current.set(actionID, pendingAction);
    setPendingReverseActions((current) => ({ ...current, [actionID]: pendingAction }));
    setReversePromptDrafts(pendingDrafts);
    reversePromptDraftsRef.current = pendingDrafts;
    setReverseModalStage("generating");
    saveCozeReversePromptPendingAction(
      pendingAction,
      reversePromptHistoryScope,
      workflowKey,
      comp.coze_param_key,
    );

    const requestKey = JSON.stringify([templateSetId, workflowKey, variant, slotRequests]);
    const requestPromise = runCozeTemplateReverseRequestOnce(requestKey, () => (
      ecomImageApi.reverseCozeTemplateReversePrompts(templateSetId, {
        action_id: actionID,
        async: true,
        confirmed_standard_unit_cost: isPaidReverse ? reverseCost : 0,
        workflow_key: workflowKey,
        variant,
        slots: slotRequests,
      })
    ));
    updateReversePromptOperation(operation, "polling", { pending: pendingAction });
    await resumePendingReversePromptAction(pendingAction, pendingDrafts, requestPromise, operation);
  }, [
    comp.coze_param_key,
    isPaidReverse,
    reverseCost,
    reversePromptHistoryScope,
    resumePendingReversePromptAction,
    pendingReverseSlotKeys,
    workflowKey,
  ]);

  const updateReversePromptDraft = useCallback((slotKey: string, prompt: string) => {
    editedReversePromptSlotKeysRef.current.add(slotKey);
    reversePromptDraftsRef.current = reversePromptDraftsRef.current.map((item) => (
      item.slot_key === slotKey && item.status === "success"
        ? { ...item, prompt }
        : item
    ));
    setReversePromptDrafts((current) => current.map((item) => (
      item.slot_key === slotKey && item.status === "success"
        ? { ...item, prompt }
        : item
    )));
  }, []);

  const applyReversePromptDrafts = useCallback(() => {
    if (reverseModalStage === "generating") return;
    const currentValue = latestValueRef.current;
    if (!currentValue || !reversePromptDrafts.some((item) => item.status === "success" && cleanText(item.prompt))) return;
    commitValue(applyCozeTemplateReverseDraftResults(currentValue, reversePromptDrafts));
    clearCozeReversePromptHistory(
      reversePromptHistoryScope,
      workflowKey,
      comp.coze_param_key,
      currentValue.variant,
    );
    setReverseModalOpen(false);
    setReverseModalStage("closed");
    setReversePromptDrafts([]);
    editedReversePromptSlotKeysRef.current.clear();
  }, [
    commitValue,
    comp.coze_param_key,
    reversePromptDrafts,
    reversePromptHistoryScope,
    reverseModalStage,
    workflowKey,
  ]);

  const handleApplyTemplate = useCallback(async (
    templateSetId: number,
    preparedDetail?: CozeTemplateSetDetail,
  ) => {
    if (applyingTemplateIdRef.current !== null) return;
    if (
      !preparedDetail &&
      previewDetail?.id === templateSetId &&
      (previewTemplateDirty || uploadingPreviewTemplate || savingPreviewTemplate)
    ) {
      feedback.warning("请先保存模板修改");
      return;
    }
    applyingTemplateIdRef.current = templateSetId;
    setApplyingId(templateSetId);
    try {
      const detail = preparedDetail || (await ecomImageApi.getCozeTemplateSet(templateSetId)).data;
      if (!detail) throw new Error("empty_template");
      const currentValue = latestValueRef.current;
      const isSwitchingTemplate = (
        typeof currentValue?.template_set_id === "number" &&
        currentValue.template_set_id !== templateSetId
      );
      const valueForMerge: CozeTemplateSetValue | undefined = isSwitchingTemplate && currentValue
        ? { ...currentValue, uploaded_images: [] }
        : currentValue ?? undefined;
      const nextValue = mergeTemplateDetailToValue(
        detail,
        valueForMerge,
        userModules,
        userModulesLoaded,
        mineTemplateIds.has(templateSetId),
      );
      const syncedUploadedImages = isSwitchingTemplate
        ? []
        : syncUploadedImagesWithSources(nextValue.uploaded_images, sourceImages, getCozeTemplateProductImageDefinitionOptions(nextValue.slots));
      if (isSwitchingTemplate && currentValue) {
        currentValue.uploaded_images.forEach((image) => {
          revokeBlobUrl(image.url);
          revokeBlobUrl(image.preview_url);
        });
      }
      const syncedValue = {
        ...nextValue,
        uploaded_images: syncedUploadedImages,
        slots: isSwitchingTemplate
          ? nextValue.slots.map((slot) => invalidateCozeTemplateSlotPrompt(slot, nextValue.variant))
          : nextValue.slots,
      };
      const appliedSlotKey = syncedValue.slots.find((slot) => (
        previewDetail?.id === templateSetId &&
        slot.slot_key === previewSlotKey &&
        Boolean(getCozeTemplateSlotImage(slot))
      ))?.slot_key ||
        syncedValue.slots.find((slot) => getCozeTemplateSlotImage(slot))?.slot_key ||
        syncedValue.slots.find((slot) => slot.selected)?.slot_key ||
        syncedValue.slots[0]?.slot_key ||
        "";
      const activeSyncedValue = normalizeSingleSelectedSlot(syncedValue, appliedSlotKey);
      commitValue(activeSyncedValue);
      setActiveSlotKey(appliedSlotKey);
      setLibraryOpen(false);
    } catch {
      feedback.warning("模板应用失败，请稍后重试");
    } finally {
      applyingTemplateIdRef.current = null;
      setApplyingId(null);
    }
  }, [
    commitValue,
    mineTemplateIds,
    previewDetail?.id,
    previewSlotKey,
    previewTemplateDirty,
    savingPreviewTemplate,
    sourceImages,
    uploadingPreviewTemplate,
    userModules,
    userModulesLoaded,
  ]);

  const openTemplateDetail = useCallback(async (
    templateSetId: number,
    preferredSlotKey = "",
    ownedTemplate = mineTemplateIds.has(templateSetId),
  ) => {
    const requestId = ++templateDetailRequestRef.current;
    setLoadingDetailId(templateSetId);
    setTemplateError("");
    try {
      const detail = (await ecomImageApi.getCozeTemplateSet(templateSetId)).data;
      if (requestId !== templateDetailRequestRef.current) return;
      if (!detail) throw new Error("empty_template");
      setPreviewSlotKey(getFirstPreviewSlotKey(
        detail,
        userModules,
        userModulesLoaded,
        ownedTemplate,
        preferredSlotKey,
      ));
      if (ownedTemplate) {
        initializePreviewTemplateDraft(detail);
      } else {
        resetPreviewTemplateDraft();
      }
      setPreviewDetail(detail);
    } catch {
      if (requestId !== templateDetailRequestRef.current) return;
      setLibraryNotice("模板详情暂不可用，请稍后重试");
      feedback.warning("模板详情暂不可用，请稍后重试");
    } finally {
      if (requestId === templateDetailRequestRef.current) setLoadingDetailId(null);
    }
  }, [feedback, initializePreviewTemplateDraft, mineTemplateIds, resetPreviewTemplateDraft, userModules, userModulesLoaded]);

  const navigablePreviewSlots = useMemo(
    () => previewImageSlots.filter((slot) => Boolean(getTemplateImageFromDTO(slot))),
    [previewImageSlots],
  );
  const previewNavigationIndex = navigablePreviewSlots.findIndex((slot) => slot.slot_key === previewSlot?.slot_key);
  const previewTemplateIndex = filteredTemplateSets.findIndex((template) => template.id === previewDetail?.id);
  const previewNavigationBlocked = loadingDetailId !== null || applyingId !== null || uploadingSlotKey !== null ||
    uploadingPreviewTemplate || savingPreviewTemplate || reorderingPreviewTemplate;

  const navigatePreviewImage = useCallback((direction: -1 | 1) => {
    if (previewNavigationBlocked || templateNavigationPendingRef.current) return;
    setPreviewSlotKey((currentKey) => {
      const currentIndex = navigablePreviewSlots.findIndex((slot) => slot.slot_key === currentKey);
      const nextSlot = navigablePreviewSlots[(currentIndex < 0 ? 0 : currentIndex) + direction];
      return nextSlot?.slot_key || currentKey;
    });
  }, [navigablePreviewSlots, previewNavigationBlocked]);

  const navigatePreviewTemplate = useCallback(async (direction: -1 | 1) => {
    if (previewNavigationBlocked || templateNavigationPendingRef.current || previewTemplateIndex < 0) return;
    const nextTemplate = filteredTemplateSets[previewTemplateIndex + direction];
    if (!nextTemplate) return;
    if (previewTemplateDirty) {
      feedback.warning("请先保存模板修改，再切换套装");
      return;
    }
    templateNavigationPendingRef.current = true;
    try {
      await openTemplateDetail(nextTemplate.id);
    } finally {
      templateNavigationPendingRef.current = false;
    }
  }, [feedback, filteredTemplateSets, openTemplateDetail, previewNavigationBlocked, previewTemplateDirty, previewTemplateIndex]);

  useEffect(() => {
    if (!libraryOpen || !previewDetail || referencePickerOpen || croppingPreviewTemplateSlot ||
      croppingReferenceSlot || croppingProductImage || pendingDeleteTemplate || reverseModalOpen) return;
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const target = event.target;
      const targetDialog = target instanceof HTMLElement ? target.closest('[role="dialog"], [role="alertdialog"]') : null;
      if (target instanceof HTMLElement && (
        target.isContentEditable ||
        target.closest('input, textarea, select, [role="textbox"], [role="combobox"], [role="slider"], [aria-roledescription="sortable"]') ||
        (targetDialog && !targetDialog.contains(templateDetailRef.current))
      )) return;
      if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
      event.preventDefault();
      if (event.key === "ArrowLeft") navigatePreviewImage(-1);
      else if (event.key === "ArrowRight") navigatePreviewImage(1);
      else if (!event.repeat) void navigatePreviewTemplate(event.key === "ArrowUp" ? -1 : 1);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [croppingPreviewTemplateSlot, croppingProductImage, croppingReferenceSlot, libraryOpen, navigatePreviewImage,
    navigatePreviewTemplate, pendingDeleteTemplate, previewDetail, referencePickerOpen, reverseModalOpen]);

  useEffect(() => {
    if (!libraryOpen || !previewDetail) return;
    const activeThumbnail = Array.from(templateDetailRef.current?.querySelectorAll<HTMLElement>("[data-coze-template-slot-key]") || [])
      .find((element) => element.dataset.cozeTemplateSlotKey === previewSlot?.slot_key);
    activeThumbnail?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [libraryOpen, previewDetail, previewSlot?.slot_key]);

  const cancelTemplateDetailRequest = () => {
    templateDetailRequestRef.current += 1;
    setLoadingDetailId(null);
  };

  useEffect(() => () => { templateDetailRequestRef.current += 1; }, []);

  const selectSlot = (slot: CozeTemplateSetSlot) => {
    if (!getCozeTemplateSlotImage(slot)) return;
    setActiveSlotKey(slot.slot_key);
    emitValue((current) => {
      const currentSlots = current.template_set_id === COZE_TEMPLATE_REFERENCE_DRAFT_ID
        ? current.slots
        : resolveUserModuleSlots(
          current.slots,
          userModules,
          includeUserModulesForCurrent ? userModulesLoaded : true,
          includeUserModulesForCurrent,
        );
      return {
        ...current,
        slots: selectSingleCozeTemplateSlot(
          currentSlots,
          slot.slot_key,
        ),
      };
    });
  };

  const handleSlotCardKeyDown = (event: KeyboardEvent<HTMLDivElement>, slot: CozeTemplateSetSlot) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    selectSlot(slot);
  };

  const showSlotImagePreview = (imageUrl: string, alt: string) => {
    if (!imageUrl) return;
    onHoverPreviewChange?.({
      url: imageUrl,
      alt,
    });
  };

  const hideSlotImagePreview = () => {
    onHoverPreviewChange?.(null);
  };

  const handlePromptChange = (slotKey: string, prompt: string) => {
    emitValue((current) => ({
      ...current,
      slots: current.slots.map((slot) => slot.slot_key === slotKey
        ? slotWithPrompt(slot, current.variant, prompt, prompt.trim() ? "success" : "idle")
        : slot),
    }));
  };

  const handleProductImageRefInsert = useCallback((index: number) => {
    const current = latestValueRef.current;
    if (!current) return;
    const targetSlot = current.slots.find((slot) => slot.slot_key === activeSlotKey) ||
      current.slots.find((slot) => slot.slot_key === activeSlot?.slot_key);
    if (!targetSlot) return;

    const rawPrompt = targetSlot.prompt_by_variant?.[current.variant];
    const prompt = typeof rawPrompt === "string" ? rawPrompt : "";
    const textarea = promptTextareaRef.current;
    const selectionStart = Math.min(textarea?.selectionStart ?? prompt.length, prompt.length);
    const selectionEnd = Math.min(textarea?.selectionEnd ?? selectionStart, prompt.length);
    const result = insertPromptImageReferenceAtCaret(prompt, selectionStart, index + 2, selectionEnd);

    emitValue((latest) => ({
      ...latest,
      slots: latest.slots.map((slot) => slot.slot_key === targetSlot.slot_key
        ? slotWithPrompt(slot, latest.variant, result.value, result.value.trim() ? "success" : "idle")
        : slot),
    }));
    window.requestAnimationFrame(() => {
      const currentTextarea = promptTextareaRef.current;
      if (!currentTextarea) return;
      currentTextarea.focus();
      currentTextarea.setSelectionRange(result.caret, result.caret);
    });
  }, [activeSlot?.slot_key, activeSlotKey, emitValue]);

  const handleProductImageReorder = useCallback((fromIndex: number, toIndex: number) => {
    const slotName = cleanText(activeSlot?.slot_name);
    if (!imageReferenceBindingEnabled || !slotName || fromIndex === toIndex) return;

    onHoverPreviewChange?.(null);
    emitValue((current) => {
      const scopedImages = current.uploaded_images.filter(
        (image) => cleanText(image.definition) === slotName,
      );
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= scopedImages.length ||
        toIndex >= scopedImages.length
      ) {
        return current;
      }

      const reorderedImages = [...scopedImages];
      const [movedImage] = reorderedImages.splice(fromIndex, 1);
      reorderedImages.splice(toIndex, 0, movedImage);
      let scopedIndex = 0;

      return {
        ...current,
        uploaded_images: current.uploaded_images.map((image) => (
          cleanText(image.definition) === slotName
            ? reorderedImages[scopedIndex++]
            : image
        )),
        slots: current.slots.map((slot) => (
          cleanText(slot.slot_name) === slotName
            ? rewriteCozeSlotPromptImageReferences(
              slot,
              (prompt) => rewritePromptImageReferencesForMove(
                prompt,
                fromIndex + 1,
                toIndex + 1,
                scopedImages.length + 1,
              ),
            )
            : slot
        )),
      };
    });
  }, [activeSlot?.slot_name, emitValue, imageReferenceBindingEnabled, onHoverPreviewChange]);

  const handleRemoveUploadedImage = (imageId: string) => {
    onHoverPreviewChange?.(null);
    emitValue((current) => {
      const removed = current.uploaded_images.find((image) => image.id === imageId);
      revokeBlobUrl(removed?.url);
      revokeBlobUrl(removed?.preview_url);
      const removedDefinition = cleanText(removed?.definition);
      const scopedImages = current.uploaded_images.filter(
        (image) => cleanText(image.definition) === removedDefinition,
      );
      const removedIndex = scopedImages.findIndex((image) => image.id === imageId);
      return {
        ...current,
        uploaded_images: current.uploaded_images.filter((image) => image.id !== imageId),
        slots: current.slots.map((slot) => removedDefinition === cleanText(slot.slot_name)
          ? imageReferenceBindingEnabled && removedIndex >= 0
            ? rewriteCozeSlotPromptImageReferences(
              slot,
              (prompt) => rewritePromptImageReferencesForRemoval(
                prompt,
                removedIndex + 1,
                scopedImages.length + 1,
              ),
            )
            : invalidateCozeTemplateSlotPrompt(slot, current.variant)
          : slot),
      };
    });
  };

  const handleProductFiles = useCallback(async (fileList: FileList | File[]) => {
    const selectedFiles = getImageFilesFromFileList(fileList);
    const current = latestValueRef.current;
    const currentSlotName = current?.slots.find((slot) => slot.slot_key === activeSlotKey)?.slot_name || activeSlot?.slot_name || "";
    if (!current || selectedFiles.length === 0 || !currentSlotName) {
      feedback.warning("请先选择一个有模板图的模块");
      return;
    }
    const remaining = maxProductImages - getCozeTemplateSlotUploadedImages(current, currentSlotName).length;
    if (remaining <= 0) {
      feedback.warning(`每个模块最多上传 ${maxProductImages} 张素材图片`);
      return;
    }
    const files = selectedFiles.slice(0, remaining);
    if (files.length < selectedFiles.length) {
      feedback.warning(`每个模块最多上传 ${maxProductImages} 张素材图片`);
    }

    const placeholders: CozeTemplateUploadedImage[] = files.map((file, index) => {
      const localUrl = URL.createObjectURL(file);
      return {
        id: `${LOCAL_PRODUCT_IMAGE_SOURCE_KEY}-${Date.now()}-${index}-${file.name}`,
        url: localUrl,
        preview_url: localUrl,
        source_param_key: LOCAL_PRODUCT_IMAGE_SOURCE_KEY,
        source_label: `上传产品图 ${current.uploaded_images.length + index + 1}`,
        definition: currentSlotName,
      };
    });

    // 新增图片追加到 uploaded_images 末尾，已有图片的 @图片N 序号保持不变，
    // 因此无需作废用户已写的提示词。单张上传失败时在下方 catch 中回滚该
    // placeholder 即可，不会影响 slot 的 prompt 状态。
    commitValue({
      ...current,
      uploaded_images: [...current.uploaded_images, ...placeholders],
    });
    setUploadingProductImages(true);
    notifyUploading?.(1);

    try {
      const results = await Promise.all(placeholders.map(async (placeholder, index) => {
        try {
          const res = await ecomImageApi.uploadProductImage(files[index], { maxBytes: MAX_REVERSE_PROMPT_BYTES });
          const url = getUploadedUrl(res.data);
          if (!url) throw new Error("upload_failed");
          const previewUrl = getUploadedPreviewUrl(res.data) || url;
          const currentAfterUpload = latestValueRef.current;
          if (!currentAfterUpload) return;
          commitValue({
            ...currentAfterUpload,
            uploaded_images: currentAfterUpload.uploaded_images.map((image) => image.id === placeholder.id
              ? {
                  ...image,
                  url,
                  preview_url: previewUrl,
                  source_label: image.source_label || `上传产品图 ${index + 1}`,
                }
              : image),
          });
          revokeBlobUrl(placeholder.url);
          revokeBlobUrl(placeholder.preview_url);
          return true;
        } catch {
          const currentAfterError = latestValueRef.current;
          if (currentAfterError) {
            commitValue({
              ...currentAfterError,
              uploaded_images: currentAfterError.uploaded_images.filter((image) => image.id !== placeholder.id),
            });
          }
          revokeBlobUrl(placeholder.url);
          revokeBlobUrl(placeholder.preview_url);
          return false;
        }
      }));
      if (results.some((ok) => !ok)) {
        feedback.warning("部分产品图片上传失败，请重试");
      }
    } catch {
      feedback.warning("部分产品图片上传失败，请重试");
    } finally {
      setUploadingProductImages(false);
      notifyUploading?.(-1);
    }
  }, [activeSlot?.slot_name, activeSlotKey, commitValue, maxProductImages, notifyUploading]);

  const handleProductImagePreviewEnter = useCallback((image: CozeTemplateUploadedImage, index: number) => {
    const previewUrl = cleanText(image.preview_url) || cleanText(image.url);
    if (!previewUrl) return;
    onHoverPreviewChange?.({
      url: previewUrl,
      alt: image.source_label || `上传产品图 ${index + 1}`,
    });
  }, [onHoverPreviewChange]);

  const handleProductImagePreviewLeave = useCallback(() => {
    onHoverPreviewChange?.(null);
  }, [onHoverPreviewChange]);

  const handleProductCropSave = useCallback(async (croppedDataUrl: string) => {
    const targetImage = croppingProductImage;
    setCroppingProductImage(null);
    if (!targetImage || !latestValueRef.current?.uploaded_images.some((image) => image.id === targetImage.id)) {
      return;
    }

    setUploadingProductImages(true);
    notifyUploading?.(1);
    try {
      const blob = await fetch(croppedDataUrl).then((response) => response.blob());
      const file = new File(
        [blob],
        `cropped-product-${Date.now()}.png`,
        { type: "image/png" },
      );
      const res = await ecomImageApi.uploadProductImage(file, { maxBytes: MAX_REVERSE_PROMPT_BYTES });
      const url = getUploadedUrl(res.data);
      if (!url) throw new Error("upload_failed");
      const previewUrl = getUploadedPreviewUrl(res.data) || url;
      const current = latestValueRef.current;
      if (!current) return;
      const targetDefinition = cleanText(targetImage.definition);
      commitValue({
        ...current,
        uploaded_images: current.uploaded_images.map((image) => image.id === targetImage.id
          ? { ...image, url, preview_url: previewUrl }
          : image),
        slots: current.slots.map((slot) => targetDefinition && slot.slot_name === targetDefinition
          ? invalidateCozeTemplateSlotPrompt(slot, current.variant)
          : slot),
      });
    } catch {
      feedback.warning("裁剪图片上传失败，请稍后重试");
    } finally {
      setUploadingProductImages(false);
      notifyUploading?.(-1);
    }
  }, [commitValue, croppingProductImage, notifyUploading]);

  const handleProductFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = getImageFilesFromFileList(event.target.files || []);
    event.target.value = "";
    if (files.length) {
      void handleProductFiles(files);
    }
  };

  const handleProductDrop = async (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setProductDragOver(false);
    const files = await getImageFilesFromDataTransfer(event.dataTransfer);
    if (files.length > 0) {
      void handleProductFiles(files);
    }
  };

  const openReferencePicker = (
    templateDetail?: CozeTemplateSetDetail | null,
    preferredSlotName?: string,
  ) => {
    const currentValue = latestValueRef.current;
    const includeTemplateUserModules = templateDetail
      ? mineTemplateIds.has(templateDetail.id)
      : shouldProjectCozeTemplateUserModules(
        currentValue,
        currentValue?.template_set_id !== undefined && mineTemplateIds.has(currentValue.template_set_id),
      );
    let contextValue = templateDetail
      ? mergeTemplateDetailToValue(templateDetail, currentValue, userModules, userModulesLoaded, includeTemplateUserModules)
      : currentValue
        ? {
            ...currentValue,
            slots: currentValue.template_set_id === COZE_TEMPLATE_REFERENCE_DRAFT_ID
              ? sortCozeTemplateSlots(currentValue.slots)
              : resolveUserModuleSlots(
                currentValue.slots,
                userModules,
                includeTemplateUserModules ? userModulesLoaded : true,
                includeTemplateUserModules,
              ),
          }
        : null;
    contextValue = prepareCozeTemplateReferencePickerContext(contextValue);
    const pickerSlots = getCozeTemplateReferencePickerSlots(contextValue?.slots || [], {
      includeUploadSlot: allowUserSlots,
      includeUserSlots: false,
      maxUserSlots: referencePickerUserSlotLimit,
    });
    const nextSessionTemplateId = contextValue?.template_set_id ?? null;
    if (referencePickerSessionTemplateId !== nextSessionTemplateId) {
      setReferencePickerSessionSlots([]);
      setReferencePickerSessionTemplateId(nextSessionTemplateId);
    }
    const firstSlotWithImage = pickerSlots.find((slot) => cleanText(slot.template_image) || cleanText(slot.image_url));
    const preferredSlotKey = cleanText(preferredSlotName)
      ? pickerSlots.find((slot) => slot.slot_name === preferredSlotName)?.slot_key || ""
      : "";
    setReferencePickerProductType(cleanText(contextValue?.category) || COZE_TEMPLATE_DEFAULT_PRODUCT_TYPES[0] || "");
    setReferencePickerContext(contextValue);
    setReferencePickerSlotOrder(pickerSlots.map((slot) => slot.slot_key));
    setDragOverReferenceSlotKey(null);
    setReferencePickerRemovedSlotKeys(new Set());
    setSelectedReferenceSlotKey((currentKey) => {
      if (preferredSlotKey) return preferredSlotKey;
      const currentSlot = pickerSlots.find((slot) => (
        slot.slot_key === currentKey &&
        (cleanText(slot.template_image) || cleanText(slot.image_url))
      ));
      return currentSlot?.slot_key || firstSlotWithImage?.slot_key || "";
    });
    referencePickerOpenRef.current = true;
    setReferencePickerOpen(true);
  };

  // Re-open the picker with the template that is currently applied.  The
  // workspace value only carries the selected slot data, so fetching the
  // latest detail is necessary after switching templates; otherwise the
  // picker falls back to an empty custom-reference draft.
  const openCurrentTemplateReferencePicker = async () => {
    const currentValue = latestValueRef.current || normalizedValue;
    const templateSetId = currentValue?.template_set_id;
    if (typeof templateSetId === "number" && templateSetId > 0) {
      try {
        const detail = (await ecomImageApi.getCozeTemplateSet(templateSetId)).data;
        if (detail) {
          openReferencePicker(detail);
          return;
        }
      } catch {
        // Fall through to the local draft when the detail request fails.
      }
    }
    // Drafts (template_set_id=-8800) and an unavailable detail should retain
    // the current local value, including any custom images already uploaded.
    openReferencePicker();
  };

  // Re-enter the template library after a template has been applied.  A
  // fixed/custom template (positive ID) waits for the applied template
  // detail, then opens the library directly on that detail so the
  // already-selected template is immediately visible without flashing the
  // list; closing the detail returns to the library list.  The custom
  // reference draft reopens its picker with the current local value.
  const openAppliedTemplateLibrary = async () => {
    const currentValue = latestValueRef.current || normalizedValue;
    const templateSetId = currentValue?.template_set_id;
    if (typeof templateSetId === "number" && templateSetId > 0) {
      // Wait for the applied template detail before opening the library so
      // the modal renders directly on the selected detail instead of flashing
      // the list first. The X button then falls back to the library list.
      setTemplateError("");
      setLibraryCategory("");
      if (!userModulesLoaded && !loadingUserModules) {
        void loadUserModules();
      }
      let ownedTemplate = mineTemplateIds.has(templateSetId);
      if (!ownedTemplate && !mineTemplatesLoaded) {
        const ownedTemplates = await loadMineTemplates();
        ownedTemplate = ownedTemplates.some((template) => template.id === templateSetId);
      }
      await openTemplateDetail(templateSetId, activeSlotKey, ownedTemplate);
      setLibraryOpen(true);
      return;
    }
    await openCurrentTemplateReferencePicker();
  };

  const closeReferencePicker = (openLibrary = true) => {
    const preserveSessionSlots = referencePickerKeepSessionSlotsRef.current;
    referencePickerKeepSessionSlotsRef.current = false;
    referencePickerOpenRef.current = false;
    referencePickerUploadSeqRef.current += 1;
    referencePickerUploadTokenBySlotRef.current.clear();
    setReferencePickerOpen(false);
    setReferencePickerContext(null);
    setSelectedReferenceSlotKey("");
    setCroppingReferenceSlot(null);
    setReferencePickerSaveTemplate(false);
    setReferencePickerSlotOrder([]);
    setDragOverReferenceSlotKey(null);
    setReferencePickerRemovedSlotKeys(new Set());
    if (!preserveSessionSlots) {
      setReferencePickerSessionSlots([]);
      setReferencePickerSessionTemplateId(null);
    }
    pendingReferencePickerSlotKeyRef.current = null;
    pendingReferencePickerFilesRef.current = null;
    clearReferencePickerDrafts();
    setReferencePickerUploadingSlotKeys(new Set());
    if (openLibrary) setLibraryOpen(true);
  };

  const closeAppliedReferenceOverlays = () => {
    closeReferencePicker(false);
    setLibraryOpen(false);
    resetPreviewTemplateDraft();
    setPreviewDetail(null);
  };

  const openReferencePickerSlotUpload = (slotKey: string) => {
    if (!referencePickerContextValue) return;
    setSelectedReferenceSlotKey(slotKey);
    pendingReferencePickerSlotKeyRef.current = slotKey;
    window.setTimeout(() => customReferenceInputRef.current?.click(), 0);
  };

  const applyReferenceImagesFromPicker = async () => {
    if (!referencePickerContextValue) return;
    if (!referencePickerHasApplicableImage) {
      feedback.warning("请先上传参考图");
      return;
    }

    if (!referencePickerSaveTemplate) {
      // Keep all five fixed slots in the local draft. Only populated entries
      // are used for the cover/reverse request; empty middle slots retain
      // their stable slot key and display position.
      const orderedApplicableEntries = referencePickerApplicableEntries;
      const nextValue = buildReferenceDraftTemplateValue(
        orderedApplicableEntries,
        referencePickerSlots,
        referencePickerProductType,
        referencePickerContextValue,
      );
      commitValue(nextValue);
      setActiveSlotKey(nextValue.slots.find((slot) => slot.selected)?.slot_key || nextValue.slots[0]?.slot_key || "");
      setReferencePickerSessionSlots(nextValue.slots
        .filter((slot) => slot.is_user_slot)
        .map((slot) => ({ ...slot, is_reference_picker_session_slot: true })));
      setReferencePickerSessionTemplateId(nextValue.template_set_id ?? null);
      referencePickerKeepSessionSlotsRef.current = true;
      closeAppliedReferenceOverlays();
      return;
    }

    const orderedApplicableEntries = referencePickerApplicableEntries;
    const orderedPickerSlotsForSubmit = orderedApplicableEntries.map((entry) => entry.slot);

    const productType = cleanText(referencePickerProductType);
    if (!productType) {
      feedback.warning("请选择产品类型");
      return;
    }

    setAddingCustom(true);
    try {
      const res = await ecomImageApi.saveCozeTemplateReferenceTemplate({
        workflow_key: workflowKey,
        product_type: productType,
        name: `${productType}参考图模版`,
        cover_image_url: orderedApplicableEntries[0]?.imageUrl,
        slots: orderedApplicableEntries.map((entry) => ({
          slot_name: entry.slot.slot_name,
          image_url: entry.imageUrl,
        })),
      });
      const detail = res.data;
      if (!detail) throw new Error("empty_template");
      const mergedValue = mergeTemplateDetailToValue(
        detail,
        referencePickerContextValue,
        userModules,
        userModulesLoaded,
      );
      const orderedSlotByKey = new Map(orderedPickerSlotsForSubmit.map((slot) => [slot.slot_key, slot]));
      const existingSlotKeyByName = new Map(referencePickerContextValue.slots.map((slot) => [cleanText(slot.slot_name), slot.slot_key]));
      const remappedUploadedImages = mergedValue.uploaded_images.map((image) => {
        const existingSlotKey = existingSlotKeyByName.get(cleanText(image.definition));
        const orderedSlotName = existingSlotKey ? orderedSlotByKey.get(existingSlotKey)?.slot_name : undefined;
        return orderedSlotName ? { ...image, definition: orderedSlotName } : image;
      });
      const mergedReferenceSlots = mergeCozeTemplateReferenceSlots(mergedValue.slots, orderedPickerSlotsForSubmit)
        .map((slot) => {
          const orderedSlot = orderedSlotByKey.get(slot.slot_key);
          return orderedSlot
            ? { ...slot, slot_name: orderedSlot.slot_name, sort_order: orderedSlot.sort_order }
            : slot;
        });
      const nextValue = normalizeSingleSelectedSlot({
        ...mergedValue,
        slots: mergedReferenceSlots,
        uploaded_images: remappedUploadedImages,
      });
      commitValue(nextValue);
      setActiveSlotKey(nextValue.slots.find((slot) => slot.selected)?.slot_key || nextValue.slots[0]?.slot_key || "");
      setReferencePickerSessionSlots(nextValue.slots
        .filter((slot) => slot.is_user_slot)
        .map((slot) => ({ ...slot, is_reference_picker_session_slot: true })));
      setReferencePickerSessionTemplateId(nextValue.template_set_id ?? null);
      referencePickerKeepSessionSlotsRef.current = true;
      const savedSummary: CozeTemplateSetSummary = {
        image_sources: detail.image_sources,
        id: detail.id,
        name: detail.name,
        description: detail.description,
        category: detail.category,
        product_type: detail.product_type,
        size_label: detail.size_label,
        cover_image_url: detail.cover_image_url,
        visibility: detail.visibility,
        status: detail.status,
        sort_order: detail.sort_order,
        created_by: detail.created_by,
        slots: detail.slots,
        user_slots: detail.user_slots,
      };
      setTemplateSets((current) => [savedSummary, ...current.filter((item) => item.id !== detail.id)]);
      if (mineTemplatesLoaded) {
        setMineTemplateSets((current) => [savedSummary, ...current.filter((item) => item.id !== detail.id)]);
      }
      closeAppliedReferenceOverlays();
      feedback.success("参考图模版已保存");
    } catch {
      feedback.warning("参考图模版保存失败，请稍后重试");
    } finally {
      setAddingCustom(false);
    }
  };

  const uploadCustomReferenceFile = useCallback(async (
    file: File,
    slotKey: string,
    pickerSlotOverride?: CozeTemplateReferencePickerSlot,
  ) => {
    if (!referencePickerOpenRef.current || !isImageFile(file)) return false;
    const pickerContext = referencePickerContextValue;
    if (!pickerContext) return false;
    const pickerSlot = pickerSlotOverride || referencePickerSlots.find((slot) => slot.slot_key === slotKey);
    if (!pickerSlot) return false;
    if (
      pickerSlot.is_upload_placeholder &&
      referencePickerFilledSlots.length >= COZE_TEMPLATE_REFERENCE_IMAGE_LIMIT
    ) {
      feedback.warning("最多上传10张参考图，请先删除一张");
      return false;
    }
    const slotName = pickerSlot.slot_name;
    if (
      pickerSlot.is_default_reference_slot &&
      referencePickerRemovedSlotKeys.has(pickerSlot.slot_key) &&
      referencePickerFilledSlots.length >= 5
    ) {
      setReferencePickerSlotOrder((current) => {
        const imageSlotKeys = referencePickerSlots
          .filter((slot) => !slot.is_upload_placeholder)
          .map((slot) => slot.slot_key);
        const imageSlotKeySet = new Set(imageSlotKeys);
        const completeOrder = [
          ...current.filter((slotKey) => imageSlotKeySet.has(slotKey)),
          ...imageSlotKeys.filter((slotKey) => !current.includes(slotKey)),
        ];
        return [
          ...completeOrder.filter((slotKey) => slotKey !== pickerSlot.slot_key),
          pickerSlot.slot_key,
        ];
      });
    }
    // A virtual tail must never bind by its sequential display name: a
    // historical personal module may have been normalized to the same 副图N
    // label while intentionally hidden from this picker.
    const existingReferenceUserSlot = pickerSlot.is_upload_placeholder
      ? undefined
      : pickerContext.slots.find((slot) => slot.is_user_slot && slot.slot_key === pickerSlot.slot_key);
    const uploadSeq = referencePickerUploadSeqRef.current + 1;
    referencePickerUploadSeqRef.current = uploadSeq;
    const uploadSlotKey = pickerSlot.slot_key;
    referencePickerUploadTokenBySlotRef.current.set(uploadSlotKey, uploadSeq);
    const isCurrentSlotUpload = () => (
      referencePickerOpenRef.current &&
      referencePickerUploadTokenBySlotRef.current.get(uploadSlotKey) === uploadSeq
    );
    const localUrl = URL.createObjectURL(file);
    setSelectedReferenceSlotKey(uploadSlotKey);
    setReferencePickerSlotDraft(uploadSlotKey, { imageUrl: localUrl, previewUrl: localUrl });
    setReferencePickerUploadingSlotKeys((current) => {
      const next = new Set(current);
      next.add(uploadSlotKey);
      return next;
    });
    try {
      const res = await ecomImageApi.uploadReferenceImage(file, { maxBytes: MAX_REVERSE_PROMPT_BYTES });
      const url = getUploadedUrl(res.data);
      if (!url) throw new Error("upload_failed");
      const previewUrl = getUploadedPreviewUrl(res.data) || url;
      if (!isCurrentSlotUpload()) return false;
      const templateSetId = Number(pickerContext.template_set_id);
      const userModuleId = existingReferenceUserSlot?.user_module_id;
      const optimisticSessionSlot: ReferencePickerSessionSlot | null = pickerSlot.is_upload_placeholder && !existingReferenceUserSlot
        ? {
            ...pickerSlot,
            template_image: url,
            image_url: url,
            is_user_slot: true,
            is_upload_placeholder: false,
            selected: false,
            prompt_by_variant: {},
            prompt_status_by_variant: {},
            is_reference_picker_session_slot: true,
          }
        : null;
      if (optimisticSessionSlot) {
        // The uploaded asset is already durable even if the optional
        // account-module binding fails. Materialize it locally before that
        // second request so the visible image never falls back to a revoked
        // blob URL or disappears from the picker.
        setReferencePickerSessionSlots((current) => [
          ...current.filter((slot) => slot.slot_key !== optimisticSessionSlot.slot_key),
          optimisticSessionSlot,
        ]);
        setReferencePickerSlotOrder((current) => {
          const completeOrder = [
            ...current,
            ...referencePickerSlots
              .map((slot) => slot.slot_key)
              .filter((slotKey) => !current.includes(slotKey)),
          ];
          return completeOrder.includes(optimisticSessionSlot.slot_key)
            ? completeOrder
            : [...completeOrder, optimisticSessionSlot.slot_key];
        });
        setReferencePickerContext((current) => current ? {
          ...current,
          slots: [
            ...current.slots.filter((slot) => slot.slot_key !== optimisticSessionSlot.slot_key),
            optimisticSessionSlot,
          ],
        } : current);
        restoreReferencePickerSlot(uploadSlotKey);
        setReferencePickerSlotDraft(uploadSlotKey, { imageUrl: url, previewUrl });
      }
      if (userModuleId && canCreateCozeTemplateUserReference({ template_set_id: templateSetId })) {
        const bindingRes = await ecomImageApi.updateCozeTemplateUserModuleImage(templateSetId, userModuleId, {
          image_url: url,
        });
        if (!isCurrentSlotUpload()) return false;
        const savedBinding: CozeTemplateUserSlotDTO = bindingRes.data || {
          id: userModuleId,
          template_set_id: templateSetId,
          user_module_id: userModuleId,
          slot_key: existingReferenceUserSlot.slot_key,
          slot_name: existingReferenceUserSlot.slot_name,
          image_url: url,
        };
        setReferencePickerContext((current) => current ? {
          ...current,
          slots: current.slots.map((slot) => slot.user_module_id === userModuleId
            ? {
                ...slot,
                image_url: url,
                template_image: url,
                template_user_slot_id: savedBinding.template_user_slot_id,
                prompt_by_variant: {},
                prompt_status_by_variant: {},
              }
            : slot),
        } : current);
        setPreviewDetail((detail) => upsertUserSlotInTemplateDetail(detail, savedBinding, templateSetId));
        restoreReferencePickerSlot(uploadSlotKey);
        setReferencePickerSlotDraft(uploadSlotKey, null);
        setSelectedReferenceSlotKey(uploadSlotKey);
        return true;
      }
      if (pickerSlot.is_upload_placeholder && !existingReferenceUserSlot && allowUserSlots && userModules.length < maxUserSlots &&
        canCreateCozeTemplateUserReference({ template_set_id: templateSetId })) {
        const bindingRes = await ecomImageApi.createCozeTemplateUserSlot(templateSetId, {
          workflow_key: workflowKey,
          slot_name: slotName,
          image_url: url,
        })
          .catch(() => null);
        if (!bindingRes) {
          if (isCurrentSlotUpload()) {
            feedback.warning("图片已上传，模块同步失败，本次仍可应用");
          }
          return true;
        }
        if (!isCurrentSlotUpload()) return false;
        const savedBinding: CozeTemplateUserSlotDTO = bindingRes.data || {
          id: 0,
          template_set_id: templateSetId,
          user_module_id: undefined,
          slot_key: pickerSlot.slot_key,
          slot_name: slotName,
          image_url: url,
        };
        const userModuleIdFromBinding = savedBinding.user_module_id || savedBinding.id;
        if (!userModuleIdFromBinding) throw new Error("empty_user_module_binding");
        const userModule: CozeTemplateUserModuleDTO = {
          id: userModuleIdFromBinding,
          slot_key: cleanText(savedBinding.slot_key) || pickerSlot.slot_key,
          slot_name: cleanText(savedBinding.slot_name) || slotName,
          param_key: savedBinding.param_key,
          is_default_selected: savedBinding.is_default_selected,
          sort_order: savedBinding.sort_order,
          status: savedBinding.status,
        };
        const nextModules = sortCozeTemplateSlots([
          ...userModules.filter((module) => module.id !== userModule.id),
          userModule,
        ]);
        const nextSlot: CozeTemplateSetSlot & ReferencePickerSessionSlot = {
          ...pickerSlot,
          ...savedBinding,
          id: savedBinding.id || pickerSlot.id,
          user_module_id: userModule.id,
          slot_key: userModule.slot_key,
          // Account-level module names can keep increasing after a picker
          // image is removed. The local picker position is authoritative so
          // a newly appended image follows the compacted 副图N sequence.
          slot_name: slotName,
          param_key: userModule.param_key,
          template_image: url,
          image_url: url,
          is_user_slot: true,
          is_upload_placeholder: false,
          selected: false,
          prompt_by_variant: {},
          prompt_status_by_variant: {},
          is_reference_picker_session_slot: true,
        };
        setReferencePickerSessionSlots((current) => [
          ...current.filter((slot) => (
            slot.slot_key !== pickerSlot.slot_key && slot.slot_key !== nextSlot.slot_key
          )),
          nextSlot,
        ]);
        setReferencePickerSlotOrder((current) => current.map((slotKey) => (
          slotKey === pickerSlot.slot_key ? nextSlot.slot_key : slotKey
        )));
        setUserModules(nextModules);
        setReferencePickerContext((current) => current ? {
          ...current,
          slots: resolveUserModuleSlots(
            [...current.slots.filter((slot) => slot.slot_key !== pickerSlot.slot_key), nextSlot],
            nextModules,
            true,
          ),
        } : current);
        setPreviewDetail((detail) => upsertUserSlotInTemplateDetail(detail, savedBinding, templateSetId));
        restoreReferencePickerSlot(uploadSlotKey);
        setReferencePickerSlotDraft(uploadSlotKey, null);
        setSelectedReferenceSlotKey(nextSlot.slot_key);
        return true;
      }
      restoreReferencePickerSlot(uploadSlotKey);
      setReferencePickerSlotDraft(uploadSlotKey, { imageUrl: url, previewUrl });
      return true;
    } catch {
      if (isCurrentSlotUpload()) {
        setReferencePickerSlotDraft(uploadSlotKey, null);
        feedback.warning("参考图上传失败，请稍后重试");
      }
      return false;
    } finally {
      if (referencePickerUploadTokenBySlotRef.current.get(uploadSlotKey) === uploadSeq) {
        referencePickerUploadTokenBySlotRef.current.delete(uploadSlotKey);
        setReferencePickerUploadingSlotKeys((current) => {
          if (!current.has(uploadSlotKey)) return current;
          const next = new Set(current);
          next.delete(uploadSlotKey);
          return next;
        });
      }
    }
  }, [allowUserSlots, maxUserSlots, referencePickerContextValue, referencePickerFilledSlots.length, referencePickerRemovedSlotKeys, referencePickerSlots, restoreReferencePickerSlot, setReferencePickerSlotDraft, userModules, workflowKey]);

  const handleCustomReferenceFiles = useCallback(async (files: File[], preferredSlotKey?: string) => {
    if (!referencePickerOpenRef.current) return;
    const imageFiles = files.filter(isImageFile);
    if (imageFiles.length === 0) {
      feedback.warning("请拖入图片文件");
      return;
    }
    const availableSlots = referencePickerSlots.filter((slot) => !referencePickerSlotImages.get(slot.slot_key));
    const preferredSlot = preferredSlotKey
      ? availableSlots.find((slot) => slot.slot_key === preferredSlotKey)
      : undefined;
    let targetSlots = preferredSlot
      ? [preferredSlot, ...availableSlots.filter((slot) => slot.slot_key !== preferredSlot.slot_key)]
      : [...availableSlots];
    if (preferredSlot?.is_upload_placeholder) {
      targetSlots = [preferredSlot];
    }
    let nextVirtualIndex = referencePickerSlots.length;
    while (
      targetSlots.length < imageFiles.length &&
      nextVirtualIndex < COZE_TEMPLATE_REFERENCE_IMAGE_LIMIT
    ) {
      targetSlots.push({
        slot_key: `__upload_reference_${nextVirtualIndex + 1}`,
        slot_name: getCozeTemplateSequentialSlotName(nextVirtualIndex),
        sort_order: nextVirtualIndex + 1,
        is_upload_placeholder: true,
      });
      nextVirtualIndex += 1;
    }
    if (imageFiles.length > targetSlots.length) {
      feedback.warning(`当前仅剩${targetSlots.length}个可用槽位，已按顺序处理前${targetSlots.length}张`);
    }
    const filesToUpload = imageFiles.slice(0, targetSlots.length);
    await Promise.all(filesToUpload.map((file, index) => {
      const targetSlot = targetSlots[index];
      if (!targetSlot) return Promise.resolve(false);
      return uploadCustomReferenceFile(file, targetSlot.slot_key, targetSlot);
    }));
  }, [referencePickerSlotImages, referencePickerSlots, uploadCustomReferenceFile]);

  const handleReferenceCropSave = useCallback(async (croppedDataUrl: string) => {
    const targetSlot = croppingReferenceSlot;
    setCroppingReferenceSlot(null);
    if (!targetSlot) return;

    try {
      const blob = await fetch(croppedDataUrl).then((response) => response.blob());
      const file = new File(
        [blob],
        `cropped-reference-${Date.now()}.png`,
        { type: "image/png" },
      );
      await uploadCustomReferenceFile(file, targetSlot.slotKey);
    } catch {
      feedback.warning("裁剪图片上传失败，请稍后重试");
    }
  }, [croppingReferenceSlot, uploadCustomReferenceFile]);

  const handleCustomReferenceFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    const slotKey = pendingReferencePickerSlotKeyRef.current || selectedReferenceSlotKey;
    pendingReferencePickerSlotKeyRef.current = null;
    const firstFile = files[0];
    if (!firstFile || !slotKey || !referencePickerOpen) return;
    const targetHasImage = Boolean(referencePickerSlotImages.get(slotKey));
    if (targetHasImage) {
      void uploadCustomReferenceFile(firstFile, slotKey);
      return;
    }
    void handleCustomReferenceFiles(files, slotKey);
  };

  useEffect(() => {
    if (!referencePickerOpen || !pendingReferencePickerFilesRef.current) return;
    const files = pendingReferencePickerFilesRef.current;
    pendingReferencePickerFilesRef.current = null;
    void handleCustomReferenceFiles(files);
  }, [handleCustomReferenceFiles, referencePickerOpen]);

  const handleReferenceTemplateCardDrop = async (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setCustomReferenceDragOver(false);
    if (addingCustom || uploadingCustomReference) return;
    const files = await getImageFilesFromDataTransfer(event.dataTransfer);
    if (files.length === 0) {
      feedback.warning("请拖入图片文件");
      return;
    }
    pendingReferencePickerFilesRef.current = files;
    openReferencePicker();
  };

  const handleReferenceSlotDragOver = (event: React.DragEvent<HTMLDivElement>, slotKey: string) => {
    const targetSlot = referencePickerSlots.find((slot) => slot.slot_key === slotKey);
    if (!targetSlot || !event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setDragOverReferenceSlotKey(slotKey);
  };

  const handleReferenceSlotDrop = async (event: React.DragEvent<HTMLDivElement>, slotKey: string) => {
    event.preventDefault();
    event.stopPropagation();
    const targetSlot = referencePickerSlots.find((slot) => slot.slot_key === slotKey);
    if (!event.dataTransfer.types.includes("Files")) return;
    const files = await getImageFilesFromDataTransfer(event.dataTransfer);
    if (files.length === 0) {
      feedback.warning("请拖入图片文件");
    } else {
      await handleCustomReferenceFiles(
        files,
        targetSlot && !referencePickerSlotImages.get(slotKey) ? targetSlot.slot_key : undefined,
      );
    }
    setDragOverReferenceSlotKey(null);
  };

  const handleReferenceSlotReorder = (orderedSlotKeys: string[]) => {
    setReferencePickerSlotOrder(() => {
      const currentSlotKeys = referencePickerSlots.map((slot) => slot.slot_key);
      const currentSlotKeySet = new Set(currentSlotKeys);
      const reorderedKeys = orderedSlotKeys.filter((slotKey) => currentSlotKeySet.has(slotKey));
      const reorderedKeySet = new Set(reorderedKeys);
      return [
        ...reorderedKeys,
        ...currentSlotKeys.filter((slotKey) => !reorderedKeySet.has(slotKey)),
      ];
    });
  };

  const handleReferenceDialogFileDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = await getImageFilesFromDataTransfer(event.dataTransfer);
    if (files.length === 0) {
      feedback.warning("请拖入图片文件");
      return;
    }
    await handleCustomReferenceFiles(files);
  };

  const handlePreviewTemplateSlotDragOver = (event: React.DragEvent<HTMLDivElement>, slotKey: string) => {
    const targetSlot = previewSlots.find((slot) => slot.slot_key === slotKey);
    if (!targetSlot || !event.dataTransfer.types.includes("Files")) return;
    const canManage = Boolean(previewDetail && mineTemplateIds.has(previewDetail.id));
    if (!canManage) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setDragOverPreviewSlotKey(slotKey);
  };

  const uploadFilesToPreviewTemplateDraft = async (files: File[], preferredSlotKey: string) => {
    const detail = previewDetail;
    if (
      !detail ||
      !mineTemplateIds.has(detail.id) ||
      previewTemplateDraftId !== detail.id ||
      uploadingPreviewTemplate ||
      savingPreviewTemplate
    ) {
      return;
    }
    const imageFiles = files.filter(isImageFile);
    if (imageFiles.length === 0) {
      feedback.warning("请拖入图片文件");
      return;
    }

    const plan = planCozeTemplatePreviewFileDrop(
      // `previewSlots` is a display projection and intentionally trims tail
      // empty system slots after the first five. Planning against it would
      // mistake existing physical slots 6..10 for append-only positions,
      // producing draft slots with sort_order 11+ (副图4/6/8 in the UI).
      previewTemplateDraftRef.current,
      preferredSlotKey,
      imageFiles.length,
    );
    if (plan.accepted_count === 0) {
      feedback.warning(`模板最多上传 ${COZE_TEMPLATE_REFERENCE_IMAGE_LIMIT} 张图片`);
      return;
    }
    if (plan.truncated) {
      feedback.warning(`当前最多还能处理${plan.accepted_count}张图片，已按顺序上传`);
    }

    const acceptedFiles = imageFiles.slice(0, plan.accepted_count);
    const localUrls = acceptedFiles.map((file) => URL.createObjectURL(file));
    const draftBeforeUpload = previewTemplateDraftRef.current;
    const previousSlots = new Map<string, TemplatePreviewSlot | null>();
    const nextDraft = [...draftBeforeUpload];
    plan.targets.forEach((target, index) => {
      const existingIndex = nextDraft.findIndex((slot) => slot.slot_key === target.slot_key);
      const existing = existingIndex >= 0 ? nextDraft[existingIndex] : null;
      previousSlots.set(target.slot_key, existing ? { ...existing } : null);
      const localUrl = localUrls[index];
      const nextSlot: TemplatePreviewSlot = {
        ...(existing || {}),
        template_set_id: detail.id,
        slot_key: target.slot_key,
        slot_name: target.slot_name,
        sort_order: target.sort_order,
        is_user_slot: existing ? Boolean(existing.is_user_slot) : target.is_user_slot,
        is_upload_placeholder: false,
        image_url: localUrl,
        template_image: localUrl,
        selected: existing?.selected ?? false,
        prompt_by_variant: {},
        prompt_status_by_variant: {},
      };
      if (existingIndex >= 0) nextDraft[existingIndex] = nextSlot;
      else nextDraft.push(nextSlot);
    });

    const session = previewTemplateEditSessionRef.current;
    syncPreviewTemplateDraft(nextDraft);
    setPreviewTemplateUploadingSlotKeys(new Set(plan.targets.map((target) => target.slot_key)));
    setPreviewSlotKey(plan.targets[0]?.slot_key || preferredSlotKey);

    const results = await mapSettledWithConcurrency(acceptedFiles, async (file) => {
      const response = await ecomImageApi.uploadProductImage(file, { maxBytes: MAX_REVERSE_PROMPT_BYTES });
      const url = getUploadedUrl(response.data);
      if (!url) throw new Error("upload_failed");
      return url;
    });

    if (previewTemplateEditSessionRef.current !== session) {
      localUrls.forEach(revokeBlobUrl);
      return;
    }

    const failedSlotKeys = new Set<string>();
    syncPreviewTemplateDraft((current) => {
      const next = [...current];
      results.forEach((result, index) => {
        const target = plan.targets[index];
        const localUrl = localUrls[index];
        const currentIndex = next.findIndex((slot) => slot.slot_key === target.slot_key);
        if (result.status === "fulfilled") {
          if (currentIndex >= 0 && getTemplateImageFromDTO(next[currentIndex]) === localUrl) {
            next[currentIndex] = {
              ...next[currentIndex],
              image_url: result.value,
              template_image: result.value,
            };
          }
          return;
        }
        failedSlotKeys.add(target.slot_key);
        const previous = previousSlots.get(target.slot_key);
        if (previous) {
          if (currentIndex >= 0 && getTemplateImageFromDTO(next[currentIndex]) === localUrl) {
            next[currentIndex] = previous;
          }
        } else if (currentIndex >= 0 && getTemplateImageFromDTO(next[currentIndex]) === localUrl) {
          next.splice(currentIndex, 1);
        }
      });
      return next;
    });
    localUrls.forEach(revokeBlobUrl);
    setPreviewTemplateUploadingSlotKeys(new Set());

    if (failedSlotKeys.size > 0) {
      feedback.warning(failedSlotKeys.size === acceptedFiles.length
        ? "模板图上传失败，请稍后重试"
        : "部分模板图片上传失败，请重试");
    }
  };

  const handlePreviewTemplateCropSave = useCallback(async (croppedDataUrl: string) => {
    const targetSlot = croppingPreviewTemplateSlot;
    setCroppingPreviewTemplateSlot(null);
    if (!targetSlot) return;

    try {
      const blob = await fetch(croppedDataUrl).then((response) => response.blob());
      const file = new File(
        [blob],
        `cropped-template-${Date.now()}.png`,
        { type: "image/png" },
      );
      await uploadFilesToPreviewTemplateDraft([file], targetSlot.slotKey);
    } catch {
      feedback.warning("裁剪图片上传失败，请稍后重试");
    }
  }, [croppingPreviewTemplateSlot, uploadFilesToPreviewTemplateDraft]);

  const handlePreviewTemplateSlotDrop = async (event: React.DragEvent<HTMLDivElement>, slotKey: string) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverPreviewSlotKey(null);
    const targetSlot = previewSlots.find((slot) => slot.slot_key === slotKey);
    if (!targetSlot || !event.dataTransfer.types.includes("Files")) return;
    const canManage = Boolean(previewDetail && mineTemplateIds.has(previewDetail.id));
    if (!canManage) {
      feedback.warning("系统模块模板图由管理员维护");
      return;
    }
    if (uploadingPreviewTemplate || savingPreviewTemplate) return;
    const files = await getImageFilesFromDataTransfer(event.dataTransfer);
    await uploadFilesToPreviewTemplateDraft(files, slotKey);
  };

  const requestDeleteTemplate = (template: CozeTemplateSetSummary) => {
    if (!mineTemplateIds.has(template.id) || deletingTemplateRef.current) return;
    setPendingDeleteTemplate(template);
  };

  const closeDeleteTemplateConfirm = () => {
    if (deletingTemplateRef.current) return;
    setPendingDeleteTemplate(null);
  };

  const handleDeleteTemplate = async () => {
    const template = pendingDeleteTemplate;
    if (!template || deletingTemplateRef.current) return;
    deletingTemplateRef.current = true;
    setDeletingTemplate(true);
    try {
      await ecomImageApi.deleteCozeTemplateSet(template.id);
      setTemplateSets((current) => current.filter((item) => item.id !== template.id));
      setMineTemplateSets((current) => current.filter((item) => item.id !== template.id));
      if (previewDetail?.id === template.id) {
        resetPreviewTemplateDraft();
        setPreviewDetail(null);
        setPreviewSlotKey("");
      }
      setPendingDeleteTemplate(null);
      feedback.success("自定义模版已删除");
    } catch {
      feedback.warning("自定义模版删除失败，请稍后重试");
    } finally {
      deletingTemplateRef.current = false;
      setDeletingTemplate(false);
    }
  };

  const openSlotUpload = (slotKey: string, templateSetId?: number, userModuleId?: number) => {
    pendingUploadSlotKeyRef.current = slotKey;
    pendingUploadTemplateSetIdRef.current = templateSetId || null;
    pendingUploadUserModuleIdRef.current = userModuleId || null;
    fileInputRef.current?.click();
  };

  const canManagePreviewTemplateSlot = (slot: TemplatePreviewSlot) => (
    mineTemplateIds.has(slot.template_set_id)
  );

  const updatePreviewTemplateSlotImage = async (slot: TemplatePreviewSlot, imageUrl: string) => {
    const templateSetId = slot.template_set_id;
    if (!canManagePreviewTemplateSlot(slot) || uploadingSlotKey) return false;

    setUploadingSlotKey(slot.slot_key);
    try {
      if (slot.is_user_slot) {
        if (!slot.user_module_id) throw new Error("empty_user_module");
        await ecomImageApi.updateCozeTemplateUserModuleImage(templateSetId, slot.user_module_id, { image_url: imageUrl });
      } else {
        await ecomImageApi.updateCozeTemplateSlotImage(templateSetId, slot.slot_key, { image_url: imageUrl });
      }
      const current = getCurrentTemplateValue(templateSetId);
      if (current) {
        commitValue({
          ...current,
          slots: current.slots.map((item) => item.slot_key === slot.slot_key
            ? {
                ...item,
                image_url: imageUrl,
                template_image: imageUrl,
                prompt_by_variant: {},
                prompt_status_by_variant: {},
              }
            : item),
        });
      }
      const nextPreviewDetailSlots = previewDetail?.id === templateSetId
        ? previewDetail.slots.map((item) => item.slot_key === slot.slot_key
          ? { ...item, image_url: imageUrl, template_image: imageUrl }
          : item)
        : null;
      setPreviewDetail((detail) => {
        if (detail?.id !== templateSetId) return detail;
        const nextDetailSlots = nextPreviewDetailSlots || (detail.slots || []).map((item) => item.slot_key === slot.slot_key
          ? { ...item, image_url: imageUrl, template_image: imageUrl }
          : item);
        return {
          ...detail,
          slots: nextDetailSlots,
          cover_image_url: resolveTemplateCoverImage(nextDetailSlots),
        };
      });
      const updateSummary = (template: CozeTemplateSetSummary) => {
        if (template.id !== templateSetId) return template;
        const nextSummarySlots = (nextPreviewDetailSlots || template.slots || []).map((item) => item.slot_key === slot.slot_key
          ? { ...item, image_url: imageUrl, template_image: imageUrl }
          : item);
        return {
          ...template,
          slots: nextSummarySlots,
          cover_image_url: resolveTemplateCoverImage(nextSummarySlots),
        };
      };
      setTemplateSets((currentTemplates) => currentTemplates.map(updateSummary));
      setMineTemplateSets((currentTemplates) => currentTemplates.map(updateSummary));
      return true;
    } catch {
      feedback.warning(imageUrl ? "模板图上传失败，请稍后重试" : "模板图删除失败，请稍后重试");
      return false;
    } finally {
      setUploadingSlotKey(null);
    }
  };

  const handlePreviewTemplateSlotReorder = (orderedSlotKeys: string[]) => {
    if (
      !previewDetail ||
      !ownedPreviewDetail ||
      uploadingPreviewTemplate ||
      savingPreviewTemplate ||
      reorderingPreviewTemplate
    ) return;
    const nextSlots = reorderCozeTemplateReferenceSlotImages(previewSlots, orderedSlotKeys)
      .filter((slot) => !slot.is_upload_placeholder)
      .map((slot) => ({ ...slot, template_set_id: previewDetail.id }));
    if (
      getPreviewTemplateDraftSignature(nextSlots) ===
      getPreviewTemplateDraftSignature(previewTemplateDraftRef.current)
    ) return;

    setReorderingPreviewTemplate(true);
    syncPreviewTemplateDraft(nextSlots);
    const currentPreviewSlot = nextSlots.find((slot) => slot.slot_key === previewSlotKey);
    if (!currentPreviewSlot || !getTemplateImageFromDTO(currentPreviewSlot)) {
      setPreviewSlotKey(nextSlots.find((slot) => Boolean(getTemplateImageFromDTO(slot)))?.slot_key || "");
    }
    setReorderingPreviewTemplate(false);
  };

  const removePreviewUserSlotImage = (slot: TemplatePreviewSlot) => {
    if (
      !canManagePreviewTemplateSlot(slot) ||
      !ownedPreviewDetail ||
      uploadingPreviewTemplate ||
      savingPreviewTemplate
    ) return;
    revokeBlobUrl(getTemplateImageFromDTO(slot));
    syncPreviewTemplateDraft((current) => current.map((item) => (
      item.slot_key === slot.slot_key
        ? {
            ...item,
            image_url: "",
            template_image: "",
            prompt_by_variant: {},
            prompt_status_by_variant: {},
          }
        : item
    )));
    if (previewSlotKey === slot.slot_key) {
      const nextPreviewSlot = previewImageSlots.find((item) => (
        item.slot_key !== slot.slot_key && Boolean(getTemplateImageFromDTO(item))
      ));
      setPreviewSlotKey(nextPreviewSlot?.slot_key || "");
    }
  };

  const uploadFileToPreviewSlot = async (
    file: File,
    slotKey: string,
    templateSetId: number,
    pendingUserModuleId?: number | null,
    sequentialIndex?: number,
  ) => {
    if (
      !isImageFile(file) ||
      !slotKey ||
      !templateSetId ||
      !mineTemplateIds.has(templateSetId)
    ) return;
    setUploadingSlotKey(slotKey);
    try {
      const res = await ecomImageApi.uploadProductImage(file, { maxBytes: MAX_REVERSE_PROMPT_BYTES });
      const url = getUploadedUrl(res.data);
      if (!url) throw new Error("upload_failed");
      const uploadPlaceholder = previewSlots.find((slot) => (
        slot.slot_key === slotKey && slot.is_upload_placeholder
      ));
      if (uploadPlaceholder) {
        if (!allowUserSlots ||
          previewFilledSlotCount >= COZE_TEMPLATE_REFERENCE_IMAGE_LIMIT ||
          !canCreateCozeTemplateUserReference({ template_set_id: templateSetId })) {
          feedback.warning("模板图上传失败，请稍后重试");
          return;
        }
        // 序号由调用方传入（拖拽多文件按已建数量递增），未传时取当前已填图槽位数（点击单图）。
        const slotName = sequentialIndex !== undefined
          ? getCozeTemplateSequentialSlotName(sequentialIndex)
          : getCozeTemplateSequentialSlotName(previewImageSlots.length);
        const bindingRes = await ecomImageApi.createCozeTemplateUserSlot(templateSetId, {
          workflow_key: workflowKey,
          slot_name: slotName,
          image_url: url,
        });
        const savedBinding: CozeTemplateUserSlotDTO = bindingRes.data || {
          id: 0,
          template_set_id: templateSetId,
          user_module_id: undefined,
          slot_key: uploadPlaceholder.slot_key,
          slot_name: slotName,
          image_url: url,
        };
        const userModuleId = savedBinding.user_module_id || savedBinding.id;
        if (!userModuleId) throw new Error("empty_user_module_binding");
        const userModule: CozeTemplateUserModuleDTO = {
          id: userModuleId,
          slot_key: cleanText(savedBinding.slot_key) || `user_slot_${userModuleId}`,
          slot_name: cleanText(savedBinding.slot_name) || slotName,
          param_key: savedBinding.param_key,
          is_default_selected: savedBinding.is_default_selected,
          sort_order: savedBinding.sort_order,
          status: savedBinding.status,
        };
        const nextModules = sortCozeTemplateSlots([
          ...userModulesRef.current.filter((module) => module.id !== userModule.id),
          userModule,
        ]);
        const nextSlot: CozeTemplateSetSlot = {
          ...uploadPlaceholder,
          ...savedBinding,
          id: savedBinding.id || userModule.id,
          user_module_id: userModule.id,
          slot_key: userModule.slot_key,
          slot_name: userModule.slot_name,
          param_key: userModule.param_key,
          template_image: getTemplateImageFromDTO(savedBinding) || url,
          image_url: getTemplateImageFromDTO(savedBinding) || url,
          is_user_slot: true,
          is_upload_placeholder: false,
          selected: false,
          prompt_by_variant: {},
          prompt_status_by_variant: {},
        };
        syncUserModules(nextModules);
        const current = getCurrentTemplateValue(templateSetId);
        if (current) {
          commitValue({
            ...current,
            slots: resolveUserModuleSlots(
              [...current.slots.filter((slot) => slot.slot_key !== uploadPlaceholder.slot_key), nextSlot],
              nextModules,
              true,
            ),
          });
        }
        setPreviewDetail((detail) => upsertUserSlotInTemplateDetail(detail, savedBinding, templateSetId));
        const updateSummary = (template: CozeTemplateSetSummary) => (
          upsertUserSlotInTemplateSummary(template, savedBinding, templateSetId)
        );
        setTemplateSets((currentTemplates) => currentTemplates.map(updateSummary));
        setMineTemplateSets((currentTemplates) => currentTemplates.map(updateSummary));
        setPreviewSlotKey(userModule.slot_key);
        return;
      }
      const currentBeforeUpdate = getCurrentTemplateValue(templateSetId) ||
        (previewDetail?.id === templateSetId
          ? mergeTemplateDetailToValue(
            previewDetail,
            latestValueRef.current,
            userModules,
            userModulesLoaded,
            mineTemplateIds.has(templateSetId),
          )
          : null);
      const targetSlot = currentBeforeUpdate?.slots.find((slot) => (
        (pendingUserModuleId && slot.user_module_id === pendingUserModuleId) || slot.slot_key === slotKey
      ));
      if (!targetSlot || !canManagePreviewTemplateSlot({ ...targetSlot, template_set_id: templateSetId })) {
        feedback.warning("系统模块模板图由管理员维护");
        return;
      }
      if (!targetSlot.is_user_slot) {
        const replaced = await updatePreviewTemplateSlotImage(
          { ...targetSlot, template_set_id: templateSetId },
          url,
        );
        if (replaced) setPreviewSlotKey(slotKey);
        return;
      }
      const userModuleId = pendingUserModuleId || targetSlot.user_module_id;
      if (!userModuleId || !canCreateCozeTemplateUserReference({ template_set_id: templateSetId })) {
        feedback.warning("模板图上传失败，请稍后重试");
        return;
      }
      const updateRes = await ecomImageApi.updateCozeTemplateUserModuleImage(templateSetId, userModuleId, {
        image_url: url,
      });
      const savedImageUrl = updateRes.data ? getTemplateImageFromDTO(updateRes.data) || url : url;
      const savedBinding: CozeTemplateUserSlotDTO = updateRes.data || {
        id: userModuleId,
        template_set_id: templateSetId,
        user_module_id: userModuleId,
        slot_key: targetSlot.slot_key,
        slot_name: targetSlot.slot_name,
        image_url: savedImageUrl,
      };
      const current = getCurrentTemplateValue(templateSetId);
      const nextValue = current ? {
        ...current,
        slots: resolveUserModuleSlots(
          current.slots,
          userModules,
          shouldProjectCozeTemplateUserModules(current, mineTemplateIds.has(templateSetId)) ? userModulesLoaded : true,
          shouldProjectCozeTemplateUserModules(current, mineTemplateIds.has(templateSetId)),
        ).map((slot) => slot.user_module_id === userModuleId
          ? {
              ...slot,
              template_image: savedImageUrl,
              image_url: savedImageUrl,
              template_user_slot_id: savedBinding.template_user_slot_id,
              prompt_by_variant: {},
              prompt_status_by_variant: {},
            }
          : slot),
      } : null;
      setPreviewDetail((detail) => upsertUserSlotInTemplateDetail(detail, savedBinding, templateSetId));
      setPreviewSlotKey(slotKey);
      if (nextValue) {
        commitValue(nextValue);
      }
    } catch {
      feedback.warning("模板图上传失败，请稍后重试");
    } finally {
      setUploadingSlotKey(null);
    }
  };

  const handleSlotFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    const slotKey = pendingUploadSlotKeyRef.current;
    const pendingTemplateSetId = pendingUploadTemplateSetIdRef.current;
    const pendingUserModuleId = pendingUploadUserModuleIdRef.current;
    pendingUploadSlotKeyRef.current = null;
    pendingUploadTemplateSetIdRef.current = null;
    pendingUploadUserModuleIdRef.current = null;
    const firstFile = files[0];
    if (!firstFile || !slotKey) return;
    const templateSetId = pendingTemplateSetId || normalizedValue?.template_set_id;
    if (!templateSetId) return;
    if (previewDetail?.id === templateSetId && !mineTemplateIds.has(templateSetId)) return;
    if (
      previewDetail?.id === templateSetId &&
      mineTemplateIds.has(templateSetId) &&
      previewTemplateDraftId === templateSetId
    ) {
      const targetSlot = previewTemplateDraftRef.current.find((slot) => slot.slot_key === slotKey);
      const targetHasImage = Boolean(targetSlot && getCozeTemplateSlotImage(targetSlot));
      await uploadFilesToPreviewTemplateDraft(targetHasImage ? [firstFile] : files, slotKey);
      return;
    }
    if (!isImageFile(firstFile)) return;
    await uploadFileToPreviewSlot(firstFile, slotKey, templateSetId, pendingUserModuleId);
  };

  const handleSavePreviewTemplate = async (): Promise<CozeTemplateSetDetail | null> => {
    const detail = previewDetail;
    if (
      !detail ||
      !ownedPreviewDetail ||
      previewTemplateDraftId !== detail.id ||
      !previewTemplateDirty ||
      uploadingPreviewTemplate ||
      savingPreviewTemplate ||
      previewTemplateDraft.some((slot) => isBlobUrl(getTemplateImageFromDTO(slot)))
    ) return null;

    const session = previewTemplateEditSessionRef.current;
    setSavingPreviewTemplate(true);
    try {
      const response = await ecomImageApi.saveCozeTemplateSetSlotsBatch(detail.id, {
        slots: previewTemplateDraft.map((slot, index) => ({
          slot_key: slot.slot_key,
          slot_name: getCozeTemplateSequentialSlotName(index),
          image_url: getTemplateImageFromDTO(slot),
          sort_order: index + 1,
          is_user_slot: Boolean(slot.is_user_slot && slot.user_module_id),
          ...(slot.is_user_slot && slot.user_module_id
            ? { user_module_id: slot.user_module_id }
            : {}),
        })),
      });
      if (previewTemplateEditSessionRef.current !== session) return null;
      const savedDetail = response.data;
      if (!savedDetail) throw new Error("empty_template");

      setPreviewDetail(savedDetail);
      const updateSummary = (template: CozeTemplateSetSummary) => template.id === savedDetail.id
        ? {
            ...template,
            ...savedDetail,
            slots: savedDetail.slots,
            user_slots: savedDetail.user_slots || [],
          }
        : template;
      setTemplateSets((templates) => templates.map(updateSummary));
      setMineTemplateSets((templates) => templates.map(updateSummary));

      const current = getCurrentTemplateValue(savedDetail.id);
      if (current) {
        commitValue(mergeTemplateDetailToValue(
          savedDetail,
          current,
          userModulesRef.current,
          true,
          true,
        ));
      }
      initializePreviewTemplateDraft(savedDetail);
      setPreviewSlotKey((currentKey) => {
        const savedSlots = createPreviewTemplateDraft(savedDetail);
        return savedSlots.find((slot) => (
          slot.slot_key === currentKey && Boolean(getTemplateImageFromDTO(slot))
        ))?.slot_key || savedSlots.find((slot) => Boolean(getTemplateImageFromDTO(slot)))?.slot_key || "";
      });
      feedback.success("模板已保存");
      return savedDetail;
    } catch {
      if (previewTemplateEditSessionRef.current === session) {
        setSavingPreviewTemplate(false);
        feedback.warning("模板保存失败，请稍后重试");
      }
      return null;
    }
  };

  const handlePreviewTemplatePrimaryAction = async () => {
    const detail = previewDetail;
    if (!detail) return;
    if (!previewTemplateDirty) {
      await handleApplyTemplate(detail.id);
      return;
    }
    const savedDetail = await handleSavePreviewTemplate();
    if (!savedDetail) return;
    await handleApplyTemplate(savedDetail.id, savedDetail);
  };

  return (
    <TemplateImageSourceProvider sources={templateDisplaySources}>
    <div className="text-white">
      {!showOnboarding && (
      <div className="space-y-4" data-coze-template-workspace="true">
          {hasAppliedTemplate && normalizedValue && (
          <section data-coze-template-modules="true">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="text-[13px] font-medium text-white/90">模块</div>
              <button
                type="button"
                onClick={() => void openAppliedTemplateLibrary()}
                aria-label="主图模板库"
                className="group relative inline-flex h-[30px] w-[142px] max-w-full shrink-0 cursor-pointer items-center justify-center gap-1.5 overflow-hidden rounded-[10px] border-0 bg-[linear-gradient(100deg,#282e6c_0%,#1d253e_52%,#123335_100%)] px-3 text-[13px] font-medium text-white transition-[filter] hover:brightness-110 disabled:cursor-not-allowed"
              >
                <span className="relative z-10 truncate">主图模板库</span>
                <ArrowUpRight size={17} strokeWidth={2} className="relative z-10 shrink-0" aria-hidden="true" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {visibleSlots.map((slot) => {
                const imageUrl = getCozeTemplateSlotImage(slot);
                const displayName = visibleSlotDisplayNames.get(slot.slot_key) || slot.slot_name;
                const active = activeSlot?.slot_key === slot.slot_key;
                const uploaded = getCozeTemplateSlotUploadedImages(draftValue, slot.slot_name)
                  .some((image) => Boolean(cleanText(image.url)) && !isBlobUrl(image.url));
                const available = Boolean(imageUrl);
                return (
                  <div
                    key={`module-${slot.slot_key}`}
                    role="button"
                    tabIndex={available ? 0 : -1}
                    aria-pressed={active}
                    aria-disabled={!available}
                    onClick={available ? () => selectSlot(slot) : undefined}
                    onKeyDown={available ? (event) => handleSlotCardKeyDown(event, slot) : undefined}
                    className={`${getCozeTemplateSlotCardClassName({ selected: active, active, enabled: available, highlighted: available })} ${uploaded ? "pr-[27px]" : ""} ${!available ? "opacity-45" : ""}`}
                    data-coze-module-slot-key={slot.slot_key}
                    data-coze-module-card={slot.slot_name}
                  >
                    <span className="flex min-w-0 flex-1 items-center gap-2.5 text-left">
                      <span
                        className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-[6px] bg-[#242424]"
                        onMouseEnter={imageUrl ? () => showSlotImagePreview(imageUrl, slot.slot_name) : undefined}
                        onMouseLeave={hideSlotImagePreview}
                      >
                        {imageUrl ? (
                          // eslint-disable-next-line @next/next/no-img-element -- Template images can be external dynamic URLs.
                          <img src={imageUrl} alt={displayName} className="h-full w-full object-cover" />
                        ) : <CozeTemplateSlotPlaceholder />}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm leading-[17px]">{displayName}</span>
                    </span>
                    {uploaded && (
                      <span
                        className="absolute inset-y-0 right-0 z-10 flex w-6 shrink-0 flex-col items-center justify-center rounded-r-[7px] bg-[linear-gradient(180deg,#2A3761_0%,#16434C_100%)] text-[10px] leading-3 font-normal tracking-[0.02em] text-white/90"
                        data-coze-module-uploaded-status="true"
                        aria-label={`${displayName}已上传`}
                      >
                        <span aria-hidden="true">已</span>
                        <span aria-hidden="true">上</span>
                        <span aria-hidden="true">传</span>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
          )}
          <section data-coze-product-upload-section="true">
            <ImageUploadField<CozeProductUploadFieldItem>
              items={activeUploadedImages.map((image, index) => ({
                id: image.id,
                src: image.preview_url || image.url,
                alt: `上传图 ${index + 1}`,
                status: isBlobUrl(image.url) ? "uploading" : "ready",
                image,
                itemAttributes: {
                  "data-coze-product-image": index + 1,
                } as HTMLAttributes<HTMLDivElement>,
              }))}
              maxItems={maxProductImages}
              onPasteFiles={handleProductFiles}
              pasteDisabled={uploadingProductImages}
              dragOver={productDragOver}
              header={(
                <div className="mb-2 text-[13px] font-medium text-white/90">
                  <span>上传素材图片（可选）</span>
                  <span className="ml-1 text-white/30">
                    ({activeUploadedImages.length}/{maxProductImages})
                  </span>
                </div>
              )}
              emptyState={(
                <button
                  type="button"
                  onClick={() => productUploadInputRef.current?.click()}
                  disabled={uploadingProductImages}
                  className={`block w-full cursor-pointer rounded-[8px] border border-dashed transition-colors ${
                    productDragOver ? "border-white/65 bg-white/10 text-white" : "border-white/14 text-white/38 hover:border-white/25 hover:text-white/62"
                  } disabled:cursor-not-allowed disabled:opacity-70`}
                >
                  <ImageUploadEmptyState
                    uploading={uploadingProductImages}
                    labels={{
                      idle: `点击上传${activeSlotDisplayName || "模块"}素材图片或拖拽图片到此处`,
                      uploading: (
                        <span className="flex items-center justify-center gap-1.5">
                          <Loader2 size={14} className="animate-spin text-white/80" />
                          图片上传中...
                        </span>
                      ),
                    }}
                    loadingIndicator={false}
                    description={(
                      <>
                        <span className="text-[12px] leading-5 text-[#8C8C8C]">
                          推荐分别上传<span className="text-[#F6F6F6]">产品、细节、材质</span>图
                        </span>
                        <span className="text-[12px] leading-5 text-[#8C8C8C]">
                          支持<span className="text-[#F6F6F6]"> JPG、PNG</span>格式，支持多张上传
                        </span>
                      </>
                    )}
                    className="h-[104px] w-full gap-1 rounded-[8px] border-0 bg-[#141414] px-4 py-0 hover:border-0 hover:bg-[#141414]"
                  />
                </button>
              )}
              addTrigger={(
                <button
                  type="button"
                  onClick={() => productUploadInputRef.current?.click()}
                  disabled={uploadingProductImages}
                  aria-label={uploadingProductImages ? "图片上传中" : "添加产品图片"}
                  className="block aspect-square w-full disabled:cursor-not-allowed disabled:opacity-45"
                >
                  <ImageUploadAddTrigger uploading={uploadingProductImages} />
                </button>
              )}
              capabilities={{
                activate: true,
                remove: true,
                edit: true,
                reorder: imageReferenceBindingEnabled,
                showNumber: true,
                showReference: true,
              }}
              labels={{
                activate: (_item, index) => `点击插入 @图片${index + 2} 到提示词`,
                number: (_item, index) => index + 2,
                reference: (_item, index) => `@图片${index + 2}`,
              }}
              reorder={imageReferenceBindingEnabled ? {
                activeIndex: productReorderDragIndex,
                overIndex: productReorderOverIndex,
                onDragStart: (event, _item, index) => {
                  event.dataTransfer.effectAllowed = "move";
                  productReorderDragIndexRef.current = index;
                  setProductReorderDragIndex(index);
                  setProductReorderOverIndex(index);
                  onHoverPreviewChange?.(null);
                },
                onDragOver: (event, _item, index) => {
                  event.preventDefault();
                  event.stopPropagation();
                  event.dataTransfer.dropEffect = "move";
                  setProductReorderOverIndex(index);
                },
                onDrop: (event, _item, index) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (productReorderDragIndexRef.current !== null) {
                    handleProductImageReorder(productReorderDragIndexRef.current, index);
                  }
                  productReorderDragIndexRef.current = null;
                  setProductReorderDragIndex(null);
                  setProductReorderOverIndex(null);
                },
                onDragEnd: () => {
                  productReorderDragIndexRef.current = null;
                  setProductReorderDragIndex(null);
                  setProductReorderOverIndex(null);
                },
              } : undefined}
              editIcon={<CropIcon data-coze-product-image-crop-icon />}
              onItemActivate={(_item, index) => handleProductImageRefInsert(index)}
              onItemRemove={(item) => handleRemoveUploadedImage(item.image.id)}
              onItemEdit={(item) => setCroppingProductImage(item.image)}
              onItemHoverChange={(item, index) => {
                if (!item || index === null) {
                  handleProductImagePreviewLeave();
                  return;
                }
                handleProductImagePreviewEnter(item.image, index);
              }}
              isItemEditDisabled={() => uploadingProductImages}
              onDrop={handleProductDrop}
              onDragOver={(event) => {
                event.preventDefault();
                setProductDragOver(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setProductDragOver(false);
              }}
            />
          </section>

          {hasAppliedTemplate && normalizedValue && (
          <section data-coze-prompt-section="true">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="text-[13px] font-medium text-white/90">提示词</div>
              <button
                type="button"
                onClick={openReversePromptModal}
                disabled={!canReversePrompts}
                aria-label="AI反推提示词"
                aria-busy={promptGenerating}
                className="group relative inline-flex h-[26px] w-[118px] shrink-0 cursor-pointer items-center justify-center gap-1 overflow-hidden rounded-[34px] border border-white/15 bg-[url('/button_bg8.png')] bg-cover bg-center px-2 text-[13px] font-medium text-white transition-all hover:border-white/25 hover:brightness-125 disabled:cursor-not-allowed disabled:border-white/8 disabled:bg-[url('/button_bg7.png')] disabled:text-white/30"
              >
                {promptGenerating ? (
                  <Loader2 className="relative z-10 h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true" />
                ) : (
                  <ReversePromptAiIcon className="relative z-10 h-3.5 w-3.5 shrink-0" />
                )}
                <span className="relative z-10 whitespace-nowrap text-[12px]">{promptGenerating ? "提示词生成中..." : "反推提示词"}</span>
              </button>
            </div>
            {activeSlot ? (() => {
              const imageUrl = getCozeTemplateSlotImage(activeSlot);
              // Keep prompt whitespace verbatim in the editor. The image token
              // contract deliberately stores one ordinary space on each side;
              // getCozeTemplateSlotPrompt is trimmed for non-editor checks.
              const rawPrompt = activeSlot.prompt_by_variant?.[normalizedValue.variant];
              const prompt = typeof rawPrompt === "string" ? rawPrompt : "";
              const status = getPromptStatus(activeSlot, normalizedValue.variant);
              const isPromptGenerating = status === "generating";
              return (
                <div className="overflow-hidden rounded-[4px] border border-white/10 bg-[#191919]">
                  <div className="flex h-10 items-center justify-between gap-2 border-b border-white/8 px-3">
                    <div className="min-w-0 text-[12px] text-white/58">
                      当前模块：<span className="text-white/90">{activeSlotDisplayName}</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {activeSlot.is_user_slot && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => openSlotUpload(
                            activeSlot.slot_key,
                            normalizedValue.template_set_id,
                            activeSlot.user_module_id,
                          )}
                          className="flex h-7 w-7 items-center justify-center rounded transition-colors"
                          title={imageUrl ? "替换模板图" : "上传模板图"}
                        >
                          {uploadingSlotKey === activeSlot.slot_key ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="relative">
                    <CozePromptTextarea
                      value={prompt}
                      onChange={(nextPrompt) => handlePromptChange(activeSlot.slot_key, nextPrompt)}
                      disabled={isPromptGenerating}
                      textareaRef={promptTextareaRef}
                      imageUrls={imageReferenceBindingEnabled
                        ? [imageUrl, ...activeUploadedImages.map((image) => image.preview_url || image.url)]
                        : []}
                    />
                    {isPromptGenerating && (
                      <div
                        className="absolute inset-0 cursor-not-allowed bg-[#191919] px-3 py-3 text-[12px] text-white/86"
                        aria-live="polite"
                      >
                        <div className="flex items-center gap-2">
                          <Loader2 size={16} className="shrink-0 animate-spin text-white/80" />
                          <span>提示词生成中…</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="sr-only">
                    {getCozeTemplatePromptKey(normalizedValue.template_set_id || 0, activeSlot.slot_key, normalizedValue.variant)}
                  </div>
                </div>
              );
            })() : (
              <div className="flex h-[120px] items-center justify-center rounded-[4px] border border-dashed border-white/12 bg-[#191919] text-xs text-white/38">
                请先选择模块
              </div>
            )}
          </section>
          )}

          {statusMessage && (
            <div className="text-xs text-white/42">{statusMessage}</div>
          )}
        </div>
      )}

      <input ref={fileInputRef} type="file" accept={IMAGE_ACCEPT} multiple className="hidden" onChange={handleSlotFileChange} />
      <input ref={customReferenceInputRef} type="file" accept={IMAGE_ACCEPT} multiple className="hidden" onChange={handleCustomReferenceFileChange} />
      <input
        ref={productUploadInputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        multiple
        className="hidden"
        onChange={handleProductFileInputChange}
      />
      {typeof document !== "undefined" && showOnboarding && createPortal(
        <div
          className="fixed bottom-0 right-0 top-[64px] z-10 left-[var(--coze-onboarding-nav-width)] overflow-hidden bg-black text-white max-md:top-[56px] max-md:left-0"
          style={{
            "--coze-onboarding-nav-width": `${leftNavCollapsed ? 72 : 260}px`,
          } as CSSProperties}
          data-coze-template-onboarding="true"
        >
          <EcomOnboardingGlowLights navWidthPx={leftNavCollapsed ? 72 : 260} />
          {mainReplicaTutorialConfig && <MainReplicaTutorialButton config={mainReplicaTutorialConfig} />}
          <div className="absolute inset-y-0 left-[calc(50%_-_var(--coze-onboarding-nav-width)/2_+_30px)] z-10 flex w-[878px] -translate-x-1/2 flex-col items-center justify-center text-center max-md:left-1/2 max-md:w-[calc(100%_-_32px)]">
            <h2 className="h-[72px] w-[384px] bg-linear-to-r from-[#ABADB0] via-white to-[#ABADB0] bg-clip-text font-['Noto_Sans_SC','PingFang_SC','Microsoft_YaHei',sans-serif] text-[48px] leading-[1.5] font-medium text-transparent [text-shadow:0_4px_32px_rgba(255,255,255,0.25)] md:-translate-x-[30px] max-md:h-auto max-md:w-full max-md:text-[32px] max-md:leading-[1.5]">
              一键复刻爆款主图
            </h2>
            <Button
              type="button"
              size="ultra"
              onClick={openTemplateLibrary}
              data-coze-template-onboarding-cta="true"
              className="flowx-ecom-image mt-[42px] gap-[10px] overflow-hidden font-['PingFang_SC','Noto_Sans_SC','Microsoft_YaHei',sans-serif] leading-none md:ml-[321px] md:self-start max-md:mt-[26px]"
            >
              去生图
              <span
                aria-hidden="true"
                className="size-[9px] rotate-[-45deg] border-r-[2.5px] border-b-[2.5px] border-current"
              />
            </Button>
            <ol className="mt-[49px] flex h-[30px] w-full items-center justify-center gap-[10px] font-['PingFang_SC','Noto_Sans_SC','Microsoft_YaHei',sans-serif] text-[16px] leading-[1.6] text-[#F6F6F6] max-md:mt-8 max-md:grid max-md:h-auto max-md:w-auto max-md:grid-cols-1 max-md:items-start max-md:gap-3 max-md:text-left max-md:text-[14px]">
              <li className="flex items-center gap-2 whitespace-nowrap">
                <span className="flex size-[26px] shrink-0 items-center justify-center rounded-full bg-[#FAFBFF] text-[16px] leading-[1.6] text-[#181818]">1</span>
                <span>选择模板或上传参考图</span>
              </li>
              <li aria-hidden="true" className="w-[33px] shrink-0 text-center max-md:hidden">……</li>
              <li className="flex items-center gap-2 whitespace-nowrap">
                <span className="flex size-[26px] shrink-0 items-center justify-center rounded-full bg-[#FAFBFF] text-[16px] leading-[1.6] text-[#181818]">2</span>
                <span>根据参考图上传对应的产品图</span>
              </li>
              <li aria-hidden="true" className="w-[33px] shrink-0 text-center max-md:hidden">……</li>
              <li className="flex items-center gap-2 whitespace-nowrap">
                <span className="flex size-[26px] shrink-0 items-center justify-center rounded-full bg-[#FAFBFF] text-[16px] leading-[1.6] text-[#181818]">3</span>
                <span>确定尺寸生成提示词</span>
              </li>
              <li aria-hidden="true" className="w-[33px] shrink-0 text-center max-md:hidden">……</li>
              <li className="flex items-center gap-2 whitespace-nowrap">
                <span className="flex size-[26px] shrink-0 items-center justify-center rounded-full bg-[#FAFBFF] text-[16px] leading-[1.6] text-[#181818]">4</span>
                <span>开始生图</span>
              </li>
            </ol>
          </div>
        </div>,
        document.body,
      )}
      {referencePickerContextValue && (
        <ReferenceImageUploadDialog
          open={referencePickerOpen}
          title={COZE_TEMPLATE_UPLOAD_REFERENCE_LABEL}
          previewImageUrl={referencePickerPreviewImage}
          previewAlt={selectedReferencePickerSlot?.slot_name || COZE_TEMPLATE_UPLOAD_REFERENCE_LABEL}
          previewSlotKey={selectedReferencePickerSlot?.slot_key || ""}
          previewPosition={selectedReferencePickerSlot
            ? Math.max(
                1,
                referencePickerFilledSlots.findIndex(
                  (slot) => slot.slot_key === selectedReferencePickerSlot.slot_key,
                ) + 1,
              )
            : 0}
          previewCount={referencePickerFilledSlots.length}
          slots={referenceUploadDialogSlots}
          uploading={uploadingCustomReference}
          applying={addingCustom}
          saveTemplate={referencePickerSaveTemplate}
          productType={referencePickerProductType}
          productTypeOptions={libraryProductTypeOptions}
          canApply={
            !addingCustom &&
            !uploadingCustomReference &&
            referencePickerHasApplicableImage &&
            (!referencePickerSaveTemplate || Boolean(referencePickerProductType))
          }
          applyLabel={referencePickerSaveTemplate ? "保存并应用模版" : "应用参考图"}
          onClose={closeReferencePicker}
          onSelectSlot={(slot) => setSelectedReferenceSlotKey(slot.slotKey)}
          onUploadSlot={(slot) => openReferencePickerSlotUpload(slot.slotKey)}
          onCropSlot={(slot) => {
            setSelectedReferenceSlotKey(slot.slotKey);
            setCroppingReferenceSlot({
              slotKey: slot.slotKey,
              slotName: slot.slotName,
              imageUrl: slot.imageUrl,
            });
          }}
          onRemoveSlot={(slot) => removeReferencePickerSlot(slot.slotKey)}
          onSaveTemplateChange={setReferencePickerSaveTemplate}
          onProductTypeChange={setReferencePickerProductType}
          onApply={() => void applyReferenceImagesFromPicker()}
          onSlotDragOver={handleReferenceSlotDragOver}
          onSlotDragLeave={(slotKey) => {
            if (dragOverReferenceSlotKey === slotKey) setDragOverReferenceSlotKey(null);
          }}
          onSlotDrop={handleReferenceSlotDrop}
          onSlotReorder={(orderedSlotKeys) => handleReferenceSlotReorder(orderedSlotKeys)}
          onDialogFileDrop={handleReferenceDialogFileDrop}
        />
      )}

      {croppingReferenceSlot && (
        <ImageCropDialog
          imageUrl={croppingReferenceSlot.imageUrl}
          onCancel={() => setCroppingReferenceSlot(null)}
          onSave={(croppedDataUrl) => void handleReferenceCropSave(croppedDataUrl)}
        />
      )}

      {croppingPreviewTemplateSlot && (
        <ImageCropDialog
          imageUrl={croppingPreviewTemplateSlot.imageUrl}
          onCancel={() => setCroppingPreviewTemplateSlot(null)}
          onSave={(croppedDataUrl) => void handlePreviewTemplateCropSave(croppedDataUrl)}
        />
      )}

      {croppingProductImage && (
        <ImageCropDialog
          imageUrl={croppingProductImage.url}
          onCancel={() => setCroppingProductImage(null)}
          onSave={(croppedDataUrl) => void handleProductCropSave(croppedDataUrl)}
        />
      )}

      {reverseModalOpen && reverseModalStage !== "closed" && createPortal(
        <div
          className="fixed inset-0 z-[310] flex items-center justify-center bg-black/55 px-4 py-2 backdrop-blur-[6px]"
          data-coze-reverse-overlay
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="coze-reverse-modal-title"
            className="flex h-[min(730px,calc(100vh-16px))] w-[min(1200px,calc(100vw-40px))] flex-col overflow-hidden rounded-[10px] border border-white/5 bg-[#111215] p-[30px] text-white shadow-2xl"
            data-coze-reverse-dialog
          >
            <div className="flex shrink-0 items-center justify-between border-b border-white/20 pb-[18px]">
              <h3 id="coze-reverse-modal-title" className="text-base font-medium">反推提示词</h3>
              <button
                type="button"
                onClick={closeReversePromptModal}
                className="flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-full bg-white/15 text-white/55 transition-colors hover:bg-white/22 hover:text-white"
                aria-label="关闭反推提示词"
              >
                <X size={16} />
              </button>
            </div>
            <div className="relative min-h-0 flex-1">
              <div
                ref={reverseModalScrollRef}
                className="h-full overflow-y-auto py-[14px] pr-1 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb:hover]:bg-white/30 [&::-webkit-scrollbar-track]:bg-transparent"
                data-coze-reverse-scroll
              >
                <div className="space-y-6">
                  {reversePromptDrafts.map((item) => {
                    const canEditReversePrompt = item.status === "success";
                    return (
                      <div key={item.slot_key} data-coze-reverse-draft={item.slot_key}>
                      <div
                        className="flex items-end gap-5"
                        data-coze-reverse-media-actions
                      >
                        <div className="flex min-w-0 flex-1 flex-wrap gap-2">
                          {[item.template_image, ...item.upload_images.map((image) => image.url)].map((imageUrl, index) => (
                            <div key={`${item.slot_key}-${imageUrl}-${index}`} className="relative h-[60px] w-[60px] shrink-0 overflow-hidden rounded-[4px] bg-white/8">
                              {/* eslint-disable-next-line @next/next/no-img-element -- Reverse draft images can be external dynamic URLs. */}
                              <img src={imageUrl} alt={`${item.slot_name}图${index + 1}`} className="h-full w-full object-cover" />
                              <span className="absolute inset-x-0 bottom-0 bg-black/40 py-0.5 text-center text-[10px] text-white/85">图{index + 1}</span>
                            </div>
                          ))}
                        </div>
                        <div
                          className="ml-auto flex shrink-0 items-center gap-2 self-end"
                          data-coze-reverse-regenerate-actions={item.slot_key}
                        >
                            <button
                              type="button"
                              onClick={() => void runReversePromptDraft([item.slot_key])}
                              data-coze-reverse-regenerate={item.slot_key}
                              disabled={pendingReverseSlotKeys.has(item.slot_key)}
                              className="inline-flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-[5px] border border-white/12 px-3 text-[13px] text-white/82 hover:border-white/25 hover:text-white disabled:cursor-not-allowed disabled:opacity-45"
                            >
                              <svg
                                viewBox="0 0 1024 1024"
                                aria-hidden="true"
                                className="h-[13px] w-[13px] shrink-0"
                                data-coze-reverse-regenerate-icon
                              >
                                <path
                                  d="M882.801778 523.633778a36.096 36.096 0 0 0-72.163556-1.393778h-0.568889l-0.028444 1.365333a299.264 299.264 0 1 1-47.900445-174.648889h-53.930666a36.067556 36.067556 0 0 0 0 72.163556h138.638222a36.181333 36.181333 0 0 0 36.124444-36.067556V246.357333a36.096 36.096 0 0 0-72.192 0v45.880889a370.915556 370.915556 0 1 0 71.395556 237.966222 23.04 23.04 0 0 0 0.625778-6.599111z m0 0v-0.056889l-1.422222 0.085333h1.422222v-0.028444z"
                                  fill="#C2C2C2"
                                />
                              </svg>
                              重新生成
                            </button>
                            {isPaidReverse && (
                              <span
                                className="inline-flex shrink-0 items-baseline gap-1"
                                data-coze-reverse-regenerate-cost
                              >
                                <span
                                  className="text-[18px] leading-none text-[#FCB453]"
                                  data-coze-reverse-regenerate-cost-value
                                >
                                  {singleReverseEstimate.cost}
                                </span>
                                <span
                                  className="text-[13px] leading-none text-white/45"
                                  data-coze-reverse-regenerate-cost-unit
                                >
                                  豌豆
                                </span>
                              </span>
                            )}
                        </div>
                      </div>
                      {item.status === "success" ? (
                        <AutoResizeTextarea
                          value={item.prompt}
                          data-ecom-prompt-input
                          onChange={(event) => updateReversePromptDraft(item.slot_key, event.target.value)}
                          minHeight={48}
                          data-coze-reverse-prompt-editor={item.slot_key}
                          disabled={!canEditReversePrompt}
                          aria-label={`${item.slot_name}提示词`}
                          className="mt-3 w-full rounded-[6px] border border-white/15 bg-transparent px-3 py-2.5 text-[13px] leading-6 text-white/82 outline-none transition-colors focus:border-white/35 disabled:cursor-not-allowed disabled:opacity-70"
                        />
                      ) : item.status === "failed" ? (
                        <div className="mt-3 rounded-[6px] border border-red-400/20 bg-red-400/5 px-3 py-2.5 text-[13px] leading-6 text-red-100/75">
                          {item.errorText || "E5302：反推失败，请重新生成"}
                        </div>
                      ) : (
                        <div className="mt-2" data-coze-reverse-pending={item.slot_key}>
                          <div className="h-1.5 overflow-hidden rounded-full bg-[#555557]">
                            {reverseModalStage === "generating" && item.status === "generating" && (
                              <div
                                role="progressbar"
                                aria-label={`${item.slot_name}反推进度`}
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-valuenow={reverseProgressBySlot[item.slot_key] ?? 8}
                                className="h-full rounded-full bg-linear-to-r from-[#EA83A5] via-[#9B52FF] to-[#6830F4] transition-[width] duration-500 ease-out"
                                style={{ width: `${reverseProgressBySlot[item.slot_key] ?? 8}%` }}
                                data-coze-reverse-progress={item.slot_key}
                              />
                            )}
                          </div>
                          {reverseModalStage === "generating" && item.status === "generating" && (
                            <span className="mt-2 inline-flex h-7 items-center rounded-full bg-linear-to-r from-[#B254D8] to-[#6830F4] px-3 text-xs text-white">AI反推中</span>
                          )}
                        </div>
                      )}
                      </div>
                    );
                  })}
                </div>
              </div>
              {reverseModalHasBottomOverflow && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-16 bg-[linear-gradient(to_bottom,rgba(17,18,21,0)_0%,rgba(17,18,21,.45)_55%,rgba(17,18,21,.9)_100%)]"
                  data-coze-reverse-bottom-fade
                />
              )}
            </div>
            <div
              className="mt-2 shrink-0 rounded-[12px] bg-[#161616] px-4 py-[14px]"
              data-coze-reverse-footer
            >
              <div className="flex items-end justify-between gap-5">
                <div
                  className="flex h-10 w-[256px] shrink-0 rounded-[10px] bg-[#0F1012] p-0.5"
                  role="group"
                  aria-label="反推提示词版本"
                  data-coze-reverse-variant-switch
                >
                  {PROMPT_VARIANTS.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => handleReversePromptVariantChange(item.value)}
                      disabled={reverseModalStage === "generating"}
                      className={cn(
                        "h-9 flex-1 cursor-pointer rounded-[9px] text-[14px] text-white/45 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/45 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-white/45",
                        draftValue.variant === item.value && "bg-[#2B2B2D] text-[#F6F6F6] shadow-[0_1px_4px_rgba(0,0,0,.22)]",
                      )}
                      aria-pressed={draftValue.variant === item.value}
                      data-coze-reverse-variant={item.value}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
                <div className="flex flex-col items-end gap-[6px]">
                  <CozeReverseCostLine
                    estimate={reverseEstimate}
                    label={`${reverseBatchCounts.rerunnableCount}/${reverseBatchCounts.totalCount}预估消耗`}
                    className="w-[165px]"
                  />
                  <div className="flex items-center justify-end gap-5">
                  {reversePromptDrafts.some((item) => item.status === "success" && cleanText(item.prompt)) && (
                    <button
                      type="button"
                      onClick={applyReversePromptDrafts}
                      data-coze-reverse-apply
                      disabled={reverseModalStage === "generating"}
                      className="h-10 w-[165px] cursor-pointer rounded-[8px] bg-[#FAFBFF] text-sm font-medium text-[#222222] disabled:cursor-not-allowed disabled:bg-[#303030] disabled:text-white/35"
                    >
                      应用提示词
                    </button>
                  )}
                  {reverseModalStage === "preview" && (
                      <button
                        type="button"
                        onClick={() => void runReversePromptDraft()}
                        className="h-10 w-[165px] cursor-pointer rounded-[8px] bg-[#FAFBFF] text-sm font-medium text-[#222222]"
                      >
                        反推提示词
                      </button>
                  )}
                  {reverseModalStage === "generating" && (
                    <button
                      type="button"
                      onClick={() => void runReversePromptDraft(
                        reversePromptDrafts
                          .filter((item) => item.status !== "generating")
                          .map((item) => item.slot_key),
                      )}
                      disabled={reverseBatchCounts.rerunnableCount === 0}
                      className="h-10 w-[165px] cursor-pointer rounded-[8px] bg-[#FAFBFF] text-sm font-medium text-[#222222] disabled:cursor-not-allowed disabled:bg-[#303030] disabled:text-white/35"
                    >
                      反推提示词
                    </button>
                  )}
                  {reverseModalStage === "result" && (
                    <button
                      type="button"
                      onClick={() => void runReversePromptDraft(
                        reversePromptDrafts
                          .filter((item) => item.status !== "generating")
                          .map((item) => item.slot_key),
                      )}
                      disabled={reverseBatchCounts.rerunnableCount === 0}
                      className="h-10 w-[165px] cursor-pointer rounded-[8px] bg-[#FAFBFF] text-sm font-medium text-[#222222] disabled:cursor-not-allowed disabled:bg-[#303030] disabled:text-white/35"
                    >
                      重新反推提示词
                    </button>
                  )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}

      <EcomModal
        open={libraryOpen}
        onClose={() => {
          cancelTemplateDetailRequest();
          setLibraryOpen(false);
        }}
        title={previewDetail ? null : "上传参考图或使用模板"}
        layer="modal"
        closeLabel="关闭模板库"
        layout={previewDetail ? "frameless" : "default"}
        showCloseButton={!previewDetail}
        width="100%"
        classNames={{
          root: "flowx-ecom-image",
          wrapper: "ecom-template-library-wrapper",
          mask: "bg-black/55 backdrop-blur-[6px]",
          panel: cn("ecom-template-library-panel", previewDetail && "ecom-template-library-panel--detail"),
          header: "ecom-template-library-header",
          body: "ecom-template-library-body relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden",
        }}
      >
        <div
          data-coze-template-library-dialog
          className={previewDetail ? "relative flex min-h-0 w-full flex-1" : "relative flex min-h-0 flex-1 flex-col"}
        >
              {libraryView === "error" ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-sm text-white/65">
                  <div>{templateError}</div>
                  <button type="button" onClick={loadTemplates} className="rounded bg-white px-3 py-1.5 text-xs font-medium text-[#181818]">重试</button>
                </div>
              ) : libraryView === "detail" && previewDetail ? (
                <div ref={templateDetailRef} className="ecom-template-detail-layout flex h-full min-h-0 w-full" aria-busy={loadingDetailId !== null}>
                  <div className="ecom-template-detail-preview flex min-h-0 min-w-0 flex-1 items-center justify-center bg-[#0D0E10] p-[clamp(12px,3vw,48px)]">
                    <div className="relative flex h-full w-full items-center justify-center overflow-hidden bg-black/40">
                      {previewImageUrl ? (
                        <TemplateImage
                          src={previewImageUrl}
                          alt={previewSlot?.slot_name || previewDetail.name}
                          className="block h-auto w-auto max-h-full max-w-full object-contain"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-xs text-white/30">无预览图</div>
                      )}
                      {navigablePreviewSlots.length > 1 && ([-1, 1] as const).map((direction) => (
                        <button
                          key={direction}
                          type="button"
                          aria-label={direction === -1 ? "上一张图片" : "下一张图片"}
                          title={direction === -1 ? "上一张图片（←）" : "下一张图片（→）"}
                          aria-keyshortcuts={direction === -1 ? "ArrowLeft" : "ArrowRight"}
                          data-coze-template-image-nav={direction === -1 ? "previous" : "next"}
                          disabled={previewNavigationBlocked || (direction === -1
                            ? previewNavigationIndex <= 0
                            : previewNavigationIndex >= navigablePreviewSlots.length - 1)}
                          onClick={() => navigatePreviewImage(direction)}
                          className={cn(
                            "absolute top-1/2 z-10 flex size-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-white/15 bg-black/60 text-white/90 shadow-lg transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 disabled:cursor-not-allowed disabled:opacity-25 sm:size-12",
                            direction === -1 ? "left-2 sm:left-4" : "right-2 sm:right-4",
                          )}
                        >
                          <ChevronRight className={cn("size-6", direction === -1 && "rotate-180")} aria-hidden="true" />
                        </button>
                      ))}
                      {loadingDetailId !== null && (
                        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/35" role="status" aria-label="套装加载中">
                          <Loader2 className="size-8 animate-spin text-white" aria-hidden="true" />
                        </div>
                      )}
                      {navigablePreviewSlots.length > 0 && (
                        <div className="absolute bottom-3 right-3 rounded-[6px] bg-[#1F1F1F]/90 px-3 py-1.5 text-sm text-white sm:bottom-6 sm:right-6" aria-live="polite" aria-atomic="true">
                          {previewNavigationIndex + 1}/{navigablePreviewSlots.length}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="ecom-template-detail-sidebar flex min-h-0 w-[clamp(280px,28vw,400px)] shrink-0 flex-col bg-[#141414] p-[clamp(12px,2vw,25px)]">
                    <div className="flex shrink-0 items-start justify-between gap-3">
                      <div className="min-w-0 flex items-center gap-3">
                        <div className="truncate text-base font-medium text-white">{previewDetail.name}</div>
                        {previewDetail.id === appliedTemplateId && (
                          <span
                            className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium leading-none text-black"
                            data-coze-template-detail-applied-badge
                          >
                            已应用
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        aria-label="关闭模板详情"
                        onClick={() => {
                          cancelTemplateDetailRequest();
                          resetPreviewTemplateDraft();
                          setPreviewDetail(null);
                          setPreviewSlotKey("");
                        }}
                        className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/12 text-white/55 hover:bg-white/18 hover:text-white"
                      >
                        <X size={17} />
                      </button>
                    </div>
                    <div className="mt-5 h-px shrink-0 bg-white/12" />
                    <div
                      className="mt-5 min-h-0 flex-1 overflow-x-hidden overflow-y-auto pr-1 [scrollbar-color:rgba(255,255,255,0.2)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-track]:bg-transparent"
                      data-coze-template-detail-scroll
                    >
                      <ReferenceImageSlotGrid
                        slots={previewSlotGridSlots}
                        uploading={previewNavigationBlocked}
                        onSelectSlot={(gridSlot) => setPreviewSlotKey(gridSlot.slotKey)}
                        onUploadSlot={(gridSlot) => {
                          const slot = previewSlots.find((item) => item.slot_key === gridSlot.slotKey);
                          if (!slot) return;
                          openSlotUpload(
                            slot.slot_key,
                            slot.template_set_id || previewDetail.id,
                            slot.user_module_id,
                          );
                        }}
                        onEditSlot={(gridSlot) => {
                          const slot = previewSlots.find((item) => item.slot_key === gridSlot.slotKey);
                          if (!slot) return;
                          setCroppingPreviewTemplateSlot({
                            slotKey: slot.slot_key,
                            imageUrl: getTemplateImageFromDTO(slot),
                          });
                        }}
                        onRemoveSlot={(gridSlot) => {
                          const slot = previewSlots.find((item) => item.slot_key === gridSlot.slotKey);
                          if (slot) removePreviewUserSlotImage(slot);
                        }}
                        onSlotDragOver={handlePreviewTemplateSlotDragOver}
                        onSlotDragLeave={(slotKey) => {
                          if (dragOverPreviewSlotKey === slotKey) setDragOverPreviewSlotKey(null);
                        }}
                        onSlotDrop={handlePreviewTemplateSlotDrop}
                        onSlotReorder={handlePreviewTemplateSlotReorder}
                      />
                    </div>
                    <div className="shrink-0 pt-6" data-coze-template-detail-footer>
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-[11px] text-white/45" data-coze-template-navigation-hint>
                        <span>← → 切换图片 · ↑ ↓ 切换套装</span>
                        {previewTemplateIndex >= 0 && <span>套装 {previewTemplateIndex + 1}/{filteredTemplateSets.length}</span>}
                      </div>
                      <button
                        type="button"
                        onClick={() => void handlePreviewTemplatePrimaryAction()}
                        disabled={
                          previewNavigationBlocked
                        }
                        data-coze-template-detail-primary-action="true"
                        className="inline-flex h-10 w-full cursor-pointer items-center justify-center gap-1.5 rounded-[8px] bg-[#FAFBFF] px-4 text-sm font-medium text-[#222222] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {(applyingId === previewDetail.id || savingPreviewTemplate) && (
                          <Loader2 size={14} className="animate-spin" />
                        )}
                        {savingPreviewTemplate
                          ? "保存中..."
                          : applyingId === previewDetail.id
                            ? "应用中..."
                            : previewTemplateDirty ? "保存并应用模版" : COZE_TEMPLATE_APPLY_TEMPLATE_BUTTON_LABEL}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex min-h-[48px] min-w-0 shrink-0 items-center gap-2 text-[14px] sm:gap-4" data-coze-template-product-filter>
                    <span className="shrink-0 whitespace-nowrap text-white/45">产品类型：</span>
                    <TemplateLibraryCategoryFilter
                      options={libraryProductTypeOptions}
                      value={libraryCategory}
                      onChange={(category) => {
                        setLibraryCategory(category);
                        libraryScrollRef.current?.scrollTo({ top: 0 });
                      }}
                    />
                  </div>
                  {mineTemplateError && (
                      <button
                        type="button"
                        onClick={() => void loadMineTemplates()}
                        disabled={loadingMineTemplates}
                        className="ml-auto text-xs text-white/45 underline-offset-2 hover:text-white hover:underline disabled:cursor-wait"
                        data-coze-template-mine-retry
                      >
                        我的模版加载失败，重试
                      </button>
                  )}
                  <div ref={libraryScrollRef} className="relative min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain pb-[35px] pt-[10px] [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent]" data-coze-template-library-scroll>
                    {libraryDataLoading && (
                      <div className="mb-2 flex items-center text-xs text-white/45" data-coze-template-library-loading>
                        <Loader2 size={14} className="mr-2 animate-spin" />
                        模版加载中
                      </div>
                    )}
                    {libraryNotice && (
                      <div className="mb-2 text-xs text-white/50" data-coze-template-library-notice>{libraryNotice}</div>
                    )}
                    <div
                      className="grid min-w-0 grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))] items-start gap-[clamp(10px,1.2vw,20px)]"
                      data-coze-template-library-grid
                    >
                      <button
                        type="button"
                        onClick={() => openReferencePicker()}
                        onDragOver={(event) => {
                          event.preventDefault();
                          event.dataTransfer.dropEffect = "copy";
                          setCustomReferenceDragOver(true);
                        }}
                        onDragLeave={(event) => {
                          const relatedTarget = event.relatedTarget;
                          if (relatedTarget instanceof Node && event.currentTarget.contains(relatedTarget)) return;
                          setCustomReferenceDragOver(false);
                        }}
                        onDrop={(event) => void handleReferenceTemplateCardDrop(event)}
                        disabled={addingCustom}
                        data-coze-reference-template-card="true"
                        className={cn(
                          "group relative flex aspect-[275/399] h-auto w-full min-w-0 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-[12px] border border-dashed border-[#535354] bg-[#141414] text-center text-[13px] font-medium text-[#F6F6F6] transition-colors hover:border-white/45 hover:bg-[#1B1B1D] focus-visible:border-white/55 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/45 disabled:cursor-not-allowed disabled:opacity-40",
                          customReferenceDragOver && "border-white/70 bg-white/10 ring-2 ring-white/45",
                        )}
                      >
                        {isReferenceDraftApplied && (
                          <span
                            className="absolute right-2 top-2 z-10 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium leading-none text-black"
                            data-coze-template-applied-badge
                          >
                            已应用
                          </span>
                        )}
                        <span>{addingCustom ? "正在上传参考图片..." : "点击或拖拽上传参考图片"}</span>
                        <span className="mt-2 text-xs font-normal text-white/40">支持 <em className="not-italic text-[#F6F6F6]">JPG、PNG</em> 格式，支持多张上传</span>
                      </button>
                      {libraryDataLoading ? (
                        Array.from({ length: 2 }, (_, index) => (
                          <TemplateLibrarySkeletonCard key={`template-skeleton-${index}`} />
                        ))
                      ) : filteredTemplateSets.length === 0 ? (
                        <div className="flex aspect-[275/399] h-auto w-full min-w-0 items-center justify-center rounded-[12px] border border-dashed border-white/12 text-center text-sm text-white/42" data-coze-template-library-empty>
                          暂无模板
                        </div>
                      ) : filteredTemplateSets.map((template) => {
                        const owned = mineTemplateIds.has(template.id);
                        const isApplied = appliedTemplateId === template.id;
                        const previewImages = getCozeTemplateSummaryPreviewImages(template);
                        const primaryImage = previewImages.primaryImage || template.cover_image_url || "";
                        const applyButtonActive = applyHoverTemplateId === template.id;
                        return (
                          <div
                            key={template.id}
                            className="group relative flex aspect-[275/399] h-auto w-full min-w-0 cursor-pointer flex-col overflow-hidden rounded-[12px] bg-[#181818] outline-none"
                            data-coze-template-card
                            data-coze-template-owned={owned ? "true" : "false"}
                            data-coze-template-id={template.id}
                          >
                            <button
                              type="button"
                              onClick={() => openTemplateDetail(template.id)}
                              disabled={loadingDetailId === template.id}
                              className="relative z-0 aspect-square h-auto w-full shrink-0 cursor-pointer overflow-hidden bg-[#242424] text-left outline-none disabled:cursor-wait"
                              aria-label={`查看${template.name}模版详情`}
                              data-coze-template-cover
                            >
                              {primaryImage ? (
                                <TemplateImage src={primaryImage} alt={template.name} className="size-full object-cover" />
                              ) : (
                                <div className="flex size-full items-center justify-center text-xs text-white/30">无封面</div>
                              )}
                            </button>
                            {owned && (
                              <div className="pointer-events-none absolute left-0 top-0 z-30 size-14 bg-[linear-gradient(156deg,rgba(208,126,153,1),rgba(104,48,244,1))] [clip-path:polygon(0_0,100%_0,0_100%)]" data-coze-template-mine-badge>
                                <span className="absolute left-[5px] top-[9px] -rotate-45 text-sm font-medium text-white">我的</span>
                              </div>
                            )}
                            {isApplied && (
                              <span
                                className="absolute right-2 top-2 z-30 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium leading-none text-black"
                                data-coze-template-applied-badge
                              >
                                已应用
                              </span>
                            )}
                            <div className="relative z-0 flex h-[clamp(56px,5vw,64px)] min-w-0 shrink-0 cursor-pointer items-center px-[14px]" data-coze-template-secondary-area>
                              <TemplateSecondaryPreviewStrip
                                images={previewImages.secondaryImages}
                                templateName={template.name}
                                onPreviewClick={() => openTemplateDetail(template.id)}
                              />
                            </div>
                            <div
                              className="relative z-0 flex min-h-0 flex-1 items-center justify-center px-[14px]"
                              data-coze-template-apply-area
                            >
                              <Button
                                type="button"
                                onClick={() => handleApplyTemplate(template.id)}
                                disabled={applyingId === template.id}
                                variant={applyButtonActive ? "default" : "outline"}
                                onMouseEnter={() => setApplyHoverTemplateId(template.id)}
                                onMouseLeave={() => setApplyHoverTemplateId((current) => current === template.id ? null : current)}
                                onFocus={() => setApplyHoverTemplateId(template.id)}
                                onBlur={() => setApplyHoverTemplateId((current) => current === template.id ? null : current)}
                                className="relative z-20 h-9 w-[143px] cursor-pointer rounded-[8px] text-sm font-normal transition-colors disabled:cursor-not-allowed"
                                data-coze-template-apply
                              >
                                {applyingId === template.id && <Loader2 size={14} className="animate-spin" />}
                                {COZE_TEMPLATE_APPLY_TEMPLATE_BUTTON_LABEL}
                              </Button>
                            </div>
                            <div
                              className={cn(
                                "pointer-events-none absolute inset-x-0 top-0 bottom-[clamp(36px,5vw,60px)] z-10 bg-black/40 opacity-0 transition-opacity duration-150",
                                applyHoverTemplateId === template.id && "opacity-100",
                              )}
                              data-coze-template-hover-mask
                              aria-hidden="true"
                            />
                            {owned && (
                              <EcomIconAction
                                type="button"
                                iconSize="large"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  requestDeleteTemplate(template);
                                }}
                                disabled={deletingTemplate}
                                className="pointer-events-none invisible absolute bottom-3 right-3 z-20 group-hover:pointer-events-auto group-hover:visible group-focus-within:pointer-events-auto group-focus-within:visible focus-visible:pointer-events-auto focus-visible:visible disabled:cursor-not-allowed"
                                aria-label={`删除自定义模版${template.name}`}
                                title={`删除自定义模版${template.name}`}
                                data-coze-template-delete
                              >
                                <Trash2 />
                              </EcomIconAction>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {libraryHasBottomOverflow && (
                    <div
                      className="pointer-events-none absolute bottom-0 left-0 right-0 z-20 h-16 bg-[linear-gradient(to_bottom,rgba(15,16,18,0)_0%,rgba(15,16,18,.24)_42%,rgba(15,16,18,.68)_100%)]"
                      data-coze-template-bottom-fade
                      aria-hidden="true"
                    />
                  )}
                </>
              )}
        </div>
      </EcomModal>
      <DeleteCustomTemplateConfirmModal
        template={pendingDeleteTemplate}
        deleting={deletingTemplate}
        onClose={closeDeleteTemplateConfirm}
        onConfirm={() => void handleDeleteTemplate()}
      />
    </div>
    </TemplateImageSourceProvider>
  );
}
